import { KiVerbrauchKarte } from "../ui/KiVerbrauchKarte";
import { AvvSupportZugriffKarte } from "../ui/AvvSupportZugriff";
import { PreisRundungKarte } from "../ui/PreisRundungKarte";
import { GoogleBewertungEinstellung } from "../ui/GoogleBewertungEinstellung";
import { VertrauenszeileEinstellung, gleicheVertrauenszeile, vertrauenszeileAus } from "../ui/VertrauenszeileEinstellung";
import { OpenTableEinstellung } from "../ui/OpenTableEinstellung";
import { VorfreudeMailEinstellung } from "../ui/VorfreudeMailEinstellung";
import { SchliesstageKarte } from "../ui/SchliesstageKarte";
import { BegruessungsvideoEinstellung } from "../ui/BegruessungsvideoEinstellung";
import { KiZuordnungKarte } from "../ui/KiZuordnungKarte";
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BUNDESLAENDER } from "@maestro/feiertage";
import {
  eintragsAnzeige,
  budgetAnzeige,
  trefferZeile,
  type AbrufBudget,
  type EintragsTreffer,
  type EintragsHandlungsArt,
} from "@maestro/oeffnungszeiten-anzeige";
import {
  abrufBilanzZeile,
  ablaufplanZeile,
  deckelAuslastung,
  haengendeZeile,
  letzterErfolgZeile,
  plattformMeldungenZeile,
} from "@maestro/gesundheit-anzeige";
import { standZeile } from "@maestro/recherche-stand";
import {
  NOCH_NICHT_AKTIV_VERMERK,
  transparenzBaustein,
  transparenzText,
  versandTransparenzBaustein,
  versandTransparenzText,
  angebotsAbrufBaustein,
  angebotsAbrufText,
} from "@maestro/transparenz-hinweis";
import { useApi, apiMutate } from "../hooks/useApi";
import { currentMembership } from "../lib/membership";
import type { Me, Member, Location } from "../ui/types";
import { ROLE_LABEL, formatDate, formatDateTime, parseGermanDecimal } from "../ui/format";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  KeineAngabe,
  LISTEN_KOPF_ZELLE,
  ListFooter,
  LoadingRows,
  Modal,
  PageHeader,
  Select,
  SortierKopf,
  Toggle,
  useBestaetigung,
  TabBar,
  Checkbox,
  DateInput,
  Textarea,
  ButtonOptik,
  Radio,
  Segmente,
} from "../ui/components";
import { useListSort, type SortSpalte } from "../hooks/useListSort";
import type { ApiError } from "../lib/api";
import { PasswortBereich } from "../ui/PasswortBereich";
import { BildAusschnittDialog, fokusStil } from "../ui/BildAusschnittDialog";
import { GoogleAttribution } from "../ui/GoogleAttribution";
import { WidgetsTab } from "./widgets-tab";
import { SPERRE_WARNUNG } from "../ui/UnzustellbarDialog";
import { EingaengeTab, InterneAbsenderKarte } from "./eingaenge-tab";
import { useContentRefetch, useCopilot } from "../ui/CopilotProvider";
import { MailVorlagenEditor, type VorlagenBeschreibung, type PlatzhalterBeschreibung, type GespeicherteMailVorlage, type VorlagenTeile } from "../ui/MailVorlagenEditor";
// AH-0 (docs/KONZEPT-ANALYTICS-HUB.md §2.3, Migration 296): dieselben vier Werte wie der
// DB-CHECK/das Zod-Schema in apps/api/src/routes/settings.ts (Quelle der Wahrheit ist die
// Migration) — bewusst hier dupliziert statt importiert, gleiches Muster wie inquiries.service_kind
// ('catering'/'event' stehen ebenfalls unabhaengig als String-Literale in inquiries.tsx): ein
// eigenes Paket fuer vier Werte, die nur bei einer bewussten Produktentscheidung wachsen, waere
// fuer dieses Fundament ueberdimensioniert.
const INDUSTRY_CATEGORIES: { value: "catering" | "event_location" | "restaurant" | "other"; label: string }[] = [
  { value: "catering", label: "Catering (Ausser-Haus-Lieferung)" },
  { value: "event_location", label: "Event-Location (Vermietung/Ausrichtung im eigenen Haus)" },
  { value: "restaurant", label: "Restaurant" },
  { value: "other", label: "Sonstiges" },
];
/** Sentinel fuer Selects mit einer echten leeren `<option value="">`-Auswahl — Radix `Select.Item`
 *  (anders als ein natives `<option value="">`) erlaubt keinen leeren `value`-String
 *  (design-komponenten-Migration P2, settings.tsx). Muster: `MANDANTEN_STANDARD` in
 *  `ui/NachschlagenTabs.tsx` / `INDUSTRY_KEINE_ANGABE` in
 *  `pages/admin/tenant-detail/EinstellungenTab.tsx`. Wird ausschliesslich lokal in Sende-/
 *  Anzeigelogik aufgeloest (nie an die API geschickt). */
const KEIN_WERT = "__kein_wert__";
const TABS = [
  { key: "betrieb", label: "Betrieb" },
  { key: "team", label: "Team & Rollen" },
  { key: "orte", label: "Orte" },
  { key: "zahlungen", label: "Zahlungen" },
  // Paket D1 (docs/UX-POLISH-UMSETZUNGSPLAN.md § Paket D, Migration 315).
  { key: "benachrichtigungen", label: "Benachrichtigungen" },
  // Paket C1 (docs/UX-POLISH-UMSETZUNGSPLAN.md § Paket C, Migration 316).
  { key: "absenderdomain", label: "Absenderdomain" },
  // Postfach-Abholung, Credential-Variante B (Migration 356, Antoine 03.09.2026). Steht direkt
  // neben "Absenderdomain": das eine regelt, unter welcher Adresse Post RAUSgeht, das andere,
  // aus welchen Postfaechern sie HEREINkommt.
  { key: "postfaecher", label: "Postfächer" },
  { key: "module", label: "Module" },
  { key: "branding", label: "Angebots-Vorlagen & Branding" },
  { key: "widgets", label: "Widgets" },
  // Punkt 121: Titel der Anfrage-Eingaenge (Formulare), Migration 519.
  { key: "eingaenge", label: "Anfrage-Eingänge" },
  { key: "datenschutz", label: "Datenschutz" },
] as const;
type ModuleTone = "success" | "info" | "primary" | "neutral";
const TONE_CLASSES: Record<ModuleTone, string> = {
  success: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  info: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  primary: "bg-primary-fixed text-on-primary-fixed",
  neutral: "bg-surface-variant text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]",
};
type ModuleConfigKey =
  | "lexoffice"
  | "esign"
  | "ki"
  | "feiertage"
  | "wetter"
  | "handelsregister"
  | "places"
  | "oeffnungszeiten"
  | "ustid"
  | "telegram";
type TenantFlag = "feiertageEnabled" | "wetterEnabled" | "handelsregisterEnabled" | "googlePlacesEnabled" | "ustidEnabled";
/** Antwort von GET /api/settings/stripe-zahlweg — mandantenscharf, anders als `system/health`. */
export interface StripeZahlweg {
  zustand: "eigenes_konto" | "systemkonto" | "nicht_verbunden";
  kontoKennung: string | null;
  kannKassieren: boolean;
}
import { ANZAHLUNG_FRIST_MODI, type AnzahlungFristModus, anzahlungSchalten, anzahlungsWege, anzahlungsWegFelder, restzahlungFelder, restzahlungsWege, type RestFristArt, type Zahlungswege } from "../ui/NachschlagenTabs";
import { standardAnzahlungAn, standardAnzahlungsWeg, standardRestzahlung } from "../lib/zahlungsstandard";
import { StornoVorlagenEditor, stornoVorlagenListe, type StornoVorlage } from "../ui/StornoVorlagen";
import { alsWebpWandeln } from "../lib/bild-webp";

/**
 * Was in der Stripe-Modulkarte steht — WARTESCHLANGE Punkt 46b.
 *
 * Bewusst eine benannte Funktion und kein Ausdruck in der Kachel: es sind vier Fälle (drei
 * Zustände plus „noch nicht geladen"), und zwischen zweien davon liegt der Unterschied „Zahlung
 * kommt an" / „kein Gast kann bezahlen". Als Funktion ist das eine Tabelle, die man liest und
 * prüfen kann (`apps/web/test/stripe-modul-zeile.test.ts`).
 *
 * `null` heißt NOCH NICHT GELADEN und gilt als „kann kassieren": ein Alarm, der bei jedem
 * Seitenaufruf kurz aufblitzt, wird nach drei Tagen nicht mehr gelesen. Der Text bleibt dabei
 * `undefined`, damit die Kachel ihren Standardsatz zeigt statt einer Behauptung.
 */
export function stripeModulZeile(zahlweg: StripeZahlweg | null): { kannKassieren: boolean; text: string | undefined } {
  if (zahlweg === null) return { kannKassieren: true, text: undefined };
  if (zahlweg.zustand === "eigenes_konto") {
    // Die Kontokennung gehört sichtbar dazu: „verbunden" allein sagt nicht, ob es das RICHTIGE
    // Konto ist — und genau das ist bei Geld die Frage.
    return { kannKassieren: true, text: `Bereit · eigenes Stripe-Konto${zahlweg.kontoKennung ? ` (${zahlweg.kontoKennung})` : ""}` };
  }
  if (zahlweg.zustand === "systemkonto") return { kannKassieren: true, text: "Bereit · über den Zugang des Betreibers" };
  // Der Satz muss die FOLGE nennen, nicht den Zustand. „Nicht konfiguriert" liest sich wie eine
  // Kleinigkeit; „Keine Zahlung möglich" ist, was tatsächlich passiert.
  return { kannKassieren: false, text: "Keine Zahlung möglich · kein Stripe-Konto verbunden" };
}

const MODULES: {
  icon: string;
  name: string;
  desc: string;
  planned: boolean;
  statusText: string;
  tone: ModuleTone;
  healthKey?: "invoice" | "esign" | "ai" | "payments" | "places" | "telegram";
  missingText?: string;
  offText?: string;
  configureHint: string;
  configureKey?: ModuleConfigKey;
  tenantFlag?: TenantFlag;
  aktivWenn?: (z: ModulZustand) => boolean;
  /**
   * WARTESCHLANGE Punkt 46b: ein Statustext, der vom ZUSTAND abhaengt, nicht nur von „an/aus".
   * Bisher gab es genau zwei Saetze je Modul (`statusText`/`missingText`). Beim Zahlungsweg sind
   * es drei Faelle, und der Unterschied zwischen ihnen ist fuer den Betrieb bares Geld.
   */
  statusTextVon?: (z: ModulZustand) => string | undefined;
}[] = [
  {
    icon: "receipt_long", name: "LexOffice", desc: "Rechnungen automatisch erzeugen", planned: false,
    statusText: "Bereit · Rechnungen & Gutschriften", tone: "success", healthKey: "invoice",
    missingText: "Nicht konfiguriert · LexOffice-API-Key fehlt",
    // Kein Rückfall auf einen zentralen Schlüssel mehr (Antoine 07.08.2026, Ratsche in
    // apps/api/test/lexoffice-key-resolution.test.ts) — der Satz stand hier noch aus der Zeit davor.
    configureHint: "Der API-Key deines Betriebs – direkt hier. Ohne eigenen Key kann dieser Betrieb keine Rechnung stellen.",
    configureKey: "lexoffice",
  },
  {
    icon: "mail", name: "E-Mail-Sync (IMAP)", desc: "Anfragen aus dem Postfach importieren", planned: true,
    statusText: "Geplant · Posteingang läuft aktuell über E-Mail-Routing", tone: "info",
    configureHint: "Geplant – der Posteingang läuft aktuell über E-Mail-Routing, noch keine direkte IMAP-Anbindung.",
  },
  {
    icon: "chat", name: "WhatsApp", desc: "Kundenkommunikation per WhatsApp", planned: true,
    statusText: "Geplant · noch nicht verbunden", tone: "neutral",
    configureHint: "Geplant – noch nicht verbunden.",
  },
  {
    icon: "signature", name: "E-Signatur", desc: "Verträge digital unterschreiben", planned: false,
    statusText: "Bereit · Kostenübernahme-Verträge", tone: "primary", healthKey: "esign",
    missingText: "Teilkonfiguriert · Composio-Verbindung oder eSign-Template-ID fehlt",
    configureHint: "Composio-Verbindung (serverseitig) + eSign-Template-ID – direkt hier:",
    configureKey: "esign",
  },
  {
    icon: "auto_awesome", name: "KI-Assistent", desc: "Angebotstexte und Antworten vorschlagen", planned: false,
    statusText: "Bereit · Anschreiben, Freitext-Import, Chat", tone: "primary", healthKey: "ai",
    missingText: "Nicht konfiguriert · kein KI-Zugang hinterlegt",
    configureHint: "Der KI-Zugang selbst (Composio oder Anthropic-Schlüssel) wird beim Betrieb der Software hinterlegt und ist bewusst kein Eingabefeld — ein Zugangsschlüssel, der jede Anfrage bezahlt, gehört nicht in eine Mandanten-Oberfläche. Was der Assistent gerade tut, steht hier:",
    configureKey: "ki",
  },
  {
    /*
     * WARTESCHLANGE Punkt 46b — DIESE KARTE HAT GELOGEN.
     *
     * Sie hing an `healthKey: "payments"`, und das ist `isDepositConfigured(env)`: eine, wie der
     * Kommentar dort selbst sagt, „grobe, DB-freie Vorab-Pruefung, nicht mandantenscharf". Sie
     * beantwortet, ob MAESTRO ueberhaupt einen Stripe-Zugang hat — nicht, ob DIESER Betrieb
     * kassieren kann.
     *
     * Seit der Sperre (sql/446) ist das der Unterschied zwischen „Zahlung kommt an" und „kein Gast
     * kann bezahlen". Ein frisch angelegter Betrieb sah „Bereit · Anzahlungs-Links" und haette es
     * erst erfahren, wenn ein Gast zusagt und vor einer toten Seite steht. Jetzt haengt die Karte
     * an `/api/settings/stripe-zahlweg` — derselben Aufloesung, die auch der Zahlungsfluss nutzt.
     */
    icon: "credit_card", name: "Stripe-Zahlungen", desc: "Online-Zahlung für Kunden", planned: false,
    statusText: "Bereit · Anzahlungs-Links", tone: "success",
    missingText: "Keine Zahlung möglich · kein Stripe-Konto verbunden",
    // `null` = noch nicht geladen. Dann NICHT „keine Zahlung möglich" zeigen: ein Alarm, der beim
    // Laden jedes Mal kurz aufblitzt, wird nach drei Tagen nicht mehr gelesen.
    aktivWenn: (z) => stripeModulZeile(z.stripeZahlweg).kannKassieren,
    statusTextVon: (z) => stripeModulZeile(z.stripeZahlweg).text,
    configureHint: "Hier steht, worüber dein Betrieb Geld einnimmt. Steht „Keine Zahlung möglich“, kann kein Gast bezahlen – auch dann nicht, wenn er zusagt; MAESTRO erzeugt bewusst keinen Zahlungslink, solange nicht eindeutig feststeht, auf welches Konto das Geld fließt. Das Verbinden eines eigenen Stripe-Kontos direkt an dieser Stelle ist in Arbeit; bis dahin richtet es der Betreiber der Software ein. Wie viel angezahlt wird und wann der Rest fällig ist, entscheidest du unter „Zahlungen“ – das sind Vertragsbedingungen deines Betriebs, keine Einstellung dieses Moduls.",
  },
  {
    icon: "event", name: "Feiertage im Kalender", desc: "Feiertage als Badge im Kalender", planned: false,
    statusText: "Aktiv · Feiertage werden im Kalender angezeigt", tone: "info",
    missingText: "Aus · keine Feiertage im Kalender",
    configureHint: "Bundesweite und regionale Feiertage als Badge im Kalender – rein im Browser berechnet, kein externer Dienst.",
    configureKey: "feiertage", tenantFlag: "feiertageEnabled",
  },
  {
    icon: "wb_sunny", name: "Wetter je Vorgang", desc: "Wetterinfo im Vorgang anzeigen", planned: false,
    statusText: "Aktiv · Wetterinfo im Vorgang", tone: "info",
    missingText: "Aus · keine Wetterinfo im Vorgang",
    configureHint: 'Kurze Wetterinfo (Bright Sky/DWD) im Vorgang – nutzt die Betriebsadresse bei „Im Haus"-Events bzw. die Kundenadresse bei „Außer Haus"/Catering.',
    configureKey: "wetter", tenantFlag: "wetterEnabled",
  },
  {
    icon: "domain", name: "Handelsregister-Check", desc: "B2B-Kunden automatisch nachschlagen", planned: false,
    statusText: "Aktiv · Abruf bei neuen Firmenkunden", tone: "info",
    missingText: "Aus · kein Abruf bei Firmenkunden",
    configureHint: "Sobald ein Kunde mit Firma angelegt wird, läuft im Hintergrund einmalig ein kostenloser Abruf bei Handelsregister.de + Bundesanzeiger – die Tatsachen daraus stehen im Kunden-Profil in der Karte „Firmenprofil“, jede mit Quelle und Stand — beschreibend, ohne Bewertung. Handelsregister.de duldet informell ~60 Abrufe/Stunde insgesamt; pro Firma wird nur einmal abgerufen.",
    configureKey: "handelsregister", tenantFlag: "handelsregisterEnabled",
  },
  {
    icon: "travel_explore", name: "Google-Places-Lookup", desc: "Firmendaten im Kunden-Profil nachschlagen", planned: false,
    statusText: "Aktiv · Nachschlagen-Button im Kunden-Profil", tone: "info", healthKey: "places",
    missingText: "Nicht konfiguriert · Google-Places-Zugang fehlt",
    offText: "Aus · kein Nachschlagen im Kunden-Profil",
    configureHint: 'Manueller „Nachschlagen"-Button im Kunden-Profil: sucht Firma + Ort bei Google Places und zeigt Bewertung, Öffnungszeiten und Kontaktdaten zum gezielten Übernehmen – läuft NICHT automatisch bei neuen Kunden (jeder Abruf kostet, ein Mensch bestätigt jeden übernommenen Wert einzeln).',
    configureKey: "places", tenantFlag: "googlePlacesEnabled",
  },
  {
    icon: "schedule", name: "Öffnungszeiten & Google-Eintrag", desc: "Öffnungszeiten aus dem eigenen Google-Profil übernehmen", planned: false,
    statusText: "Aktiv · täglicher Abgleich mit dem bestätigten Eintrag", tone: "info", healthKey: "places",
    missingText: "Nicht konfiguriert · Google-Places-Zugang fehlt",
    offText: "Aus · noch kein Google-Eintrag bestätigt",
    configureHint:
      'Einmal den eigenen Google-Eintrag suchen und bestätigen — danach gleicht MAESTRO täglich nur noch diesen einen Eintrag ab, nie wieder über eine Textsuche. Abweichende Zeiten werden als Vorschlag gezeigt, nie stillschweigend übernommen.',
    configureKey: "oeffnungszeiten",
    aktivWenn: (z) => Boolean(z.einstellungen?.googlePlaceId),
  },
  {
    icon: "send", name: "Telegram-Benachrichtigungen", desc: "Wichtige Meldungen im Telegram-Chat erhalten", planned: false,
    statusText: "Aktiv · Meldungen gehen an die verknüpften Chats", tone: "info", healthKey: "telegram",
    missingText: "Nicht eingerichtet · Bot-Zugang fehlt",
    offText: "Eingerichtet · noch kein Chat verknüpft, es geht nichts hinaus",
    configureHint:
      "MAESTRO meldet sich von sich aus im Telegram-Chat: wenn ein Angebots-Entwurf bereitliegt, wenn eine neue Anfrage zu wenig Angaben hat, wenn das Tageslimit der Automatik erreicht ist, wenn ein Aufschub fällig wird. Der Kanal geht nur in eine Richtung – im Telegram-Chat lässt sich ausschließlich „was ist neu?“ abfragen, keine Aktion auslösen. Beträge und Preise stehen nie in einer dieser Meldungen.",
    configureKey: "telegram",
    aktivWenn: (z) => (z.telegram === null ? true : z.telegram.chats.length > 0),
  },
  {
    icon: "verified", name: "USt-IdNr.-Bestätigung", desc: "USt-IdNr. von EU-Kunden amtlich bestätigen lassen", planned: false,
    statusText: "Aktiv · Bestätigung beim Bundeszentralamt für Steuern", tone: "info",
    offText: "Aus · keine Bestätigungsabfrage",
    configureHint:
      "Bestätigungsabfrage nach § 18e UStG beim Bundeszentralamt für Steuern: kostenlos, ohne Zugangsschlüssel. " +
      "Bestätigt werden nur Nummern aus ANDEREN EU-Mitgliedstaaten — für deutsche gibt es kein solches Verfahren. " +
      "Gespeichert wird das Ergebnis samt Zeitpunkt; das ist eine Momentaufnahme und keine dauerhafte Aussage.",
    configureKey: "ustid", tenantFlag: "ustidEnabled",
  },
];
export interface ModuleSettings {
  /**
   * Bughunt 24.08.2026 § 1: hier stand `lexofficeApiKey: string | null`. Die API liefert den
   * Schlüssel nicht mehr aus — nur noch, OB einer hinterlegt ist. Derselbe Feldname wie im
   * Agent-Werkzeug `get_module_settings`.
   */
  lexofficeKeyHinterlegt: boolean | null;
  feiertageEnabled: boolean | null;
  bundesland: string | null;
  wetterEnabled: boolean | null;
  handelsregisterEnabled: boolean | null;
  googlePlacesEnabled: boolean | null;
  ustidEnabled: boolean | null;
  companyVatId: string | null;
  esignTemplateId: string | null;
  /** Migration 433: nur der ZUSTAND des eSignatures-Schluessels — nie der Wert. */
  esignKeyHinterlegt?: boolean | null;
  googlePlaceId: string | null;
  openingHours: string[] | null;
  openingHoursPending: string[] | null;
  openingHoursSyncedAt: string | null;
  companyLegalName: string | null;
  companyAddress: string | null;
  placesBudget?: AbrufBudget | null;
}
interface TelegramKanal {
  botKonfiguriert: boolean;
  internSecretKonfiguriert: boolean;
  botUsername: string | null;
  zugang: { anzeigename: string; secret: string; weg: string; erneuerungsLink: string | null };
  chats: { id: string; anzeige: string; stackUserId: string; status: string; enrolledAt: string }[];
}
interface ModulZustand {
  einstellungen: ModuleSettings | null;
  telegram: TelegramKanal | null;
  /** `null` = noch nicht geladen (s. `aktivWenn` beim Stripe-Modul). */
  stripeZahlweg: StripeZahlweg | null;
}

interface AgentStatus {
  agentMode: "off" | "shadow" | "live";
  tagesdeckelGenutzt: number;
  tagesdeckel: number;
  letzterEntwurf: string | null;
  reife: { versendeteAngebote: number; referenzAngebote: number };
  kaltstart: string | null;
  referenzEinladung: string | null;
}
const AGENT_MODUS_TEXT: Record<AgentStatus["agentMode"], { titel: string; erklaerung: string }> = {
  off: { titel: "Aus", erklaerung: "MAESTRO bereitet nichts von selbst vor." },
  shadow: { titel: "Mitlesend", erklaerung: "MAESTRO bereitet Entwürfe vor, während es Ihren Betrieb kennenlernt." },
  live: { titel: "Aktiv", erklaerung: "MAESTRO bereitet Entwürfe vor, sobald eine Anfrage eingeht." },
};
function AgentKachel() {
  const q = useApi<AgentStatus>("/api/agent/status");
  if (q.loading) return <div className="mt-3"><LoadingRows rows={2} /></div>;
  if (q.error || !q.data) return <ErrorNote message={q.error ?? "Status nicht abrufbar."} />;
  const s = q.data;
  const modus = AGENT_MODUS_TEXT[s.agentMode] ?? AGENT_MODUS_TEXT.off;
  const ton =
    s.agentMode === "live"
      ? "bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
      : s.agentMode === "shadow"
        ? "bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
        : "bg-surface-container-high text-on-surface-variant";
  return (
    <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2.5 py-0.5 font-body-sm text-body-sm font-semibold ${ton}`}>{modus.titel}</span>
        <span className="font-body-sm text-body-sm text-on-surface-variant">{modus.erklaerung}</span>
      </div>
      {s.kaltstart && (
        <div className="rounded-lg bg-secondary/70 px-3 py-2 font-body-sm text-body-sm text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
          {s.kaltstart}
          {s.referenzEinladung && <span className="mt-1 block">{s.referenzEinladung}</span>}
        </div>
      )}
      <dl className="grid gap-2 sm:grid-cols-3">
        <div>
          <dt className="font-body-sm text-body-sm text-on-surface-variant">Heute vorbereitet</dt>
          <dd className="font-title-sm text-title-sm text-on-background dark:text-inverse-on-surface">
            {s.tagesdeckelGenutzt}
            {s.tagesdeckel > 0 && <span className="text-on-surface-variant"> / {s.tagesdeckel}</span>}
          </dd>
        </div>
        <div>
          <dt className="font-body-sm text-body-sm text-on-surface-variant">Versendete Angebote</dt>
          <dd className="font-title-sm text-title-sm text-on-background dark:text-inverse-on-surface">{s.reife.versendeteAngebote}</dd>
        </div>
        <div>
          <dt className="font-body-sm text-body-sm text-on-surface-variant">Angebote aus Unterlagen</dt>
          <dd className="font-title-sm text-title-sm text-on-background dark:text-inverse-on-surface">{s.reife.referenzAngebote}</dd>
        </div>
      </dl>
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        {s.letzterEntwurf
          ? `Zuletzt vorbereitet: ${new Date(s.letzterEntwurf).toLocaleString("de-DE")}.`
          : "Noch kein Entwurf vorbereitet."}{" "}
        Versendet wird nie ohne Ihre Freigabe.
      </p>
    </div>
  );
}
/**
 * E-Signatur-Kachel — Vorlagen-ID UND Schluessel dieses Betriebs (Migration 433).
 *
 * Beide stehen hier zusammen, und das ist der Punkt: sie gehoeren beim Anbieter ins SELBE Konto.
 * Sie getrennt zu fuehren (Vorlage pro Mandant, Token global als Worker-Secret) war der
 * strukturelle Fehler, den diese Kachel behebt — Antoine, 16.09.2026: „Wir sind ein Tenant-System.
 * […] Der Betreiber hat keinen Zugriff auf Worker Secrets."
 *
 * Der Schluessel folgt exakt der LexOffice-Kachel weiter unten: das Feld startet IMMER leer, was
 * darin steht ist ein NEUER Schluessel, der Zustand („hinterlegt"/„nicht hinterlegt") bleibt fuer
 * jede Rolle sichtbar, und Speichern bei leerem Feld ist abgeschaltet (sonst waere ein Klick ohne
 * Eingabe ein stilles Loeschen). Zusaetzlich gegenueber LexOffice: der Zugang wird beim Speichern
 * beim Anbieter GEPRUEFT — die Antwort sagt sofort, ob er funktioniert.
 */
// Benannter Export wie `LexofficeConfig`: die Kachel wird in apps/web/test direkt gerendert,
// statt die ganze Einstellungen-Seite aufzubauen (Muster: lexoffice-kachel-schluessel.test.tsx).
export function EsignConfig({ canEdit, onSaved }: { canEdit: boolean; onSaved: () => void }) {
  const q = useApi<{ esignTemplateId: string | null; esignTemplateIdPrivat?: string | null; esignKeyHinterlegt?: boolean }>("/api/settings/esign");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Der Schluessel hat eigene Zustaende: er wird geprueft, das dauert, und sein Ergebnis ist eine
  // andere Aussage als "Vorlage gespeichert".
  const [schluessel, setSchluessel] = useState("");
  const [schluesselBusy, setSchluesselBusy] = useState(false);
  const [schluesselOk, setSchluesselOk] = useState(false);
  const [schluesselFehler, setSchluesselFehler] = useState<string | null>(null);
  const [entfernenBestaetigen, setEntfernenBestaetigen] = useState(false);
  const hinterlegt = q.data?.esignKeyHinterlegt === true;

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const value = ((form.get("esignTemplateId") as string) ?? "").trim() || null;
    // Punkt 42: zweite Vorlage für Privatpersonen (leer = die obere gilt für alle).
    const privat = ((form.get("esignTemplateIdPrivat") as string) ?? "").trim() || null;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/esign", "PUT", { esignTemplateId: value, esignTemplateIdPrivat: privat });
      setSaved(true);
      q.reload();
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function schreibeSchluessel(value: string | null) {
    setSchluesselBusy(true);
    setSchluesselFehler(null);
    setSchluesselOk(false);
    try {
      await apiMutate("/api/settings/esign/schluessel", "PUT", { esignApiKey: value });
      setSchluessel("");
      setEntfernenBestaetigen(false);
      setSchluesselOk(true);
      q.reload();
      onSaved();
    } catch (err) {
      // Die Meldung kommt woertlich vom Server (z. B. "eSignatures.io hat den Schluessel
      // abgelehnt (HTTP 401) …"). Genau das will Antoine sehen — nicht "Fehler beim Speichern".
      setSchluesselFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setSchluesselBusy(false);
    }
  }

  if (q.loading) return <div className="mt-3"><LoadingRows rows={1} /></div>;
  return (
    <form className="mt-3 space-y-3" onSubmit={(e) => void save(e)}>
      {error && <ErrorNote message={error} />}
      <label className="block">
        <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
          Vorlage Firma und Verein (eSignatures.io Template-ID)
        </span>
        <Input
          name="esignTemplateId"
          data-testid="esign-vorlage-firma"
          defaultValue={q.data?.esignTemplateId ?? ""}
          placeholder="0f9f9ad4-02a8-4678-889a-52d3b4bd459e"
          disabled={!canEdit}
        />
      </label>
      <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
        Ohne diese Template-ID lässt sich in einem Vorgang keine Kostenübernahme anfordern.
      </p>
      <label className="block">
        <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
          Vorlage Privatperson (eSignatures.io Template-ID)
        </span>
        <Input
          name="esignTemplateIdPrivat"
          data-testid="esign-vorlage-privat"
          defaultValue={q.data?.esignTemplateIdPrivat ?? ""}
          placeholder="optional"
          disabled={!canEdit}
        />
      </label>
      <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
        Gilt für Kunden vom Typ „Privat“. Leer lassen, solange es keine eigene Fassung gibt — dann nutzen auch Privatpersonen die
        Vorlage darüber.
      </p>
      {canEdit && (
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={busy}>{busy ? "Speichert …" : "Speichern"}</Button>
          {saved && (
            <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
              <Icon name="check_circle" className="text-[18px]" /> Gespeichert
            </span>
          )}
        </div>
      )}

      {/* Der Schlüssel dieses Betriebs — Migration 433. Bewusst in derselben Kachel wie die
          Vorlagen-ID: beide gehören ins selbe eSignatures-Konto, und getrennt zu suchen war
          genau das Problem. Eigener Endpunkt, deshalb auch eigener Absende-Knopf statt eines
          gemeinsamen Formulars. */}
      <div className="mt-4 space-y-3 border-t border-outline-variant pt-4">
        {schluesselFehler && <ErrorNote message={schluesselFehler} />}
        <p className="flex items-center gap-2 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${hinterlegt ? "bg-tertiary" : "bg-secondary-fixed-dim"}`} />
          {hinterlegt ? "Ein eigener eSignatures-Schlüssel ist hinterlegt." : "Es ist noch kein eSignatures-Schlüssel hinterlegt."}
        </p>
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
            {hinterlegt ? "Neuen eSignatures-Schlüssel eintragen" : "eSignatures-Schlüssel dieses Betriebs"}
          </span>
          <Input
            name="esignApiKey"
            type="password"
            autoComplete="off"
            value={schluessel}
            onChange={(e) => setSchluessel(e.currentTarget.value)}
            placeholder={hinterlegt ? "•••••••• hinterlegt" : "Noch kein Schlüssel hinterlegt"}
            disabled={!canEdit}
          />
        </label>
        <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
          Bestimmt, in welchem eSignatures-Konto die Kostenübernahme-Verträge dieses Betriebs
          entstehen. Er muss aus <strong>demselben</strong> Konto stammen wie die Template-ID oben —
          sonst entsteht der Vertrag im falschen Konto oder gar nicht. Ohne eigenen Schlüssel kann
          dieser Betrieb keine Kostenübernahme anfordern.
        </p>
        <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
          Der hinterlegte Schlüssel wird aus Sicherheitsgründen nie angezeigt — auch Inhabern nicht.
          Speichern <strong>ersetzt</strong> ihn. Beim Speichern prüfen wir ihn direkt bei
          eSignatures.io; schlägt die Prüfung fehl, wird nichts gespeichert.
        </p>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              disabled={schluesselBusy || !schluessel.trim()}
              onClick={() => void schreibeSchluessel(schluessel.trim())}
            >
              {/* Bewusst NICHT nur „Speichern": die Kachel hat zwei Absende-Knöpfe (Vorlagen-ID
                  und Schlüssel). Zwei gleich beschriftete Knöpfe nebeneinander sind für den
                  Betreiber eine Falle — und für einen Test nicht unterscheidbar. */}
              {schluesselBusy ? "Prüft beim Anbieter …" : hinterlegt ? "Schlüssel ersetzen" : "Schlüssel speichern"}
            </Button>
            {schluesselOk && (
              <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
                <Icon name="check_circle" className="text-[18px]" /> Geprüft und gespeichert
              </span>
            )}
            {hinterlegt &&
              (entfernenBestaetigen ? (
                <>
                  <button
                    type="button"
                    className="font-label-md text-[11px] font-semibold text-error hover:underline"
                    disabled={schluesselBusy}
                    onClick={() => void schreibeSchluessel(null)}
                  >
                    Wirklich entfernen?
                  </button>
                  <button
                    type="button"
                    className="font-label-md text-[11px] font-semibold text-secondary hover:underline"
                    onClick={() => setEntfernenBestaetigen(false)}
                  >
                    Abbrechen
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="font-label-md text-[11px] font-semibold text-secondary hover:underline dark:text-secondary-fixed-dim"
                  disabled={schluesselBusy}
                  onClick={() => setEntfernenBestaetigen(true)}
                >
                  Schlüssel entfernen
                </button>
              ))}
          </div>
        )}
      </div>
    </form>
  );
}
/**
 * LexOffice-Kachel — zeigt den ZUSTAND des Schlüssels, nie den Schlüssel.
 *
 * Bughunt 24.08.2026 § 1: hier stand `defaultValue={data?.lexofficeApiKey}` in einem
 * `disabled`-Input. `disabled` verhindert Tippen, nicht Lesen — der Buchhaltungs-Schlüssel des
 * Betriebs stand damit im Klartext auf dem Bildschirm jedes Mitarbeiters, drei Klicks nach dem
 * Login. Seit dem 24.08.2026 liefert die API ihn gar nicht mehr aus (nur noch
 * `lexofficeKeyHinterlegt`), und die Kachel ist entsprechend umgebaut:
 *
 *  - Das Feld startet IMMER leer. Was drinsteht, ist ein NEUER Schlüssel — nie der alte.
 *  - Der Zustand („hinterlegt" / „nicht hinterlegt") bleibt für JEDE Rolle sichtbar; das ist der
 *    dokumentierte Lese-Vorsatz der Modul-Kacheln (Konzept 2.2: „was ist eingeschaltet").
 *  - Speichern ERSETZT den bestehenden Schlüssel. Deshalb ist der Knopf bei leerem Feld
 *    abgeschaltet: vorher hätte ein Klick auf „Speichern" ohne Eingabe den hinterlegten Schlüssel
 *    gelöscht — mit vorbefülltem Feld fiel das nicht auf, mit leerem Feld wäre es eine Falle.
 *  - Löschen ist weiterhin möglich, aber als eigene, zweistufige Handlung.
 */
// Benannter Export wie `CompanyHeaderForm`: die Kachel wird in apps/web/test direkt gerendert,
// statt die ganze Einstellungen-Seite aufzubauen (Muster: geschaeftsprofil-form.test.tsx).
export function LexofficeConfig({ canEdit, data, onSaved }: { canEdit: boolean; data: ModuleSettings | null; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eingabe, setEingabe] = useState("");
  const [entfernenBestaetigen, setEntfernenBestaetigen] = useState(false);
  const hinterlegt = data?.lexofficeKeyHinterlegt === true;

  async function schreibe(value: string | null) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/lexoffice", "PUT", { lexofficeApiKey: value });
      setEingabe("");
      setEntfernenBestaetigen(false);
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const value = eingabe.trim();
    if (!value) return; // Leer speichern ist keine Aktion — Löschen läuft über „Schlüssel entfernen".
    await schreibe(value);
  }

  return (
    <form className="mt-3 space-y-3" onSubmit={(e) => void save(e)}>
      {error && <ErrorNote message={error} />}
      <p className="flex items-center gap-2 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${hinterlegt ? "bg-tertiary" : "bg-secondary-fixed-dim"}`} />
        {hinterlegt ? "Ein eigener Schlüssel ist hinterlegt." : "Es ist noch kein Schlüssel hinterlegt."}
      </p>
      <label className="block">
        <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
          {hinterlegt ? "Neuen LexOffice API-Key eintragen" : "LexOffice API-Key dieses Betriebs"}
        </span>
        <Input
          name="lexofficeApiKey"
          type="password"
          autoComplete="off"
          value={eingabe}
          onChange={(e) => setEingabe(e.currentTarget.value)}
          placeholder={hinterlegt ? "•••••••• hinterlegt" : "Noch kein Schlüssel hinterlegt"}
          disabled={!canEdit}
        />
      </label>
      <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
        Bestimmt, in welchem LexOffice-Konto die Rechnungen und Gutschriften dieses Betriebs landen.
        Bei mehreren Betrieben unbedingt den eigenen Schlüssel eintragen — sonst laufen die Belege
        in ein fremdes Konto und lassen sich dort nicht mehr zurückholen. Ohne eigenen Schlüssel
        kann dieser Betrieb keine Rechnung stellen.
      </p>
      <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
        Der hinterlegte Schlüssel wird aus Sicherheitsgründen nie angezeigt — auch Inhabern nicht.
        Speichern <strong>ersetzt</strong> ihn durch den hier eingetragenen.
      </p>
      {canEdit ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={busy || !eingabe.trim()}>
            {busy ? "Speichert …" : hinterlegt ? "Schlüssel ersetzen" : "Speichern"}
          </Button>
          {hinterlegt &&
            (entfernenBestaetigen ? (
              <>
                <button
                  type="button"
                  className="font-label-md text-[11px] font-semibold text-error hover:underline"
                  disabled={busy}
                  onClick={() => void schreibe(null)}
                >
                  Wirklich entfernen?
                </button>
                <button
                  type="button"
                  className="font-label-md text-[11px] font-semibold text-secondary hover:underline"
                  onClick={() => setEntfernenBestaetigen(false)}
                >
                  Abbrechen
                </button>
              </>
            ) : (
              <button
                type="button"
                className="font-label-md text-[11px] font-semibold text-secondary hover:underline dark:text-secondary-fixed-dim"
                disabled={busy}
                onClick={() => setEntfernenBestaetigen(true)}
              >
                Schlüssel entfernen
              </button>
            ))}
          {saved && (
            <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
              <Icon name="check_circle" className="text-[18px]" /> Gespeichert
            </span>
          )}
        </div>
      ) : (
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Nur Inhaber dürfen den Schlüssel ändern.
        </p>
      )}
    </form>
  );
}
function RechercheModulConfig({
  canEdit,
  feld,
  data,
  onSaved,
  gesperrt,
  gesperrtHinweis,
  extra,
}: {
  canEdit: boolean;
  feld: TenantFlag;
  data: ModuleSettings | null;
  onSaved: () => void;
  gesperrt?: boolean;
  gesperrtHinweis?: string;
  extra?: (aktiv: boolean, speichere: (felder: Record<string, unknown>) => Promise<void>) => ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const aktiv = data?.[feld] === true;
  async function speichere(felder: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/recherche", "PUT", felder);
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-3 space-y-3">
      {error && <ErrorNote message={error} />}
      <ToggleSwitch
        checked={aktiv}
        onChange={(v) => void speichere({ [feld]: v })}
        label={aktiv ? "Eingeschaltet" : "Ausgeschaltet"}
        disabled={!canEdit || gesperrt || busy}
        disabledHint={gesperrt ? gesperrtHinweis : !canEdit ? "Nur Inhaber dürfen dieses Modul schalten." : undefined}
      >
        {extra?.(aktiv, speichere)}
      </ToggleSwitch>
      {saved && (
        <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
          <Icon name="check_circle" className="text-[18px]" /> Gespeichert
        </span>
      )}
    </div>
  );
}
function OeffnungszeitenConfig({
  canEdit,
  data,
  zugangVorhanden,
  onSaved,
}: {
  canEdit: boolean;
  data: ModuleSettings | null;
  zugangVorhanden: boolean;
  onSaved: () => void;
}) {
  const [treffer, setTreffer] = useState<EintragsTreffer | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const anzeige = eintragsAnzeige({
    zugangVorhanden,
    adresseVorhanden: Boolean(data?.companyLegalName?.trim() && data?.companyAddress?.trim()),
    bestaetigtePlaceId: data?.googlePlaceId ?? null,
    bestaetigteZeiten: data?.openingHours ?? null,
    offenerVorschlag: data?.openingHoursPending ?? null,
    letzterAbgleich: data?.openingHoursSyncedAt ?? null,
    suchtreffer: treffer,
  });
  const budget = budgetAnzeige(data?.placesBudget);
  async function handle(art: EintragsHandlungsArt) {
    setError(null);
    if (art === "verwerfen") {
      setTreffer(undefined);
      return;
    }
    setBusy(true);
    try {
      if (art === "suchen") {
        const gefunden = await apiMutate<EintragsTreffer | null>("/api/settings/oeffnungszeiten/suche", "GET");
        setTreffer(gefunden);
      } else if (art === "bestaetigen") {
        if (!treffer) return;
        await apiMutate("/api/settings/oeffnungszeiten", "PUT", { placeId: treffer.placeId });
        setTreffer(undefined);
        onSaved();
      } else if (art === "uebernehmen") {
        await apiMutate("/api/settings/oeffnungszeiten", "PUT", { vorschlagUebernehmen: true });
        onSaved();
      } else if (art === "loesen") {
        await apiMutate("/api/settings/oeffnungszeiten", "PUT", { placeId: null });
        setTreffer(undefined);
        onSaved();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  const zeiten = treffer ? treffer.zeiten : (data?.openingHours ?? []);
  const vorschlag = data?.openingHoursPending ?? [];
  const googleWerte: readonly unknown[] = [
    zeiten,
    vorschlag,
    treffer?.name ?? null,
    treffer?.adresse ?? null,
    data?.googlePlaceId ?? null,
  ];
  return (
    <div className="mt-3 space-y-3">
      {error && <ErrorNote message={error} />}
      <p className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">{anzeige.satz}</p>
      {budget && (
        <div>
          <p
            className={
              budget.erschoepft
                ? "font-body-sm text-body-sm font-semibold text-error dark:text-error"
                : "font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim"
            }
          >
            {budget.satz}
          </p>
          <div className="mt-1 h-1 w-full max-w-xs overflow-hidden rounded-full bg-outline-variant/40 dark:bg-outline/30">
            <div
              className={budget.erschoepft ? "h-full bg-error" : "h-full bg-primary"}
              style={{ width: `${Math.round(budget.anteil * 100)}%` }}
            />
          </div>
        </div>
      )}
      {anzeige.verknuepft && data?.googlePlaceId && (
        <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
          Bestätigter Eintrag: <span className="font-mono">{data.googlePlaceId}</span>{" "}
          <a
            className="text-primary hover:underline"
            href={`https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(data.googlePlaceId.replace(/^places\//, ""))}`}
            target="_blank"
            rel="noreferrer noopener"
          >
            bei Google ansehen
          </a>
        </p>
      )}
      {treffer && (
        <p className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
          {trefferZeile(treffer)}
        </p>
      )}
      {zeiten.length > 0 && (
        <div>
          {anzeige.quelleUndStand && (
            <p className="mb-1 font-body-sm text-[11px] uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              {anzeige.quelleUndStand}
            </p>
          )}
          <ul className="space-y-0.5">
            {zeiten.map((z, i) => (
              <li key={i} className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                {z}
              </li>
            ))}
          </ul>
        </div>
      )}
      {vorschlag.length > 0 && (
        <div className="rounded-lg border border-outline-variant/40 p-2 dark:border-outline/30">
          <p className="mb-1 font-body-sm text-[11px] uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
            Offener Vorschlag — {standZeile("Google-Eintrag", data?.openingHoursSyncedAt)}
          </p>
          <ul className="space-y-0.5">
            {vorschlag.map((z, i) => (
              <li key={i} className="font-body-sm text-[12px] text-on-surface dark:text-inverse-on-surface">
                {z}
              </li>
            ))}
          </ul>
        </div>
      )}
      <GoogleAttribution werte={googleWerte} />
      {canEdit ? (
        anzeige.handlungen.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {anzeige.handlungen.map((h) => (
              <Button
                key={h.art}
                type="button"
                variant={h.betont ? "primary" : "secondary"}
                disabled={busy}
                onClick={() => void handle(h.art)}
              >
                {busy ? "Einen Moment …" : h.beschriftung}
              </Button>
            ))}
          </div>
        )
      ) : (
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Nur Inhaber dürfen den Google-Eintrag bestätigen oder lösen.
        </p>
      )}
    </div>
  );
}
function TelegramConfig({
  canEdit,
  data,
  loading,
  onChanged,
}: {
  canEdit: boolean;
  data: TelegramKanal | null;
  loading: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [einladung, setEinladung] = useState<{ command: string; botUsername: string | null; instructions: string; expiresInSeconds: number } | null>(null);
  if (!canEdit) {
    return (
      <p className="mt-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Nur Inhaber dürfen den Telegram-Kanal einsehen und verwalten.
      </p>
    );
  }
  if (loading) return <div className="mt-3"><LoadingRows rows={2} /></div>;
  if (!data) return <div className="mt-3"><ErrorNote message="Der Zustand des Telegram-Kanals konnte nicht geladen werden." /></div>;
  const fehlend: string[] = [];
  if (!data.botKonfiguriert) fehlend.push("der Bot-Zugang");
  if (!data.internSecretKonfiguriert) fehlend.push("das gemeinsame Secret der beiden Dienste");
  const bereit = fehlend.length === 0;
  async function verknuepfen() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiMutate<{ command: string; botUsername: string | null; instructions: string; expiresInSeconds: number }>(
        "/api/agent/telegram/enroll",
        "POST",
        {},
      );
      setEinladung(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function widerrufen(id: string) {
    setBusy(true);
    setError(null);
    try {
      await apiMutate(`/api/agent/telegram/chats/${id}`, "DELETE");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-3 space-y-4">
      {error && <ErrorNote message={error} />}
      {!bereit && (
        <div className="rounded-lg border border-outline-variant/50 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10">
          <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
            Dieses Modul ruht. Es fehlt {fehlend.join(" und ")}.
          </p>
          {!data.botKonfiguriert && (
            <>
              <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {data.zugang.weg}
              </p>
              {data.zugang.erneuerungsLink ? (
                <a
                  href={data.zugang.erneuerungsLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block font-label-md text-[11px] font-semibold text-primary hover:underline"
                >
                  Zur Seite des Anbieters
                </a>
              ) : (
                <p className="mt-1 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                  Es gibt dafür keine Einstellungsseite im Netz — Telegram vergibt Bot-Zugänge ausschließlich im Chat mit @BotFather.
                </p>
              )}
            </>
          )}
          <p className="mt-2 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
            Hinterlegt wird der Zugang beim Betrieb der Software (als {data.zugang.secret}), nicht hier: ein Bot bedient alle
            Betriebe, und wer seinen Zugang hat, kann in seinem Namen schreiben. Deshalb steht hier bewusst kein Eingabefeld.
          </p>
          <p className="mt-2 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
            Sobald der Zugang steht, erscheint hier ein Knopf, mit dem du deinen eigenen Telegram-Chat verknüpfst. Erst dann
            gehen Meldungen hinaus — vorher passiert nichts, still und ohne Kosten.
          </p>
        </div>
      )}
      <div>
        <h5 className="mb-2 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
          Verknüpfte Chats
        </h5>
        {data.chats.length === 0 ? (
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            {bereit
              ? "Noch keiner. Der Kanal ist eingerichtet, aber es gibt niemanden, an den er senden könnte."
              : "Noch keiner — solange der Zugang fehlt, lässt sich auch keiner verknüpfen."}
          </p>
        ) : (
          <ul className="space-y-2">
            {data.chats.map((c) => (
              <li
                key={c.id}
                className="flex items-center gap-3 rounded-lg border border-outline-variant/40 px-3 py-2 dark:border-outline/30"
              >
                <Icon name="chat" className="shrink-0 text-[18px] text-secondary dark:text-secondary-fixed-dim" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    Chat {c.anzeige}
                  </p>
                  <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                    verknüpft am {formatDate(c.enrolledAt)}
                  </p>
                </div>
                <button
                  type="button"
                  className="shrink-0 font-label-md text-[11px] font-semibold text-error hover:underline disabled:opacity-50"
                  disabled={busy}
                  onClick={() => void widerrufen(c.id)}
                >
                  Verknüpfung aufheben
                </button>
              </li>
            ))}
          </ul>
        )}
        {data.chats.length > 0 && (
          <p className="mt-2 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
            Angezeigt sind nur die letzten vier Stellen der Chat-Kennung — die vollständige Kennung ist personenbezogen und
            bleibt auf dem Server. „Verknüpfung aufheben" löscht sie dort ebenfalls.
          </p>
        )}
      </div>
      {bereit && (
        <div>
          <Button type="button" disabled={busy} onClick={() => void verknuepfen()}>
            {busy ? "Einen Moment …" : "Chat verknüpfen"}
          </Button>
          {einladung && (
            <div className="mt-3 rounded-lg border border-outline-variant/50 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10">
              <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">{einladung.instructions}</p>
              <code className="mt-2 block break-all rounded bg-surface-variant px-2 py-1.5 font-mono text-[12px] dark:bg-white/[0.06]">
                {einladung.command}
              </code>
              <p className="mt-2 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                Dieser Code gilt {Math.round(einladung.expiresInSeconds / 60)} Minuten und lässt sich genau einmal verwenden. Er
                wird nirgends gespeichert — wenn du dieses Feld schließt, ist er weg, und du erzeugst einfach einen neuen.
              </p>
            </div>
          )}
        </div>
      )}
      <p className="border-t border-outline-variant/30 pt-3 font-body-sm text-[11px] text-secondary dark:border-outline/30 dark:text-secondary-fixed-dim">
        Der Telegram-Chat kann ausschließlich melden und auf „was ist neu?" antworten. Anfragen bearbeiten, Angebote ändern
        oder etwas versenden geht dort nicht — dafür gibt es den Chat in MAESTRO, wo dein Login und deine Rechte gelten.
      </p>
    </div>
  );
}
function UstIdConfig({ canEdit, data, onSaved }: { canEdit: boolean; data: ModuleSettings | null; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nummer, setNummer] = useState(data?.companyVatId ?? "");
  const gespeicherteNummer = (data?.companyVatId ?? "").trim();
  const aktiv = data?.ustidEnabled === true;
  async function speichere(felder: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/recherche", "PUT", felder);
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-3 space-y-3">
      {error && <ErrorNote message={error} />}
      <label className="block max-w-xs">
        <span className="mb-1 block font-body-sm text-[11px] font-semibold uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
          Eigene USt-IdNr. dieses Betriebs
        </span>
        <Input
          value={nummer}
          placeholder="DE123456789"
          disabled={!canEdit || busy}
          onChange={(e) => setNummer(e.target.value)}
          onBlur={() => {
            const wert = nummer.trim();
            if (wert === gespeicherteNummer) return;
            void speichere({ companyVatId: wert || null });
          }}
        />
        <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
          Mit dieser Nummer stellt MAESTRO die Anfrage. Eine Bestätigung gilt immer nur für den Anfragenden — sie wird
          nie mit einer fremden Nummer geholt. Dasselbe Feld steht auch unter „Zahlungen“; es genügt, es einmal zu
          pflegen.
        </span>
      </label>
      <ToggleSwitch
        checked={aktiv}
        onChange={(v) => void speichere({ ustidEnabled: v })}
        label={aktiv ? "Eingeschaltet" : "Ausgeschaltet"}
        disabled={!canEdit || busy || (!aktiv && !gespeicherteNummer)}
        disabledHint={
          !canEdit
            ? "Nur Inhaber dürfen dieses Modul schalten."
            : !gespeicherteNummer
              ? "Ohne eigene USt-IdNr. bliebe der Schalter wirkungslos — bitte zuerst die Nummer oben eintragen."
              : undefined
        }
      />
      {saved && (
        <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
          <Icon name="check_circle" className="text-[18px]" /> Gespeichert
        </span>
      )}
    </div>
  );
}
function BundeslandFeld({ canEdit, wert, onChange }: { canEdit: boolean; wert: string; onChange: (code: string) => void }) {
  return (
    <label className="mt-3 block max-w-xs">
      <span className="mb-1 block font-body-sm text-[11px] font-semibold uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
        Bundesland
      </span>
      <Select
        ariaLabel="Bundesland"
        value={wert || KEIN_WERT}
        onValueChange={(v) => onChange(v === KEIN_WERT ? "" : v)}
        disabled={!canEdit}
        options={[
          { value: KEIN_WERT, label: "Nicht festgelegt" },
          ...BUNDESLAENDER.map((b) => ({ value: b.code, label: b.name })),
        ]}
      />
      <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
        Wird beim Speichern der Betriebsadresse automatisch aus ihr ermittelt — hier jederzeit überschreibbar.
      </span>
    </label>
  );
}
const ROLE_PILL: Record<string, string> = {
  owner: "bg-primary/10 text-primary dark:bg-primary-fixed-dim/10 dark:text-primary-fixed-dim",
  admin: "bg-primary/10 text-primary dark:bg-primary-fixed-dim/10 dark:text-primary-fixed-dim",
  staff:
    "bg-secondary-container text-on-secondary-container dark:bg-white/[0.08] dark:text-surface-variant",
};
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
        {label}
      </label>
      <div className="rounded-lg border border-outline-variant bg-surface-container-low px-4 py-2.5 font-body-md text-body-md text-on-surface dark:border-outline/40 dark:bg-surface-variant/20 dark:text-inverse-on-surface">
        {children}
      </div>
    </div>
  );
}
function TenantNameForm({ initialName, onSaved }: { initialName: string; onSaved: () => void }) {
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setName(initialName);
  }, [initialName]);
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Unternehmensname darf nicht leer sein.");
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate<{ id: string; name: string; slug: string }>("/api/tenant", "PATCH", { name: trimmed });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="space-y-2" onSubmit={(e) => void save(e)}>
      <label className="block">
        <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
          Unternehmensname
        </span>
        <Input
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setSaved(false);
          }}
        />
      </label>
      {error && <ErrorNote message={error} />}
      <div className="flex items-center gap-3">
        <Button type="submit" className="!py-2" disabled={busy}>
          {busy ? "Speichert …" : "Speichern"}
        </Button>
        {saved && (
          <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
            <Icon name="check_circle" className="text-[18px]" /> Gespeichert
          </span>
        )}
      </div>
    </form>
  );
}
type ModuleChipStatus = "active" | "off" | "missing" | "planned";
function ModuleStatusChip({ status }: { status: ModuleChipStatus }) {
  const CONF: Record<ModuleChipStatus, { label: string; className: string }> = {
    active: { label: "Aktiv", className: "bg-tertiary-container text-tertiary" },
    off: {
      label: "Aus",
      className: "bg-secondary-container text-on-secondary-container dark:bg-white/[0.08] dark:text-surface-variant",
    },
    missing: {
      label: "Nicht konfiguriert",
      className: "bg-secondary-container text-on-secondary-container dark:bg-white/[0.08] dark:text-surface-variant",
    },
    planned: { label: "Geplant", className: "bg-surface-variant text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]" },
  };
  const conf = CONF[status];
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 font-label-md text-[10.5px] font-semibold uppercase tracking-wide ${conf.className}`}>
      {conf.label}
    </span>
  );
}
export default function SettingsPage() {
  const meQ = useApi<Me>("/api/me");
  const moduleHealthQ = useApi<SystemHealth>("/api/system/health");
  const moduleSettingsQ = useApi<ModuleSettings | null>("/api/settings/module");
  // Punkt 46b: mandantenscharf, anders als `system/health` — s. Begruendung an der Stripe-Karte.
  const stripeZahlwegQ = useApi<StripeZahlweg>("/api/settings/stripe-zahlweg");
  const { emitContentChanged } = useCopilot();
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const membership = currentMembership(meQ.data);
  const isOwner = membership?.role === "owner";
  const istInhaber = isOwner;
  const telegramQ = useApi<TelegramKanal>(istInhaber ? "/api/agent/telegram/chats" : null);
  const modulZustand: ModulZustand = {
    einstellungen: moduleSettingsQ.data ?? null,
    telegram: telegramQ.data ?? null,
    stripeZahlweg: stripeZahlwegQ.data ?? null,
  };
  const visibleTab = (key: string) => key !== "datenschutz" || isOwner;
  const [tabParams, setTabParams] = useSearchParams();
  const tabParam = tabParams.get("tab");
  const requestedTab: (typeof TABS)[number]["key"] = TABS.some((t) => t.key === tabParam)
    ? (tabParam as (typeof TABS)[number]["key"])
    : "betrieb";
  const tab = visibleTab(requestedTab) ? requestedTab : "betrieb";
  const setTab = (key: (typeof TABS)[number]["key"]) =>
    setTabParams(key === "betrieb" ? {} : { tab: key }, { replace: true });
  return (
    <>
      <PageHeader title="Einstellungen" subtitle="Betrieb, Team und Module verwalten." />
      <div className="mb-8">
        <TabBar
          tabs={TABS.filter((t) => visibleTab(t.key)).map((t) => ({ value: t.key, label: t.label }))}
          value={tab}
          onValueChange={(v) => setTab(v as (typeof TABS)[number]["key"])}
        />
      </div>
      {tab === "betrieb" && (
        <div className="grid grid-cols-1 gap-gutter lg:grid-cols-3">
          <Card className="p-6 lg:col-span-2">
            <h3 className="mb-6 border-b border-outline-variant/30 pb-4 font-headline-sm text-headline-sm text-on-background dark:border-outline/30 dark:text-inverse-on-surface">
              Stammdaten
            </h3>
            {meQ.loading ? (
              <LoadingRows rows={2} />
            ) : membership ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <TenantNameForm
                    initialName={membership.name}
                    onSaved={() => {
                      meQ.reload();
                      emitContentChanged({ resource: "me" });
                    }}
                  />
                  <Field label="Subdomain">
                    <code className="font-mono">{membership.slug}</code>
                  </Field>
                </div>
                <div>
                  <label className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                    Ihre Rolle
                  </label>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 font-label-md text-label-md ${
                      ROLE_PILL[membership.role] ?? ROLE_PILL.staff
                    }`}
                  >
                    {ROLE_LABEL[membership.role] ?? membership.role}
                  </span>
                </div>
              </div>
            ) : (
              <ErrorNote message={meQ.error ?? "Keine Mitgliedschaft gefunden."} />
            )}
          </Card>
          <Card className="flex flex-col p-6 lg:col-span-1">
            <h3 className="mb-6 border-b border-outline-variant/30 pb-4 font-headline-sm text-headline-sm text-on-background dark:border-outline/30 dark:text-inverse-on-surface">
              Konto
            </h3>
            <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-outline-variant bg-surface-container-low p-6 text-center dark:border-outline/40 dark:bg-surface-variant/20">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-fixed text-on-primary-fixed">
                <Icon name="badge" className="text-[28px]" />
              </span>
              <p className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Angemeldet als
              </p>
              <p className="mt-1 break-all font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                {meQ.data?.email ?? meQ.data?.userId ?? "–"}
              </p>
              <Link to="/mein-konto" className="mt-3 font-label-md text-label-md text-primary hover:underline">
                Mein Konto öffnen
              </Link>
            </div>
          </Card>
          <Card className="p-6 lg:col-span-2">
            <h3 className="mb-6 border-b border-outline-variant/30 pb-4 font-headline-sm text-headline-sm text-on-background dark:border-outline/30 dark:text-inverse-on-surface">
              Firmen-Kopf (Kostenübernahme / Rechnung)
            </h3>
            <CompanyHeaderForm />
          </Card>
          <Card className="p-6 lg:col-span-2">
            <EmailFooterForm />
          </Card>
          <Card className="p-6 lg:col-span-2">
            <h3 className="mb-6 border-b border-outline-variant/30 pb-4 font-headline-sm text-headline-sm text-on-background dark:border-outline/30 dark:text-inverse-on-surface">
              Sofortantwort an Gäste
            </h3>
            {/* KONZEPT-PERSON-PERSOENLICH: Ansprechpartner pflegt jede Person selbst unter Team. */}
            <p className="mb-4 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Wer in Kundenmails als Ansprechpartner genannt wird, legst du jetzt unter{" "}
              <Link to="/settings?tab=team" className="text-primary hover:underline">Team</Link> fest — mit Foto, Rolle und eigener Signatur.
            </p>
            <SofortantwortSchalter />
          </Card>
          <Card className="p-6 lg:col-span-2">
            <h3 className="mb-6 border-b border-outline-variant/30 pb-4 font-headline-sm text-headline-sm text-on-background dark:border-outline/30 dark:text-inverse-on-surface">
              Begrüßungsvideo
            </h3>
            <BegruessungsvideoForm />
          </Card>
          <Card className="p-6 lg:col-span-2">
            <MailFreigabeForm />
          </Card>
          <SchliesstageKarte />
        </div>
      )}
      {tab === "team" && (
        <TeamTab
          meUserId={meQ.data?.userId}
          isOwner={isOwner}
          istInhaber={istInhaber}
        />
      )}
      {tab === "orte" && <LocationsTab />}
      {tab === "module" && (
        <div className="space-y-6">
          {/* Punkt 124: KI-Verbrauch zog von Business Health hierher (Entscheidung 5). */}
          <KiVerbrauchKarte />
          {istInhaber && (
            <Card className="p-5">
              <h3 className="mb-3 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Integrations-Status
              </h3>
              {moduleHealthQ.loading ? (
                <LoadingRows rows={1} />
              ) : (
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
                  {(["ai", "email", "esign", "invoice", "payments", "places", "telegram"] as const).map((key) => {
                    const active = moduleHealthQ.data?.integrations?.[key] === true;
                    return (
                      <div key={key} className="flex items-center gap-2 rounded-lg border border-outline-variant/40 px-3 py-2.5 dark:border-outline/30">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${active ? "bg-tertiary" : "bg-secondary-fixed-dim"}`} />
                        <span className="min-w-0 truncate font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                          {INTEGRATION_LABEL[key] ?? key}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}
          <div className="grid grid-cols-1 gap-gutter sm:grid-cols-2 lg:grid-cols-3">
            {MODULES.map((m) => {
              const isActive = m.aktivWenn ? m.aktivWenn(modulZustand) : m.tenantFlag ? modulZustand.einstellungen?.[m.tenantFlag] === true : m.healthKey ? moduleHealthQ.data?.integrations?.[m.healthKey] === true : false;
              const chipStatus: ModuleChipStatus = m.planned ? "planned" : isActive ? "active" : m.tenantFlag || m.aktivWenn ? "off" : "missing";
              const statusText =
                m.statusTextVon?.(modulZustand) ??
                (m.planned || isActive
                  ? m.statusText
                  : chipStatus === "off"
                    ? (m.offText ?? m.missingText ?? "Ausgeschaltet")
                    : (m.missingText ?? "Nicht konfiguriert"));
              const isBeta = m.name === "KI-Assistent";
              const isExpanded = expandedModule === m.name;
              return (
                <Card key={m.name} className="p-6 transition-shadow hover:shadow-md">
                  <div className="mb-4 flex items-start justify-between gap-2">
                    <span className={`flex h-12 w-12 items-center justify-center rounded-lg ${TONE_CLASSES[m.tone]}`}>
                      <Icon name={m.icon} className="text-[22px]" />
                    </span>
                    <ModuleStatusChip status={chipStatus} />
                  </div>
                  <div className="mb-2 flex items-center gap-2">
                    <h4 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">{m.name}</h4>
                    {isBeta && (
                      <span className="rounded-full bg-[#F0EAE0] px-1.5 py-0.5 font-label-md text-[9.5px] font-semibold uppercase tracking-wider text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                        Beta
                      </span>
                    )}
                  </div>
                  <p className="mb-4 h-10 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {m.desc}
                  </p>
                  <div className="flex items-center gap-2 border-t border-outline-variant/30 pt-2.5 dark:border-outline/30">
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${isActive ? "bg-tertiary" : "bg-secondary-fixed-dim"}`} />
                    <span className="min-w-0 flex-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">{statusText}</span>
                    <button
                      type="button"
                      className="shrink-0 font-label-md text-[11px] font-semibold text-primary hover:underline"
                      onClick={() => setExpandedModule(isExpanded ? null : m.name)}
                    >
                      {isExpanded ? "Schließen" : "Konfigurieren"}
                    </button>
                  </div>
                  <ModulGesundheit zeilen={gesundheitsZeilen(m.configureKey, moduleHealthQ.data ?? null)} />
                  {isExpanded && (
                    <div className="mt-3 rounded-lg border border-outline-variant/40 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10">
                      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{m.configureHint}</p>
                      {m.configureKey === "esign" && (
                        <EsignConfig canEdit={istInhaber} onSaved={() => moduleHealthQ.reload()} />
                      )}
                      {m.configureKey === "lexoffice" && (
                        <LexofficeConfig
                          canEdit={istInhaber}
                          data={moduleSettingsQ.data ?? null}
                          onSaved={() => {
                            moduleSettingsQ.reload();
                            moduleHealthQ.reload();
                          }}
                        />
                      )}
                      {m.configureKey === "ki" && <AgentKachel />}
                      {m.configureKey === "feiertage" && (
                        <RechercheModulConfig
                          canEdit={istInhaber}
                          feld="feiertageEnabled"
                          data={moduleSettingsQ.data ?? null}
                          onSaved={() => moduleSettingsQ.reload()}
                          extra={(aktiv, speichere) =>
                            aktiv ? (
                              <BundeslandFeld
                                canEdit={istInhaber}
                                wert={moduleSettingsQ.data?.bundesland ?? ""}
                                onChange={(code) => speichere({ feiertageEnabled: true, bundesland: code || null })}
                              />
                            ) : null
                          }
                        />
                      )}
                      {m.configureKey === "wetter" && (
                        <RechercheModulConfig canEdit={istInhaber} feld="wetterEnabled" data={moduleSettingsQ.data ?? null} onSaved={() => moduleSettingsQ.reload()} />
                      )}
                      {m.configureKey === "handelsregister" && (
                        <RechercheModulConfig canEdit={istInhaber} feld="handelsregisterEnabled" data={moduleSettingsQ.data ?? null} onSaved={() => moduleSettingsQ.reload()} />
                      )}
                      {m.configureKey === "places" && (
                        <RechercheModulConfig
                          canEdit={istInhaber}
                          feld="googlePlacesEnabled"
                          data={moduleSettingsQ.data ?? null}
                          onSaved={() => moduleSettingsQ.reload()}
                          gesperrt={moduleHealthQ.data?.integrations?.places !== true}
                          gesperrtHinweis="Für diesen Betrieb ist noch kein Google-Places-Zugang eingerichtet — der Schalter bliebe wirkungslos."
                        />
                      )}
                      {m.configureKey === "ustid" && (
                        <UstIdConfig canEdit={istInhaber} data={moduleSettingsQ.data ?? null} onSaved={() => moduleSettingsQ.reload()} />
                      )}
                      {m.configureKey === "telegram" && (
                        <TelegramConfig
                          canEdit={istInhaber}
                          data={telegramQ.data ?? null}
                          loading={telegramQ.loading}
                          onChanged={() => telegramQ.reload()}
                        />
                      )}
                      {m.configureKey === "oeffnungszeiten" && (
                        <OeffnungszeitenConfig
                          canEdit={istInhaber}
                          data={moduleSettingsQ.data ?? null}
                          zugangVorhanden={moduleHealthQ.data?.integrations?.places === true}
                          onSaved={() => moduleSettingsQ.reload()}
                        />
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
            <a
              href="mailto:info@monot.com?subject=Modul-Vorschlag%20MAESTRO"
              className="flex flex-col items-center justify-center gap-2 rounded-[13px] border-[1.5px] border-dashed border-outline-variant p-6 text-center text-secondary transition-colors hover:border-primary hover:text-primary dark:border-outline/40 dark:text-secondary-fixed-dim"
            >
              <span className="flex h-[34px] w-[34px] items-center justify-center rounded-lg bg-surface-variant dark:bg-white/[0.06]">
                <Icon name="add" className="text-[18px]" />
              </span>
              <span className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Modul vorschlagen</span>
              <span className="font-body-sm text-[11px] leading-relaxed">
                Welche Integration fehlt dir?
                <br />
                Sag es uns.
              </span>
            </a>
          </div>
          {istInhaber && <SystemStatusSection />}
          {istInhaber && <KiKostenSection />}
        </div>
      )}
      {tab === "branding" && (
        <div className="grid grid-cols-1 gap-gutter lg:grid-cols-3">
          <Card className="p-6 lg:col-span-2">
            <h3 className="mb-6 border-b border-outline-variant/30 pb-4 font-headline-sm text-headline-sm text-on-background dark:border-outline/30 dark:text-inverse-on-surface">
              Marke
            </h3>
            <div className="space-y-6">
              <MarkeForm />
              <BrandingLogo />
              <div className="border-t border-outline-variant/30 pt-6 dark:border-outline/30">
                <OeffentlicheAngebotsseiteForm />
              </div>
              <div className="border-t border-outline-variant/30 pt-6 dark:border-outline/30">
                <AngebotsseitenInhalteForm />
              </div>
            </div>
          </Card>
          <Card className="p-6 lg:col-span-3">
            <h3 className="mb-6 border-b border-outline-variant/30 pb-4 font-headline-sm text-headline-sm text-on-background dark:border-outline/30 dark:text-inverse-on-surface">
              E-Mail-Design
            </h3>
            <EmailVorlageForm />
          </Card>
        </div>
      )}
      {tab === "widgets" && <WidgetsTab />}
      {tab === "eingaenge" && (
        <div className="space-y-6">
          <EingaengeTab canEdit={istInhaber} />
          <InterneAbsenderKarte canEdit={istInhaber} />
        </div>
      )}
      {tab === "zahlungen" && (
        <div className="space-y-8">
          {/* Lernen S4: die Rundungsregel der Preis-Engine (ui/PreisRundungKarte.tsx). */}
          <PreisRundungKarte canEdit={istInhaber} />
          <PaymentTermsForm />
        </div>
      )}
      {tab === "benachrichtigungen" && <BenachrichtigungenTab canEdit={istInhaber} />}
      {tab === "absenderdomain" && <AbsenderdomainTab canEdit={istInhaber} />}
      {tab === "postfaecher" && <PostfaecherTab canEdit={istInhaber} />}
      {tab === "datenschutz" && isOwner && (
        <div className="space-y-6">
          <RetentionTab />
          <TransparenzBausteinKarte />
          <VersandTransparenzKarte />
          <AngebotsAbrufKarte />
          <AvvSupportZugriffKarte betrieb={membership?.name ?? ""} />
        </div>
      )}
    </>
  );
}
interface SystemHealth {
  ok: boolean;
  integrations: Record<string, boolean>;
  errorsLast24h: number;
  plattformMeldungen24h?: number;
  scheduler?: { lastSweepAt: string | null; stale: boolean; stillAbStunden?: number };
  recherche?: RechercheGesundheit;
}
interface RechercheGesundheit {
  handelsregister: {
    stundeVerbraucht: number;
    stundeDeckel: number;
    abgeschlossen24h: number;
    fehler24h: number;
    nichtGefunden24h: number;
    abgewiesen24h: number;
    haengendePending: number;
  };
  places: AbrufBudget;
  wetter: { letzterErfolgAt: string | null };
}
function ModulGesundheit({ zeilen }: { zeilen: string[] }) {
  if (zeilen.length === 0) return null;
  return (
    <ul className="mt-2.5 space-y-1 border-t border-outline-variant/30 pt-2.5 dark:border-outline/30">
      {zeilen.map((z) => (
        <li key={z} className="font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
          {z}
        </li>
      ))}
    </ul>
  );
}
function gesundheitsZeilen(schluessel: ModuleConfigKey | undefined, health: SystemHealth | null): string[] {
  const r = health?.recherche;
  if (!r || !schluessel) return [];
  if (schluessel === "handelsregister") {
    return [
      deckelAuslastung({ verbraucht: r.handelsregister.stundeVerbraucht, deckel: r.handelsregister.stundeDeckel }),
      abrufBilanzZeile({
        abgeschlossen: r.handelsregister.abgeschlossen24h,
        fehler: r.handelsregister.fehler24h,
        nichtGefunden: r.handelsregister.nichtGefunden24h,
        abgewiesen: r.handelsregister.abgewiesen24h,
      }),
      haengendeZeile(r.handelsregister.haengendePending),
    ];
  }
  if (schluessel === "places") {
    const b = budgetAnzeige(r.places);
    return b ? [b.satz] : [];
  }
  if (schluessel === "wetter") {
    return [letzterErfolgZeile(r.wetter.letzterErfolgAt ? new Date(r.wetter.letzterErfolgAt) : null, new Date())];
  }
  return [];
}
interface SystemError {
  id: string;
  path: string | null;
  method: string | null;
  statusCode: number | null;
  message: string | null;
  createdAt: string;
}
const INTEGRATION_LABEL: Record<string, string> = {
  ai: "KI (Angebot / Anschreiben / Import)",
  email: "E-Mail-Versand",
  esign: "E-Signatur (Kostenübernahme)",
  invoice: "LexOffice-Rechnung",
  payments: "Stripe-Zahlungen",
  telegram: "Telegram-Benachrichtigungen",
  places: "Google Places (Kunden-Lookup)",
};
interface Invite {
  id: string;
  email: string;
  role: "owner" | "staff";
  status: "pending" | "accepted" | "revoked";
  createdAt: string;
  expiresAt: string;
  mailVersendetAm?: string | null;
  letzterVersandfehler?: string | null;
  letzterVersandversuchAm?: string | null;
}
const EMPTY_LOCATION_FORM = {
  name: "",
  addressStreet: "",
  addressZip: "",
  addressCity: "",
  capacitySeated: "",
  capacityStanding: "",
  notes: "",
};
function LocationsTab() {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const locationsQ = useApi<Location[]>("/api/locations");
  useContentRefetch("location", locationsQ.reload);
  const locations = locationsQ.data ?? [];
  const [form, setForm] = useState(EMPTY_LOCATION_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  function startEdit(loc: Location) {
    setEditingId(loc.id);
    setForm({
      name: loc.name,
      addressStreet: loc.addressStreet ?? "",
      addressZip: loc.addressZip ?? "",
      addressCity: loc.addressCity ?? "",
      capacitySeated: loc.capacitySeated != null ? String(loc.capacitySeated) : "",
      capacityStanding: loc.capacityStanding != null ? String(loc.capacityStanding) : "",
      notes: loc.notes ?? "",
    });
  }
  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_LOCATION_FORM);
  }
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      name: form.name,
      addressStreet: form.addressStreet || undefined,
      addressZip: form.addressZip || undefined,
      addressCity: form.addressCity || undefined,
      capacitySeated: form.capacitySeated ? Number(form.capacitySeated) : undefined,
      capacityStanding: form.capacityStanding ? Number(form.capacityStanding) : undefined,
      notes: form.notes || undefined,
    };
    try {
      if (editingId) {
        await apiMutate(`/api/locations/${editingId}`, "PATCH", payload);
      } else {
        await apiMutate("/api/locations", "POST", payload);
      }
      cancelEdit();
      locationsQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function toggleActive(loc: Location) {
    setRowBusy(loc.id);
    setError(null);
    try {
      await apiMutate(`/api/locations/${loc.id}`, "PATCH", { isActive: !loc.isActive });
      locationsQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }
  async function remove(loc: Location) {
    if (
      !(await bestaetigen({
        titel: `„${loc.name}“ wirklich löschen?`,
        text: "Verknüpfte Vorgänge verlieren nur die Ortszuordnung.",
        bestaetigen: "Löschen",
        gefahr: true,
      }))
    )
      return;
    setRowBusy(loc.id);
    setError(null);
    try {
      await apiMutate(`/api/locations/${loc.id}`, "DELETE");
      if (editingId === loc.id) cancelEdit();
      locationsQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }
  return (
    <div className="space-y-6">
      {bestaetigungsDialog}
      {error && <ErrorNote message={error} />}
      <Card className="p-6">
        <h3 className="mb-4 font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
          {editingId ? "Ort bearbeiten" : "Ort anlegen"}
        </h3>
        <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={(e) => void submit(e)}>
          <label className="md:col-span-2">
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Name
            </span>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="z. B. Festsaal, Terrasse, Weinkeller"
            />
          </label>
          <label>
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Straße
            </span>
            <Input
              value={form.addressStreet}
              onChange={(e) => setForm((f) => ({ ...f, addressStreet: e.target.value }))}
            />
          </label>
          <div className="grid grid-cols-2 gap-4">
            {/* min-w-0: s. VeranstaltungsDatenDrawer.tsx (identisches PLZ/Ort-Muster). */}
            <label className="min-w-0">
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                PLZ
              </span>
              <Input
                value={form.addressZip}
                onChange={(e) => setForm((f) => ({ ...f, addressZip: e.target.value }))}
              />
            </label>
            <label className="min-w-0">
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Ort
              </span>
              <Input
                value={form.addressCity}
                onChange={(e) => setForm((f) => ({ ...f, addressCity: e.target.value }))}
              />
            </label>
          </div>
          <label>
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Kapazität sitzend
            </span>
            <Input
              type="number"
              min={0}
              value={form.capacitySeated}
              onChange={(e) => setForm((f) => ({ ...f, capacitySeated: e.target.value }))}
            />
          </label>
          <label>
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Kapazität stehend
            </span>
            <Input
              type="number"
              min={0}
              value={form.capacityStanding}
              onChange={(e) => setForm((f) => ({ ...f, capacityStanding: e.target.value }))}
            />
          </label>
          <label className="md:col-span-2">
            <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              Notizen
            </span>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </label>
          <div className="flex items-center gap-3 md:col-span-2">
            <Button type="submit" className="!py-2" disabled={busy || !form.name}>
              {busy ? "Speichert …" : editingId ? "Speichern" : "Anlegen"}
            </Button>
            {editingId && (
              <button type="button" className="font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim" onClick={cancelEdit}>
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </Card>
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-outline-variant/30 p-6 dark:border-outline/30">
          <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Orte</h3>
          {!locationsQ.loading && !locationsQ.error && locations.length > 0 && (
            <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              {locations.length} {locations.length === 1 ? "Ort" : "Orte"}
            </span>
          )}
        </div>
        {locationsQ.loading ? (
          <LoadingRows rows={3} />
        ) : locationsQ.error ? (
          <ErrorNote message={locationsQ.error} />
        ) : locations.length === 0 ? (
          <EmptyState icon="place" title="Noch keine Orte angelegt" />
        ) : (
          <div className="overflow-x-auto">
            <table className="handy-karten w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="bg-surface-container-low dark:bg-surface-variant/20">
                  <th className="table-header">Name</th>
                  <th className="table-header">Adresse</th>
                  <th className="table-header">Kapazität</th>
                  <th className="table-header">Status</th>
                  <th className="table-header" />
                </tr>
              </thead>
              <tbody>
                {locations.map((loc) => (
                  <tr key={loc.id} className="transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20">
                    <td className="border-t border-outline-variant/20 px-4 py-4 font-body-md text-body-md font-semibold text-on-background dark:border-outline/20 dark:text-inverse-on-surface">
                      {loc.name}
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 font-body-md text-body-md text-on-surface-variant dark:border-outline/20 dark:text-secondary-fixed-dim">
                      {[loc.addressStreet, [loc.addressZip, loc.addressCity].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "–"}
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 font-body-md text-body-md text-on-surface-variant dark:border-outline/20 dark:text-secondary-fixed-dim">
                      {loc.capacitySeated != null || loc.capacityStanding != null
                        ? [
                            loc.capacitySeated != null ? `${loc.capacitySeated} sitzend` : null,
                            loc.capacityStanding != null ? `${loc.capacityStanding} stehend` : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")
                        : "–"}
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4">
                      <button
                        type="button"
                        disabled={rowBusy === loc.id}
                        onClick={() => void toggleActive(loc)}
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold disabled:opacity-50 ${
                          loc.isActive
                            ? "bg-tertiary-container text-tertiary"
                            : "bg-secondary-container text-on-secondary-container dark:bg-white/[0.08] dark:text-surface-variant"
                        }`}
                      >
                        {loc.isActive ? "Aktiv" : "Inaktiv"}
                      </button>
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 text-right dark:border-outline/20">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          className="font-label-md text-label-md text-primary dark:text-primary-fixed-dim"
                          onClick={() => startEdit(loc)}
                        >
                          Bearbeiten
                        </button>
                        <button
                          type="button"
                          className="font-label-md text-label-md text-error disabled:opacity-50"
                          disabled={rowBusy === loc.id}
                          onClick={() => void remove(loc)}
                        >
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
function memberDisplayName(m: Member): string {
  return m.displayName ?? m.email ?? `Unbekannt (…${m.stackUserId.slice(-6)})`;
}
/** Listenstandard (Punkt 95, Brief § 9): Team-Liste — Mitglied, Rolle, Beigetreten sortierbar,
 *  die Aktionsspalte nicht. „Zuletzt aktiv“ aus dem Brief gibt es im Datenmodell nicht und wird
 *  bewusst nicht erfunden. Rolle sortiert fachlich: Inhaber < Admin < Mitarbeiter. */
const TEAM_SPALTEN: SortSpalte<Member>[] = [
  { key: "mitglied", label: "Mitglied", type: "text", accessor: memberDisplayName },
  { key: "rolle", label: "Rolle", type: "status", accessor: (m) => m.role, statusOrder: { owner: 0, staff: 1 } },
  { key: "beigetreten", label: "Beigetreten", type: "date", accessor: (m) => m.createdAt },
];
const TEAM_ZELLE = "border-t border-[#F6F0E4] px-[7px] py-3 align-middle first:pl-[18px] last:pr-[18px] dark:border-white/[0.06]";
type SitzungsVersand = { gelungen: boolean; um: number };
type Versandstand = Record<string, SitzungsVersand>;
function versandStand(i: Invite, sitzung: SitzungsVersand | undefined): { text: string; fehler: boolean } {
  const versendet = i.mailVersendetAm ? `Verschickt am ${formatDateTime(i.mailVersendetAm)}` : null;
  const grund = i.letzterVersandfehler;
  const dbVersuch = i.letzterVersandversuchAm ? Date.parse(i.letzterVersandversuchAm) : null;
  const sitzungAktuell = sitzung !== undefined && !(dbVersuch !== null && dbVersuch > sitzung.um);
  if (sitzungAktuell && sitzung.gelungen) return { text: "E-Mail versendet", fehler: false };
  if (sitzungAktuell && !sitzung.gelungen) {
    let text = "E-Mail NICHT versendet";
    if (grund) text += `: ${grund}`;
    if (i.mailVersendetAm) text += ` · früher verschickt am ${formatDateTime(i.mailVersendetAm)}`;
    return { text, fehler: true };
  }
  const versuchAm = i.letzterVersandversuchAm ? ` am ${formatDateTime(i.letzterVersandversuchAm)}` : "";
  if (versendet && grund) return { text: `${versendet} · letzter Versuch fehlgeschlagen${versuchAm}: ${grund}`, fehler: true };
  if (grund) return { text: `Versand fehlgeschlagen${versuchAm}: ${grund}`, fehler: true };
  if (versendet) return { text: versendet, fehler: false };
  return { text: "Noch kein Versandversuch vermerkt", fehler: false };
}
export function TeamTab({
  meUserId,
  isOwner,
  istInhaber,
}: {
  meUserId?: string;
  isOwner: boolean;
  istInhaber: boolean;
}) {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const membersQ = useApi<Member[]>("/api/members");
  const invitesQ = useApi<Invite[]>(istInhaber ? "/api/invites" : null);
  useContentRefetch(["member", "invite"], () => {
    membersQ.reload();
    invitesQ.reload();
  });
  const members = membersQ.data ?? [];
  const sort = useListSort("team", TEAM_SPALTEN);
  const pendingInvites = (invitesQ.data ?? []).filter((i) => i.status === "pending");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"owner" | "staff">("staff");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [versand, setVersand] = useState<Versandstand>({});
  const [versandHinweis, setVersandHinweis] = useState<string | null>(null);
  // KONZEPT-PERSON-PERSOENLICH: rechts „Person bearbeiten“, vorgewählt die eigene Person.
  const [auswahlId, setAuswahlId] = useState<string | null>(null);
  const auswahl = members.find((m) => m.stackUserId === (auswahlId ?? meUserId)) ?? null;
  async function invite(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setVersandHinweis(null);
    try {
      const angelegt = await apiMutate<{ id: string; email: string; mailSent: boolean; mailUnconfigured?: boolean }>("/api/invites", "POST", { email, role });
      if (!angelegt.mailUnconfigured) setVersand((v) => ({ ...v, [angelegt.id]: { gelungen: angelegt.mailSent, um: Date.now() } }));
      setVersandHinweis(
        angelegt.mailSent
          ? `Einladung angelegt. Die E-Mail an ${angelegt.email} wurde versendet.`
          : angelegt.mailUnconfigured
            ? `Einladung angelegt, aber für diesen Betrieb ist kein E-Mail-Versand eingerichtet — es wurde kein Versand versucht. „Erneut senden" hilft erst, wenn der Versand eingerichtet ist.`
            : `Einladung angelegt, aber die E-Mail an ${angelegt.email} ging NICHT hinaus. Bitte unten erneut senden — ohne den Link kommt niemand herein.`,
      );
      setEmail("");
      setRole("staff");
      invitesQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function erneutSenden(i: Invite) {
    setRowBusy(i.id);
    setError(null);
    setVersandHinweis(null);
    try {
      const ergebnis = await apiMutate<{ mailSent: boolean }>(`/api/invites/${i.id}/erneut-senden`, "POST");
      setVersand((v) => ({ ...v, [i.id]: { gelungen: ergebnis.mailSent, um: Date.now() } }));
      setVersandHinweis(
        ergebnis.mailSent
          ? `Die E-Mail an ${i.email} wurde erneut versendet.`
          : `Die E-Mail an ${i.email} ging erneut NICHT hinaus. Der Fehler ist im Systemstatus vermerkt.`,
      );
      invitesQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }
  async function revoke(id: string) {
    setRowBusy(id);
    setError(null);
    try {
      await apiMutate(`/api/invites/${id}`, "DELETE");
      invitesQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }
  async function changeRole(stackUserId: string, newRole: string, oldRole: string) {
    const label = (r: string) => ROLE_LABEL[r] ?? r;
    const ok = await bestaetigen({
      titel: `Rolle wirklich von „${label(oldRole)}“ zu „${label(newRole)}“ ändern?`,
      text: newRole === "owner" ? "Achtung: Inhaber haben volle Kontrolle über den Betrieb (Team, Daten, Löschung)." : undefined,
      bestaetigen: "Rolle ändern",
    });
    if (!ok) {
      membersQ.reload();
      return;
    }
    setRowBusy(stackUserId);
    setError(null);
    try {
      await apiMutate(`/api/members/${stackUserId}`, "PATCH", { role: newRole });
      membersQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }
  async function remove(stackUserId: string) {
    if (!(await bestaetigen({ titel: "Mitglied wirklich entfernen?", bestaetigen: "Entfernen", gefahr: true }))) return;
    setRowBusy(stackUserId);
    setError(null);
    try {
      await apiMutate(`/api/members/${stackUserId}`, "DELETE");
      membersQ.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRowBusy(null);
    }
  }
  return (
    <div className="space-y-6">
      {bestaetigungsDialog}
      {error && <ErrorNote message={error} />}
      {istInhaber && (
        <Card className="p-6">
          <h3 className="mb-4 font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
            Mitglied einladen
          </h3>
          <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => void invite(e)}>
            <label className="min-w-[220px] flex-1">
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                E-Mail
              </span>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firma.de" />
            </label>
            <label>
              <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                Rolle
              </span>
              <Select
                ariaLabel="Rolle"
                value={role}
                onValueChange={(v) => setRole(v as typeof role)}
                options={[
                  { value: "staff", label: "Mitarbeiter" },
                  ...(isOwner ? [{ value: "owner", label: "Inhaber" }] : []),
                ]}
              />
            </label>
            <Button type="submit" className="!py-2" disabled={busy || !email}>
              {busy ? "Sendet …" : "Einladen"}
            </Button>
          </form>
          {versandHinweis && (
            <p
              role="status"
              className={`mt-4 font-body-sm text-body-sm ${
                versandHinweis.includes("NICHT") ? "text-error" : "text-secondary dark:text-secondary-fixed-dim"
              }`}
            >
              {versandHinweis}
            </p>
          )}
          {pendingInvites.length > 0 && (
            <div className="mt-6 space-y-2">
              {pendingInvites.map((i) => (
                <div key={i.id} className="flex items-center justify-between gap-3 rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
                  <div>
                    <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{i.email}</p>
                    <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                      Eingeladen als {ROLE_LABEL[i.role] ?? i.role} · offen bis {formatDate(i.expiresAt)}
                    </p>
                    {(() => {
                      const stand = versandStand(i, versand[i.id]);
                      return (
                        <p
                          className={`font-body-sm text-body-sm ${
                            stand.fehler ? "text-error" : "text-secondary dark:text-secondary-fixed-dim"
                          }`}
                        >
                          {stand.text}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <button
                      type="button"
                      className="font-label-md text-label-md text-primary disabled:opacity-50 dark:text-primary-fixed-dim"
                      disabled={rowBusy === i.id}
                      onClick={() => void erneutSenden(i)}
                    >
                      Erneut senden
                    </button>
                    <button
                      type="button"
                      className="font-label-md text-label-md text-error disabled:opacity-50"
                      disabled={rowBusy === i.id}
                      onClick={() => void revoke(i.id)}
                    >
                      Widerrufen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-outline-variant/30 p-6 dark:border-outline/30">
          <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">
            Teammitglieder
          </h3>
          {!membersQ.loading && !membersQ.error && members.length > 0 && (
            <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              {members.length} {members.length === 1 ? "Mitglied" : "Mitglieder"}
            </span>
          )}
        </div>
        {membersQ.error ? (
          <ErrorNote zentriert title="Teammitglieder konnten nicht geladen werden" message={membersQ.error} onRetry={membersQ.reload} />
        ) : !membersQ.loading && members.length === 0 ? (
          <EmptyState icon="group" title="Keine Teammitglieder sichtbar" />
        ) : (
          <>
          {/* Listenstandard (Brief § 2.4): eigener Scrollbereich für den sticky Kopf. */}
          <div className="max-h-[max(20rem,calc(100vh-19rem))] overflow-auto" aria-busy={membersQ.loading}>
            <table className="handy-karten w-full min-w-[560px] border-collapse text-left text-[13px]">
              <thead>
                <tr>
                  {TEAM_SPALTEN.map((sp) => (
                    <SortierKopf
                      key={sp.key}
                      label={sp.label}
                      aktiv={sort.sortKey === sp.key ? sort.dir : null}
                      onClick={() => sort.toggle(sp.key)}
                    />
                  ))}
                  {istInhaber && (
                    <th className={LISTEN_KOPF_ZELLE}>
                      <span className="sr-only">Aktionen</span>
                    </th>
                  )}
                </tr>
              </thead>
              {membersQ.loading ? (
                <LoadingRows rows={3} spalten={istInhaber ? ["text", "pill", "text", "leer"] : ["text", "pill", "text"]} />
              ) : (
              <tbody>
                {sort.sortiere(members).map((m) => (
                  <tr
                    key={m.stackUserId}
                    className="transition-colors hover:bg-[#FBF6EC] dark:hover:bg-white/[0.03]"
                  >
                    <td className={TEAM_ZELLE}>
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-fixed text-on-primary-fixed">
                          {m.fotoUrl ? <img src={m.fotoUrl} alt="" className="h-full w-full object-cover" /> : <Icon name="person" className="text-[20px]" />}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                            {memberDisplayName(m)}
                            {m.stackUserId === meUserId ? " (Sie)" : ""}
                          </p>
                          {m.displayName && m.email && (
                            <p className="truncate font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{m.email}</p>
                          )}
                          {m.mailPosition != null && (
                            <p className="font-body-sm text-[11px] text-primary dark:text-primary-fixed-dim">
                              In Kundenmails: Position {m.mailPosition}
                            </p>
                          )}
                          {(istInhaber || m.stackUserId === meUserId) && (
                            <button
                              type="button"
                              className="font-label-md text-label-md text-primary hover:underline dark:text-primary-fixed-dim"
                              aria-label={`${memberDisplayName(m)} bearbeiten`}
                              onClick={() => setAuswahlId(m.stackUserId)}
                            >
                              Bearbeiten
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className={TEAM_ZELLE}>
                      {istInhaber && (isOwner || m.role !== "owner") ? (
                        <Select
                          value={m.role}
                          disabled={rowBusy === m.stackUserId}
                          ariaLabel={`Rolle von ${memberDisplayName(m)}`}
                          onValueChange={(v) => void changeRole(m.stackUserId, v, m.role)}
                          className="!w-auto !py-1"
                          options={[
                            { value: "staff", label: "Mitarbeiter" },
                            ...(isOwner ? [{ value: "owner", label: "Inhaber" }] : []),
                          ]}
                        />
                      ) : (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            ROLE_PILL[m.role] ?? ROLE_PILL.staff
                          }`}
                        >
                          {ROLE_LABEL[m.role] ?? m.role}
                        </span>
                      )}
                    </td>
                    <td className={`${TEAM_ZELLE} whitespace-nowrap tabular-nums text-on-surface-variant dark:text-secondary-fixed-dim`}>
                      {m.createdAt ? formatDate(m.createdAt) : <KeineAngabe />}
                    </td>
                    {istInhaber && (
                      <td className={`${TEAM_ZELLE} text-right`}>
                        <div className="flex justify-end gap-4">
                          {(isOwner || m.role !== "owner") && (
                            <button
                              type="button"
                              className="font-label-md text-label-md text-error disabled:opacity-50"
                              disabled={rowBusy === m.stackUserId}
                              onClick={() => void remove(m.stackUserId)}
                            >
                              Entfernen
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              )}
            </table>
          </div>
          <ListFooter
            anzahl={members.length}
            gesamt={members.length}
            einheit={members.length === 1 ? "Mitglied" : "Mitglieder"}
            sortiertNach={sort.spalte?.label ?? null}
            richtung={sort.dir}
            onReset={sort.reset}
          />
          </>
        )}
      </Card>
      {auswahl && (
        <PersonBearbeiten
          key={auswahl.stackUserId}
          member={auswahl}
          canEdit={istInhaber || auswahl.stackUserId === meUserId}
          selbst={auswahl.stackUserId === meUserId}
          onSaved={membersQ.reload}
        />
      )}
      </div>
      {istInhaber && <AnsprechpartnerListe members={members} onSaved={membersQ.reload} />}
    </div>
  );
}
/**
 * Punkt 146 (sql/677): „In Kundenmails genannt“ — geordnete Liste, wer im Mailkopf steht
 * (höchstens 4, ab 3 kleinere Fotos; das rendert der Server). Position 1 ist bei „Hauptperson +
 * Vertretung“ die Hauptperson. Speichern: PUT /api/members/ansprechpartner-reihenfolge
 * (tenant_users.id) und, falls geändert, die Darstellung über payment-terms.
 */
export function AnsprechpartnerListe({ members, onSaved }: { members: Member[]; onSaved: () => void }) {
  const { data, ready, reload } = usePaymentTermsData();
  const waehlbar = members.filter((m) => m.id);
  const basisIds = waehlbar
    .filter((m) => m.mailPosition != null)
    .sort((a, b) => (a.mailPosition ?? 0) - (b.mailPosition ?? 0))
    .map((m) => m.id as string);
  const basisKey = basisIds.join(",");
  const basisDarst = data?.ansprechpartnerDarstellung ?? "haupt_vertretung";
  const [ids, setIds] = useState<string[]>(basisIds);
  const [darst, setDarst] = useState<"haupt_vertretung" | "gleichberechtigt">(basisDarst);
  const [ansage, setAnsage] = useState("");
  const [ziehe, setZiehe] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setIds(basisKey ? basisKey.split(",") : []), [basisKey]);
  useEffect(() => setDarst(basisDarst), [basisDarst, ready]);
  const nach = new Map(waehlbar.map((m) => [m.id as string, m]));
  const name = (id: string) => {
    const m = nach.get(id);
    return m ? memberDisplayName(m) : "Person";
  };
  const genannt = ids.filter((id) => nach.has(id));
  const offen = waehlbar.filter((m) => !genannt.includes(m.id as string));
  function verschieben(von: number, zu: number) {
    if (zu < 0 || zu >= genannt.length || von === zu) return;
    const neu = [...genannt];
    const [id] = neu.splice(von, 1);
    neu.splice(zu, 0, id!);
    setIds(neu);
    setSaved(false);
    setAnsage(`${name(id!)} steht jetzt an Position ${zu + 1}.`);
  }
  function entfernen(id: string) {
    setIds(genannt.filter((x) => x !== id));
    setSaved(false);
    setAnsage(`${name(id)} wird nicht mehr genannt.`);
  }
  function ergaenzen(id: string) {
    setIds([...genannt, id]);
    setSaved(false);
    setAnsage(`${name(id)} an Position ${genannt.length + 1} ergänzt.`);
  }
  function badge(i: number) {
    if (darst === "gleichberechtigt") return "gleichberechtigt";
    return i === 0 ? "Hauptperson" : "Vertretung";
  }
  async function speichern() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/members/ansprechpartner-reihenfolge", "PUT", { ids: genannt });
      if (darst !== basisDarst) {
        await apiMutate("/api/settings/payment-terms", "PUT", { ansprechpartnerDarstellung: darst });
        reload();
      }
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="mt-6 p-6" id="in-kundenmails-genannt" data-testid="ansprechpartner-liste">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h2 className="font-h3 text-h3 text-on-background dark:text-inverse-on-surface">In Kundenmails genannt</h2>
        <span className="rounded-full bg-primary-fixed px-2.5 py-0.5 font-label-md text-label-md text-on-primary-fixed" data-testid="ansprechpartner-zaehler">
          {genannt.length} von {waehlbar.length} genannt
        </span>
      </div>
      <p className="mb-4 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Diese Personen stehen in dieser Reihenfolge im Kopf der Kundenmails, höchstens 4; ab 3 Personen werden die Fotos kleiner. Absender ist immer, wer die Mail abschickt.
      </p>
      <fieldset className="mb-4 flex flex-wrap gap-4">
        <legend className="sr-only">Darstellung</legend>
        <label className="flex items-center gap-2 font-body-sm text-body-sm">
          <Radio name="ansprechpartner-darstellung" checked={darst === "haupt_vertretung"} onChange={() => { setDarst("haupt_vertretung"); setSaved(false); }} /> Eine Hauptperson + Vertretung
        </label>
        <label className="flex items-center gap-2 font-body-sm text-body-sm">
          <Radio name="ansprechpartner-darstellung" checked={darst === "gleichberechtigt"} onChange={() => { setDarst("gleichberechtigt"); setSaved(false); }} /> Alle gleichberechtigt
        </label>
      </fieldset>
      <ol className="space-y-2">
        {genannt.map((id, i) => {
          const m = nach.get(id)!;
          return (
            <li
              key={id}
              draggable
              onDragStart={() => setZiehe(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (ziehe != null) verschieben(ziehe, i);
                setZiehe(null);
              }}
              className="flex items-center gap-3 rounded-lg border border-outline-variant/40 px-3 py-2 dark:border-outline/40"
            >
              <Icon name="drag_indicator" className="cursor-grab text-[20px] text-secondary" />
              <span className="w-5 font-label-md text-label-md text-secondary">{i + 1}.</span>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-fixed text-on-primary-fixed">
                {m.fotoUrl ? <img src={m.fotoUrl} alt="" className="h-full w-full object-cover" /> : <Icon name="person" className="text-[18px]" />}
              </span>
              <span className="min-w-0 flex-1 truncate font-body-md text-body-md text-on-background dark:text-inverse-on-surface">{memberDisplayName(m)}</span>
              <span className="rounded-full bg-surface-container px-2 py-0.5 font-label-md text-[11px] text-secondary dark:bg-white/[0.06]">{badge(i)}</span>
              <Button variant="ghost" type="button" aria-label={`${memberDisplayName(m)} nach oben`} disabled={i === 0} onClick={() => verschieben(i, i - 1)}>
                <Icon name="arrow_upward" className="text-[18px]" />
              </Button>
              <Button variant="ghost" type="button" aria-label={`${memberDisplayName(m)} nach unten`} disabled={i === genannt.length - 1} onClick={() => verschieben(i, i + 1)}>
                <Icon name="arrow_downward" className="text-[18px]" />
              </Button>
              <Button variant="ghost" type="button" aria-label={`${memberDisplayName(m)} nicht mehr nennen`} onClick={() => entfernen(id)}>
                <Icon name="close" className="text-[18px]" />
              </Button>
            </li>
          );
        })}
      </ol>
      {offen.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {offen.map((m) => (
            <Button key={m.id} variant="secondary" type="button" onClick={() => ergaenzen(m.id as string)}>
              {memberDisplayName(m)} nennen
            </Button>
          ))}
        </div>
      )}
      <p aria-live="polite" className="sr-only" data-testid="ansprechpartner-ansage">{ansage}</p>
      {error && <ErrorNote message={error} />}
      <div className="mt-4 flex items-center gap-3">
        <Button type="button" disabled={busy} onClick={() => void speichern()}>
          {busy ? "Speichert …" : "Speichern"}
        </Button>
        {saved && <span role="status" className="font-body-sm text-body-sm text-secondary">Gespeichert</span>}
      </div>
    </Card>
  );
}
const FELD_LABEL = "mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface";
/**
 * KONZEPT-PERSON-PERSOENLICH (sql/676): „Person bearbeiten“ rechts neben der Team-Liste.
 * Jede Person pflegt ihr Profil selbst, Inhaber dürfen alle (der Server prüft das in
 * `update_tenant_user_profil`, hier nur die Anzeige). Wer in Kundenmails genannt wird, steht seit
 * Punkt 146 (sql/677) in der eigenen Liste „In Kundenmails genannt“ — hier nur ein Verweis dorthin.
 */
export function PersonBearbeiten({
  member,
  canEdit,
  selbst = false,
  onSaved,
}: {
  member: Member;
  canEdit: boolean;
  /** Punkt 149: die angemeldete Person bearbeitet sich selbst (eigenes Passwort über Stack-Client). */
  selbst?: boolean;
  onSaved: () => void;
}) {
  const [name, setName] = useState(member.displayName ?? "");
  // Punkt 149: Login-E-Mail. Die Team-Liste liest tenant_users.email und zeigt bis zu einer
  // späteren Migration weiter die alte Adresse, darum merkt sich das Formular die zuletzt gesetzte.
  const [loginEmail, setLoginEmail] = useState(member.email ?? "");
  const [gesetzteEmail, setGesetzteEmail] = useState(member.email ?? "");
  const { bestaetigen, dialog } = useBestaetigung();
  const [rolle, setRolle] = useState(member.rolle ?? "");
  const [rolleEn, setRolleEn] = useState(member.rolleEn ?? "");
  const [telefon, setTelefon] = useState(member.telefon ?? "");
  const [gruss, setGruss] = useState(member.emailClosing ?? "");
  const [signatur, setSignatur] = useState(member.emailSignature ?? "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function speichern(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    const neueEmail = loginEmail.trim().toLowerCase();
    const emailNeu = neueEmail !== "" && neueEmail !== gesetzteEmail.trim().toLowerCase();
    if (emailNeu) {
      const ok = await bestaetigen({
        titel: "Login-E-Mail ändern?",
        text: `${selbst ? "Du meldest dich" : `${memberDisplayName(member)} meldet sich`} ab sofort mit ${neueEmail} an, die alte Adresse gilt dann nicht mehr. Die alte und die neue Adresse sowie alle Inhaber bekommen eine Hinweis-Mail.`,
        bestaetigen: "E-Mail ändern",
      });
      if (!ok) {
        setBusy(false);
        return;
      }
    }
    try {
      if (emailNeu) {
        try {
          await apiMutate(`/api/members/${member.stackUserId}/login-email`, "PATCH", { email: neueEmail });
        } catch (err) {
          const code = (err as ApiError).code;
          throw new Error(
            code === "email_vergeben" ? "Diese E-Mail-Adresse gehört schon zu einem anderen Konto. Die Login-E-Mail bleibt unverändert."
            : code === "nicht_eingerichtet" ? "Login-E-Mail ändern ist noch nicht eingerichtet. Die Verbindung zum Anmeldedienst fehlt noch, bitte melde dich beim MAESTRO-Support."
            : code === "validation" || (err as ApiError).statusCode === 422 ? "Bitte eine gültige E-Mail-Adresse eingeben."
            : "Die Login-E-Mail konnte nicht geändert werden. Bitte versuche es gleich noch einmal.",
          );
        }
        setGesetzteEmail(neueEmail);
        setLoginEmail(neueEmail);
      }
      await apiMutate(`/api/members/${member.stackUserId}/profil`, "PATCH", {
        ...(name.trim() ? { displayName: name.trim() } : {}),
        rolle,
        rolleEn,
        telefon,
        emailClosing: gruss,
        emailSignature: signatur,
      });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function foto(file: File | undefined) {
    if (!file || busy) return;
    if (!BILD_TYPEN.includes(file.type)) {
      setError("Bitte ein Bild als PNG, JPG oder WebP.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { mediaType, dataBase64 } = await fileToBase64(await alsPortraitWandeln(file));
      await apiMutate(`/api/members/${member.stackUserId}/foto`, "POST", { mediaType, dataBase64 });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function fotoEntfernen() {
    setBusy(true);
    setError(null);
    try {
      await apiMutate(`/api/members/${member.stackUserId}/foto`, "DELETE");
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  const anzeigeName = name.trim() || memberDisplayName(member);
  return (
    <Card className="p-6" data-testid="person-bearbeiten">
      <h3 className="mb-4 font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Person bearbeiten</h3>
      {dialog}
      <form className="space-y-4" onSubmit={(e) => void speichern(e)}>
        {error && <ErrorNote message={error} />}
        <fieldset disabled={!canEdit || busy} className="space-y-4">
          <div className="flex items-center gap-4">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-fixed text-on-primary-fixed">
              {member.fotoUrl ? <img src={member.fotoUrl} alt="" className="h-full w-full object-cover" /> : <Icon name="person" className="text-[28px]" />}
            </span>
            {canEdit && (
              <div className="space-x-2">
                <ButtonOptik as="label" variant="secondary" className="inline-flex cursor-pointer !py-1.5">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    aria-label="Foto hochladen"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      void foto(f);
                    }}
                  />
                  {member.fotoUrl ? "Foto ersetzen" : "Foto hochladen"}
                </ButtonOptik>
                {member.fotoUrl && (
                  <button type="button" className="font-body-sm text-body-sm text-error hover:underline" onClick={() => void fotoEntfernen()}>
                    Foto entfernen
                  </button>
                )}
              </div>
            )}
          </div>
          <label className="block">
            <span className={FELD_LABEL}>Name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </label>
          <label className="block">
            <span className={FELD_LABEL}>E-Mail (Login)</span>
            <Input type="email" autoComplete="off" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} maxLength={254} />
          </label>
          <PasswortBereich stackUserId={member.stackUserId} name={memberDisplayName(member)} selbst={selbst} darf={canEdit} />
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className={FELD_LABEL}>Rolle</span>
              <Input value={rolle} onChange={(e) => setRolle(e.target.value)} maxLength={160} placeholder="Gastgeberin und Ansprechpartnerin" />
            </label>
            <label className="block">
              <span className={FELD_LABEL}>Rolle auf Englisch (optional)</span>
              <Input value={rolleEn} onChange={(e) => setRolleEn(e.target.value)} maxLength={160} placeholder="Your host" />
            </label>
          </div>
          <label className="block">
            <span className={FELD_LABEL}>Telefon (optional)</span>
            <Input value={telefon} onChange={(e) => setTelefon(e.target.value)} maxLength={40} placeholder="+49 89 123456" />
          </label>
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Rechte: {ROLE_LABEL[member.role] ?? member.role}
          </p>
          <div className="space-y-2 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
            <span className={FELD_LABEL}>In Kundenmails genannt</span>
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim" data-testid="nennung-verweis">
              {member.mailPosition != null ? `${anzeigeName} steht an Position ${member.mailPosition}. ` : `${anzeigeName} wird nicht genannt. `}
              <a href="#in-kundenmails-genannt" className="text-primary hover:underline dark:text-primary-fixed-dim">
                Zur Liste „In Kundenmails genannt“
              </a>
            </p>
          </div>
          <div className="space-y-4 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
            <span className={FELD_LABEL}>Meine E-Mail-Signatur</span>
            <label className="block">
              <span className={FELD_LABEL}>Grußformel</span>
              <Input value={gruss} onChange={(e) => setGruss(e.target.value)} maxLength={200} placeholder="Herzliche Grüße" />
            </label>
            <label className="block">
              <span className={FELD_LABEL}>Signatur</span>
              <Textarea value={signatur} onChange={(e) => setSignatur(e.target.value)} rows={4} maxLength={4000} className="resize-y" />
            </label>
            <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">Leer gelassen gilt die Signatur des Betriebs.</p>
          </div>
        </fieldset>
        <div className="rounded-lg border border-outline-variant/40 p-4 dark:border-outline/30" data-testid="gast-vorschau">
          <p className="mb-3 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">So sieht der Gast es</p>
          {member.mailPosition != null ? (
            <div className="flex items-center gap-3">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-fixed text-on-primary-fixed">
                {member.fotoUrl ? <img src={member.fotoUrl} alt="" className="h-full w-full object-cover" /> : <Icon name="person" />}
              </span>
              <div className="min-w-0">
                <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{anzeigeName}</p>
                {rolle.trim() && <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{rolle.trim()}</p>}
                {telefon.trim() && <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{telefon.trim()}</p>}
              </div>
            </div>
          ) : (
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Wird in Kundenmails nicht als Ansprechpartner genannt.</p>
          )}
          <p className="mt-3 whitespace-pre-line font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
            {gruss.trim() || signatur.trim() ? [gruss.trim(), signatur.trim()].filter(Boolean).join("\n") : "Grußformel und Signatur des Betriebs"}
          </p>
        </div>
        <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
          Jede Person pflegt ihr Profil selbst. Inhaber dürfen alle bearbeiten.
        </p>
        {canEdit && (
          <div className="flex items-center gap-3">
            <Button type="submit" className="!py-2" disabled={busy}>{busy ? "Speichert …" : "Speichern"}</Button>
            {saved && <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-tertiary dark:text-tertiary-fixed-dim"><Icon name="check_circle" className="text-[18px]" /> Gespeichert</span>}
          </div>
        )}
      </form>
    </Card>
  );
}
function SystemStatusSection() {
  const healthQ = useApi<SystemHealth>("/api/system/health");
  const errorsQ = useApi<SystemError[]>("/api/system/errors");
  const health = healthQ.data;
  const errors = errorsQ.data ?? [];
  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
          System-Status
        </h3>
        {health && (
          <span className={`font-label-md text-label-md ${health.errorsLast24h > 0 ? "text-error" : "text-secondary dark:text-secondary-fixed-dim"}`}>
            {health.errorsLast24h} in den letzten 24 h
          </span>
        )}
      </div>
      {health && (
        <div className="mb-4 space-y-1 border-b border-outline-variant/30 pb-4 dark:border-outline/30">
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            {plattformMeldungenZeile(health.plattformMeldungen24h ?? 0)}
          </p>
          {health.scheduler && (
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Ablaufplan (Sweeps):{" "}
              {ablaufplanZeile(health.scheduler.lastSweepAt ? new Date(health.scheduler.lastSweepAt) : null, new Date())}
            </p>
          )}
        </div>
      )}
      {errorsQ.loading ? (
        <LoadingRows rows={3} />
      ) : errors.length === 0 ? (
        <EmptyState icon="check_circle" title="Keine Fehler" hint="Alles läuft rund." />
      ) : (
        <div className="space-y-2">
          {errors.map((e) => (
            <div key={e.id} className="rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-body-sm text-on-background dark:text-inverse-on-surface">
                  {e.method} {e.path}
                </span>
                <span className="shrink-0 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {formatDateTime(e.createdAt)}
                </span>
              </div>
              {e.message && (
                <p className="mt-1 break-words font-body-sm text-body-sm text-error">{e.message}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
interface KompositionsKostenZeile {
  monat: string;
  kanal: "chat" | "auto" | "gast_antwort";
  laeufe: number;
  laeufeOhneMessung: number;
  laeufeAbgeschnitten: number;
  inputTokens: number;
  outputTokens: number;
  geschaetzteKostenCent: number;
  dauerMsSchnitt: number;
}
const KANAL_TEXT: Record<KompositionsKostenZeile["kanal"], string> = {
  chat: "Im Gespräch angestoßen",
  auto: "Automatisch bei Eingang",
  gast_antwort: "Nach einer Gast-Antwort",
};
function euro(cents: number): string {
  return `${(cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}
function KiKostenSection() {
  const q = useApi<{ zeilen: KompositionsKostenZeile[]; preisstand: string }>("/api/metrics/kompositions-kosten");
  const zeilen = q.data?.zeilen ?? [];
  const gesamt = zeilen.reduce((s, z) => s + z.geschaetzteKostenCent, 0);
  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
          KI-Kosten der Angebotserstellung
        </h3>
        {zeilen.length > 0 && (
          <span className="font-label-md text-label-md text-secondary dark:text-secondary-fixed-dim">{euro(gesamt)} geschätzt</span>
        )}
      </div>
      {q.loading ? (
        <LoadingRows rows={3} />
      ) : zeilen.length === 0 ? (
        <EmptyState icon="savings" title="Noch keine Läufe erfasst" hint="Sobald MAESTRO ein Angebot komponiert, erscheint der Lauf hier." />
      ) : (
        <div className="space-y-2">
          {zeilen.map((z) => (
            <div key={`${z.monat}-${z.kanal}`} className="rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-body-md text-body-md text-on-background dark:text-inverse-on-surface">
                  {z.monat} · {KANAL_TEXT[z.kanal]}
                </span>
                <span className="font-label-md text-label-md text-on-background dark:text-inverse-on-surface">{euro(z.geschaetzteKostenCent)}</span>
              </div>
              <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {z.laeufe} {z.laeufe === 1 ? "Lauf" : "Läufe"} · {z.inputTokens.toLocaleString("de-DE")} Token hinein,{" "}
                {z.outputTokens.toLocaleString("de-DE")} hinaus · ⌘ {Math.round(z.dauerMsSchnitt / 1000)} s
              </p>
              {z.laeufeOhneMessung > 0 && (
                <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  Bei {z.laeufeOhneMessung} {z.laeufeOhneMessung === 1 ? "Lauf" : "Läufen"} hat der Anbieter keine Nutzung gemeldet — diese sind im
                  Betrag nicht enthalten.
                </p>
              )}
              {z.laeufeAbgeschnitten > 0 && (
                <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {z.laeufeAbgeschnitten} {z.laeufeAbgeschnitten === 1 ? "Lauf wurde" : "Läufe wurden"} am Token-Deckel abgeschnitten.
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      {q.data?.preisstand && (
        <p className="mt-4 border-t border-outline-variant/30 pt-3 font-body-sm text-body-sm text-secondary dark:border-outline/30 dark:text-secondary-fixed-dim">
          {q.data.preisstand}
        </p>
      )}
    </Card>
  );
}
interface RetentionCandidate {
  customerId: string;
  name: string;
  email: string;
  eventCount: number;
  retentionEndsAt: string;
  eligible: boolean;
}
function RetentionTab() {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const { data, loading, reload } = useApi<RetentionCandidate[]>("/api/retention/candidates");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ purged: string[]; skipped: { customerId: string; reason: string }[]; emailRedactFailed?: string[]; anreicherungFehlgeschlagen?: string[] } | null>(null);
  const candidates = data ?? [];
  const eligible = candidates.filter((c) => c.eligible);
  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  async function purge() {
    if (selected.size === 0) return;
    const names = candidates.filter((c) => selected.has(c.customerId)).map((c) => c.name);
    if (
      !(await bestaetigen({
        titel: `${selected.size} Kunde(n) unwiderruflich anonymisieren?`,
        text: `${names.join(", ")}\n\nEntfernt werden: Name, E-Mail, Telefon, Anschrift, Notizen sowie Kundendaten in Vorgängen (Freitexte, E-Mail-Verläufe, eSign-Unterzeichner, Angebots-Anschreiben). Beträge und Belege bleiben als Buchungsgrundlage erhalten. Kopien bei externen Anbietern (eSign, LexOffice, Mail-Provider) sind nicht erfasst.`,
        bestaetigen: "Anonymisieren",
        gefahr: true,
      }))
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiMutate<{ purged: string[]; skipped: { customerId: string; reason: string }[]; emailRedactFailed?: string[]; anreicherungFehlgeschlagen?: string[] }>(
        "/api/retention/purge",
        "POST",
        { customerIds: [...selected], confirm: true },
      );
      setResult(res);
      setSelected(new Set());
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="p-6">
      {bestaetigungsDialog}
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
          DSGVO-Aufbewahrung
        </h3>
        {eligible.length > 0 && (
          <Button type="button" className="!py-2" disabled={selected.size === 0 || busy} onClick={() => void purge()}>
            <Icon name="delete_forever" className="text-[18px]" /> {busy ? "Wird anonymisiert …" : `${selected.size || ""} anonymisieren`.trim()}
          </Button>
        )}
      </div>
      <p className="mb-6 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Gewonnene Vorgänge (bezahlt/abgeschlossen) werden 10 Jahre aufbewahrt (§ 257 HGB), alle
        anderen 12 Monate ab der letzten Änderung. Anonymisieren entfernt Name, E-Mail, Telefon,
        Anschrift und Notizen des Kunden sowie Kundendaten in den Vorgängen (Freitexte,
        E-Mail-Verläufe, eSign-Unterzeichner, Angebots-Anschreiben) — Beträge und Belege bleiben
        als Buchungsgrundlage erhalten. Kopien bei externen Anbietern (eSign, LexOffice,
        Mail-Provider) sind nicht erfasst und müssen dort separat gelöscht werden.
      </p>
      {result && (
        <p className="mb-4 rounded-lg bg-[#F0EAE0] px-4 py-3 font-body-sm text-body-sm text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
          {result.purged.length} Kunde(n) anonymisiert{result.skipped.length > 0 ? `, ${result.skipped.length} übersprungen` : ""}
          {result.emailRedactFailed && result.emailRedactFailed.length > 0 ? ` — Achtung: E-Mail-Redaktion bei ${result.emailRedactFailed.length} Kunde(n) fehlgeschlagen (erneut ausführen).` : "."}
          {result.anreicherungFehlgeschlagen && result.anreicherungFehlgeschlagen.length > 0
            ? ` Achtung: Die Firmen-Anreicherung (Handelsregister/Bundesanzeiger) konnte bei ${result.anreicherungFehlgeschlagen.length} Firma/Firmen nicht entfernt werden — bitte erneut ausführen.`
            : ""}
        </p>
      )}
      {error && <ErrorNote message={error} />}
      {loading ? (
        <LoadingRows rows={3} />
      ) : candidates.length === 0 ? (
        <EmptyState icon="privacy_tip" title="Keine Kunden vorhanden" />
      ) : (
        <div className="space-y-2">
          {candidates.map((c) => (
            <label
              key={c.customerId}
              className={`flex items-center gap-3 rounded-lg border p-3 ${
                c.eligible ? "border-outline-variant/40 dark:border-outline/30" : "border-outline-variant/20 opacity-60 dark:border-outline/15"
              }`}
            >
              <Checkbox disabled={!c.eligible} checked={selected.has(c.customerId)} onChange={() => toggle(c.customerId)} />
              <div className="min-w-0 flex-1">
                <p className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  <Link
                    to={`/kunden/${c.customerId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-primary hover:underline"
                  >
                    {c.name}
                  </Link>{" "}
                  <span className="font-normal text-secondary dark:text-secondary-fixed-dim">· {c.eventCount} Vorgang/Vorgänge</span>
                </p>
                <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{c.email}</p>
              </div>
              <span className={`shrink-0 font-label-md text-label-md ${c.eligible ? "text-secondary dark:text-[#b8ae9e]" : "text-secondary dark:text-secondary-fixed-dim"}`}>
                {c.eligible ? "Löschbar seit " : "Aufbewahrung bis "}
                {formatDateTime(c.retentionEndsAt)}
              </span>
            </label>
          ))}
        </div>
      )}
    </Card>
  );
}
/** Setzt „Kopiert" nach `ms` zurueck — und raeumt den Timer beim Abbau auf (sonst feuert er ins Leere). */
function useKopiertZuruecksetzen(kopiert: boolean, setKopiert: (v: boolean) => void, ms: number) {
  useEffect(() => {
    if (!kopiert) return;
    const t = window.setTimeout(() => setKopiert(false), ms);
    return () => window.clearTimeout(t);
  }, [kopiert, setKopiert, ms]);
}

/**
 * Die gemeinsame Darstellung EINES Textbausteins samt Kopieren-Knopf. Es gibt inzwischen zwei
 * Bausteine (oeffentliche Quellen + E-Mail-Versand ueber Brevo); die Karte selbst ist bei beiden
 * dieselbe, nur Ueberschrift und Inhalt unterscheiden sich. `textZumKopieren` wird als Funktion
 * uebergeben, damit der Text erst beim Klick gebaut wird — und damit im Quelltext sichtbar bleibt,
 * WELCHE Textfunktion an welcher Karte haengt (die Ratsche in
 * apps/api/test/transparenz-hinweis.test.ts prueft genau das).
 */
function BausteinKarte({
  ueberschrift,
  baustein,
  textZumKopieren,
}: {
  ueberschrift: string;
  baustein: ReturnType<typeof transparenzBaustein>;
  textZumKopieren: () => string;
}) {
  const [kopiert, setKopiert] = useState(false);
  const [kopierFehler, setKopierFehler] = useState<string | null>(null);
  useKopiertZuruecksetzen(kopiert, setKopiert, 2500);
  async function kopieren() {
    setKopierFehler(null);
    try {
      await navigator.clipboard.writeText(textZumKopieren());
      setKopiert(true);
    } catch {
      setKopierFehler("Kopieren nicht möglich — bitte den Text unten markieren und kopieren.");
    }
  }
  return (
    <Card className="p-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">
          {ueberschrift}
        </h3>
        <Button variant="secondary" type="button" className="!py-2" onClick={() => void kopieren()}>
          <Icon name={kopiert ? "check" : "content_copy"} className="text-[18px]" />
          {kopiert ? "Kopiert" : "Text kopieren"}
        </Button>
      </div>
      <p className="mb-4 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        {baustein.haftungshinweis}
      </p>
      {kopierFehler && <ErrorNote message={kopierFehler} />}
      <div className="space-y-4 rounded-xl border border-outline-variant/40 p-4 dark:border-outline/30">
        <p className="font-title-md text-title-md font-semibold text-on-background dark:text-inverse-on-surface">
          {baustein.ueberschrift}
        </p>
        {baustein.abschnitte.map((abschnitt) => (
          <div key={abschnitt.titel}>
            <p className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
              {abschnitt.titel}
              {abschnitt.nochNichtAktiv && (
                <span className="ml-2 normal-case tracking-normal text-secondary dark:text-[#b8ae9e]">
                  {NOCH_NICHT_AKTIV_VERMERK}
                </span>
              )}
            </p>
            {abschnitt.absaetze.map((absatz, i) => (
              <p key={i} className="mt-1 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                {absatz}
              </p>
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}

function TransparenzBausteinKarte() {
  return (
    <BausteinKarte
      ueberschrift="Textbaustein für Ihre Datenschutzhinweise"
      baustein={transparenzBaustein()}
      textZumKopieren={() => transparenzText()}
    />
  );
}

/**
 * Zweite Karte: der E-Mail-Versand ueber Brevo (Antoine 03.09.2026). Bewusst eine eigene Karte mit
 * eigenem Kopieren-Knopf statt eines weiteren Abschnitts in der Karte darueber — die beiden Texte
 * gehoeren in unterschiedliche Abschnitte der Datenschutzhinweise des Betreibers, und wer nur
 * einen davon braucht, soll auch nur einen kopieren.
 */
function VersandTransparenzKarte() {
  return (
    <BausteinKarte
      ueberschrift="Textbaustein: E-Mail-Versand über Brevo"
      baustein={versandTransparenzBaustein()}
      textZumKopieren={() => versandTransparenzText()}
    />
  );
}
/**
 * Dritte Karte (Angebot · Einblicke, 19.10.2026): was beim Abruf eines Angebots ueber den
 * persoenlichen Link protokolliert wird - kopierbar fuer die Datenschutzerklaerung des Betriebs.
 */
function AngebotsAbrufKarte() {
  return (
    <BausteinKarte
      ueberschrift="Textbaustein: Abruf von Angeboten über den persönlichen Link"
      baustein={angebotsAbrufBaustein()}
      textZumKopieren={() => angebotsAbrufText()}
    />
  );
}
interface PaymentTermsData {
  depositPercentBp?: number | null;
  depositAmountCents?: number | null;
  paymentTermDays?: number | null;
  paymentTermText?: string | null;
  cancellationTerms?: string | null;
  /** Migration 712: benannte Storno-Vorlagen; der Standard wird serverseitig nach `cancellationTerms` gespiegelt. */
  stornoVorlagen?: StornoVorlage[] | null;
  /** WARTESCHLANGE Punkt 50 (Migration 404): die strukturierte Stornostaffel, absteigend nach
   *  `days`. Die Abrechnung rechnet damit den Einbehalt (`ermittleKeepPercent`). */
  cancellationTiers?: { days: number; keepPercent: number }[] | null;
  /** Punkt 50 b2 (Migration 453): die AGB als Text — der Gast sieht sie auf der Angebotsseite
   *  hinter einem Klick. */
  agbText?: string | null;
  finalPaymentMode?: "advance" | "onsite" | null;
  depositMethod?: "none" | "stripe_immediate" | "onsite" | "invoice_advance" | null;
  finalPaymentMethod?: "stripe_advance" | "onsite" | "invoice_before_event" | "invoice_after_event" | null;
  depositDueDays?: number | null;
  /** Migration 718: worauf sich depositDueDays bezieht (null = nach_bestaetigung). */
  depositDueMode?: "nach_bestaetigung" | "vor_veranstaltung" | null;
  finalPaymentDueDaysBeforeEvent?: number | null;
  /** Punkt 116 (Migration 518): Restzahlung N Tage NACH der Veranstaltung. */
  finalPaymentDueDaysAfterEvent?: number | null;
  /** § 8 (Migration 345, 31.08.2026): Annahme-/Zahlungsfluss-Umschalter (Mandant-Default). */
  acceptanceFlow?: "explicit" | "direct_payment" | null;
  paymentPathway?: "stripe" | "kostenuebernahme" | null;
  bankHolder?: string | null;
  bankName?: string | null;
  bankIban?: string | null;
  bankBic?: string | null;
  companyLegalName?: string | null;
  companyAddress?: string | null;
  companyRegister?: string | null;
  companyCity?: string | null;
  companyWebsite?: string | null;
  /** Migration 539: Knopf „Tisch reservieren“ der Saison-Mails. */
  opentableUrl?: string | null;
  /** Migration 740: Knopf „In OpenTable eintragen“. */
  opentableKnopfAktiv?: boolean | null;
  opentableRestaurantId?: string | null;
  opentableSprache?: string | null;
  opentableMaxGaeste?: number | null;
  opentableNurImHaus?: boolean | null;
  /** Migration 542: Google-Bewertungsbitte. */
  googleReviewUrl?: string | null;
  /** sql/761: Vertrauenszeile im Widget (jsonb, mit `stand` vom Server). */
  vertrauenszeile?: unknown;
  bewertungsbitteAktiv?: boolean | null;
  /** Migration 597 (Buchungsstrecke C3): Vorfreude-Mail zwei Wochen vorher. */
  vorfreudeMailAktiv?: boolean | null;
  googlePlaceId?: string | null;
  companyBrand?: string | null;
  companyOwner?: string | null;
  agbUrl?: string | null;
  privacyUrl?: string | null;
  finalCountDays?: number | null;
  companyVatId?: string | null;
  companyPhone?: string | null;
  companyEmail?: string | null;
  companyPhotoUrl?: string | null;
  /** Briefkopf-Logo des Angebots-PDF (Migration 466). */
  companyLogoUrl?: string | null;
  /** Punkt 145 (Migration 677): der E-Mail-Footer an einem Ort. */
  emailFooterMarke?: "wortmarke" | "logo" | null;
  impressumUrl?: string | null;
  emailFooterWebseiteAn?: boolean;
  emailFooterImpressumAn?: boolean;
  emailFooterDatenschutzAn?: boolean;
  emailFooterText?: string | null;
  emailFooterTextEn?: string | null;
  /** Punkt 146: wie die genannten Ansprechpartner im Mailkopf stehen. */
  ansprechpartnerDarstellung?: "haupt_vertretung" | "gleichberechtigt" | null;
  /** Migration 603 (Buchungsstrecke B4): Begrüßungsvideo des Betriebs. */
  begruessungsvideoUrl?: string | null;
  begruessungsvideoVorschauUrl?: string | null;
  begruessungsvideoUntertitelUrl?: string | null;
  /** Migration 593 (Buchungsstrecke D4): Sofort-Antwort mit Inhalt, beide Vorgabe aus. */
  sofortantwortTagFrei?: boolean;
  sofortantwortPreisrahmen?: boolean;
  companyTagline?: string | null;
  esignTemplateId?: string | null;
  emailSenderName?: string | null;
  emailClosing?: string | null;
  emailSignature?: string | null;
  bundesland?: string | null;
  companyLat?: number | null;
  companyLon?: number | null;
  geocodedAt?: string | null;
  gastAblehnenEnabled?: boolean;
  /** Buchungsstrecke B2 (Migration 591): neue Angebotsseite an/aus. */
  angebotsseiteV2?: boolean;
  /** Punkt 82: die bearbeitbaren Mailtexte (Beschreibung + Werkstext) und die Platzhalter-Leiste. */
  mailVorlagenListe?: VorlagenBeschreibung[];
  platzhalter?: PlatzhalterBeschreibung[];
  mailVorlagen?: Record<string, GespeicherteMailVorlage>;
  /** Punkt 35 (Migration 472): Angebote auch nach dem „Gültig bis“-Datum noch annehmbar. */
  annahmeNachAblaufEnabled?: boolean;
  mailFreigabeModus?: "manuell" | "automatisch";
  /** Punkt 82: je Gast-Mail-Art, ob eine Kopie an den Betrieb geht. Fehlender Schlüssel = nicht
   *  gewählt ⇒ es gilt `abWerk` aus `mailArten`, NICHT „aus". */
  mailKopien?: Record<string, boolean>;
  /** Punkt 82: die Liste der Arten — kommt vom Server, damit es sie nicht zweimal gibt. */
  mailArten?: { schluessel: string; bezeichnung: string; anlass: string; abWerk: boolean }[];
  // Paket D1 (Migration 315): die drei Benachrichtigungs-Schalter.
  benachrichtigungNeueAnfrageEnabled?: boolean;
  benachrichtigungMailBounceEnabled?: boolean;
  benachrichtigungMailErfolgEnabled?: boolean;
  /** Buchungsstrecke D1 (Migration 575): Tempo-Alarm an/aus + Schwelle in Minuten. */
  tempoAlarmAktiv?: boolean;
  tempoAlarmMinuten?: number;
  /** Punkt 131 (Migration 669): Schild „Große Gruppe“ auf der Anfragen-Tafel an/aus + Schwelle. */
  grosseGruppeAktiv?: boolean;
  grosseGruppeAb?: number;
  /** Buchungsstrecke C2 (Migration 595): Terminreservierung auf Zeit (Vorgabe aus) + Dauer in Tagen. */
  terminreservierungAktiv?: boolean;
  terminreservierungTage?: number;
  businessCountry?: string | null;
  businessRegion?: string | null;
  industryCategory?: "catering" | "event_location" | "restaurant" | "other" | null;
  // Migration 327: Bild-Slots der oeffentlichen Angebotsseite.
  offerHeroImageUrl?: string | null;
  offerOptionPhoto1Url?: string | null;
  offerOptionPhoto2Url?: string | null;
  offerOptionPhoto3Url?: string | null;
  offerHeroFocalPoint?: string | null;
  offerOptionPhoto1FocalPoint?: string | null;
  offerOptionPhoto2FocalPoint?: string | null;
  offerOptionPhoto3FocalPoint?: string | null;
}
function TextField({ name, label, defaultValue, placeholder }: { name: string; label: string; defaultValue?: string | null; placeholder?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">{label}</span>
      <Input name={name} defaultValue={defaultValue ?? ""} placeholder={placeholder} />
    </label>
  );
}
/**
 * WARTESCHLANGE Punkt 50 — die Stornostaffel, endlich pflegbar.
 *
 * Antoine, 19.09.2026: „Storno Staffel: in den Einstellungen hinterlegen."
 *
 * Die Spalte gibt es seit Migration 404, und die Abrechnung rechnet damit bereits den Einbehalt.
 * Befuellen konnte sie nur niemand — es gab keine Maske. Hier ist sie.
 *
 * Die Zeile liest sich als Satz („bis 30 Tage vorher behalten wir 0 %"), nicht als Tabelle mit
 * zwei Zahlenspalten: es ist eine Regel, die der Betreiber seinen Gaesten gegenueber vertritt,
 * und die soll er beim Eintippen lesen koennen.
 *
 * SORTIERT wird beim Speichern auf dem Server (routes/settings.ts), nicht hier — eine Regel, die
 * an der Eingabemaske haengt, gilt nur so lange, wie es genau diese eine Maske gibt.
 */
export function StornoStaffelFeld({ wert, onChange }: { wert: { days: number; keepPercent: number }[]; onChange: (w: { days: number; keepPercent: number }[]) => void }) {
  const setzeZeile = (i: number, teil: Partial<{ days: number; keepPercent: number }>) =>
    onChange(wert.map((z, n) => (n === i ? { ...z, ...teil } : z)));
  return (
    <div className="space-y-2">
      <span className="block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Stornostaffel (optional)</span>
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Was behalten Sie ein, wenn ein Gast absagt? Je naeher die Veranstaltung, desto mehr. Der Gast sieht die Staffel auf
        seiner Angebotsseite.
      </p>
      {wert.length === 0 ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">Noch keine Stufe angelegt.</p>
      ) : (
        <ul className="space-y-2" data-testid="stornostaffel-zeilen">
          {wert.map((zeile, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2 font-body-sm text-body-sm">
              <span className="text-on-surface-variant">bis</span>
              <Input
                type="number"
                min={0}
                max={3650}
                aria-label={`Stufe ${i + 1}: Tage vor der Veranstaltung`}
                className="w-20"
                value={String(zeile.days)}
                onChange={(e) => setzeZeile(i, { days: Number(e.currentTarget.value) })}
              />
              <span className="text-on-surface-variant">Tage vorher behalten wir</span>
              <Input
                type="number"
                min={0}
                max={100}
                aria-label={`Stufe ${i + 1}: Prozent einbehalten`}
                className="w-20"
                value={String(zeile.keepPercent)}
                onChange={(e) => setzeZeile(i, { keepPercent: Number(e.currentTarget.value) })}
              />
              <span className="text-on-surface-variant">%</span>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Stufe ${i + 1} entfernen`}
                onClick={() => onChange(wert.filter((_, n) => n !== i))}
              >
                <Icon name="delete" className="text-[18px]" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Button type="button" variant="secondary" onClick={() => onChange([...wert, { days: 0, keepPercent: 0 }])}>
        <Icon name="add" className="text-[18px]" /> Stufe hinzufuegen
      </Button>
    </div>
  );
}
function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-tertiary-container px-2 py-0.5 font-label-md text-[10.5px] font-semibold uppercase tracking-wider text-tertiary">
      <span className="h-1.5 w-1.5 rounded-full bg-tertiary" />
      Live
    </span>
  );
}
function ToggleSwitch({
  checked,
  onChange,
  label,
  hint,
  disabled,
  disabledHint,
  disabledAction,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
  disabledHint?: string;
  disabledAction?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={`flex items-start justify-between gap-4 rounded-lg border border-outline-variant/40 p-4 dark:border-outline/30 ${disabled ? "opacity-60" : ""}`}>
      <div className="min-w-0 flex-1">
        <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{label}</p>
        {hint && <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{hint}</p>}
        {disabled && disabledHint && (
          <p className="mt-1.5 flex items-center gap-1 font-body-sm text-body-sm text-error">
            <Icon name="lock" className="text-[15px]" /> {disabledHint}
          </p>
        )}
        {disabled && disabledAction}
        {children}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        // Paket D1: Der Schalter trug bisher KEINEN zugaenglichen Namen — die Beschriftung steht
        // als eigener Absatz daneben und ist mit dem Knopf nicht verknuepft. Fuer einen
        // Screenreader (und fuer jeden Test, der ueber echte ARIA-Rollen sucht statt ueber
        // CSS-Klassen) hiess der Knopf damit schlicht „Schalter". `aria-label` ist die kleinste
        // Ergaenzung, die das behebt, und rein additiv: sichtbar aendert sich nichts.
        aria-label={label}
        aria-disabled={disabled}
        disabled={disabled}
        title={disabled ? disabledHint : undefined}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-primary" : "bg-surface-variant dark:bg-surface-dim"
        } ${disabled ? "cursor-not-allowed" : ""}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}
function usePaymentTermsData(onFreshData?: (data: PaymentTermsData) => void) {
  const { data, loading, reload } = useApi<PaymentTermsData | null>("/api/settings/payment-terms");
  const [ready, setReady] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const dirtyRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const onFreshDataRef = useRef(onFreshData);
  onFreshDataRef.current = onFreshData;
  useEffect(() => {
    if (loading) return;
    setReady(true);
    if (!data) {
      pendingRefreshRef.current = false;
      return;
    }
    const refreshing = pendingRefreshRef.current;
    pendingRefreshRef.current = false;
    if (refreshing && dirtyRef.current) return;
    onFreshDataRef.current?.(data);
    if (refreshing) {
      dirtyRef.current = false;
      setFormKey((k) => k + 1);
    }
  }, [loading, data]);
  useContentRefetch("settings", () => {
    pendingRefreshRef.current = true;
    reload();
  });
  return {
    data,
    ready,
    reload,
    formKey,
    markDirty: () => {
      dirtyRef.current = true;
    },
    markSaved: () => {
      dirtyRef.current = false;
    },
  };
}
export function CompanyHeaderForm() {
  // `reload` wird für den Logo-Upload gebraucht: der schreibt über eine eigene Route und muss
  // die Formulardaten danach neu holen, sonst zeigt die Vorschau noch das alte Bild.
  const { data, ready, formKey, reload, markDirty, markSaved } = usePaymentTermsData();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `industryCategory` ist ein Select (Radix, s. Kopfkommentar KEIN_WERT) statt eines rohen
  // Select-Elements — anders als die uebrigen Formularfelder dieses Formulars nicht mehr per FormData
  // beim Absenden ausgelesen, sondern als eigener State direkt in `save()` verwendet. Reset bei
  // jedem `formKey`-Wechsel (frischer, unbearbeiteter Server-Stand) - exakt dasselbe Verhalten wie
  // bei den unveraendert unkontrollierten `<input defaultValue>`-Feldern, die `<form key={formKey}>`
  // automatisch zuruecksetzt.
  const [industryCategory, setIndustryCategory] = useState<string>(data?.industryCategory ?? KEIN_WERT);
  useEffect(() => {
    setIndustryCategory(data?.industryCategory ?? KEIN_WERT);
    // `ready` gehoert dazu: beim ersten Laden ist `data` beim Einhaengen noch null und `formKey`
    // wechselt danach NICHT — ohne `ready` bliebe die Branche auf „Nicht angegeben“ stehen und
    // jedes Speichern loeschte sie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey, ready]);
  /*
   * Veralteter zweiter Tab (Nebenfund AGB-Analyse 27.09.2026): das Formular schickte bisher
   * IMMER alle Felder. Wer in einem alten Tab nur die Tagline aenderte, schrieb damit auch den
   * alten Firmennamen, die alte Adresse usw. zurueck — ueber das, was ein anderer Tab
   * inzwischen gespeichert hatte. Jetzt geht nur mit, was sich gegenueber dem Stand, aus dem das
   * Formular gebaut wurde, WIRKLICH geaendert hat; der Server laesst fehlende Felder stehen.
   *
   * Kein `updated_at`-Vergleich (409): dieselbe Zeile schreiben acht Formulare dieser Seite mit
   * je eigenem Ladestand — ein Speichern unter „Zahlungen“ haette hier einen Scheinkonflikt
   * ausgeloest.
   *
   * Die Basis haengt an `formKey`/`ready`, NICHT an `data`: kommt ein frischer Stand, waehrend
   * das Formular bearbeitet wird, bleibt das Formular stehen (usePaymentTermsData) — dann muss
   * auch die Basis der alte Stand bleiben, sonst saehe jedes unberuehrte Feld „geaendert“ aus.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const basis = useMemo(() => data, [formKey, ready]);
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const str = (k: string) => ((f.get(k) as string) || "").trim() || null;
    const businessCountryRaw = ((f.get("businessCountry") as string | null) ?? "").trim().toUpperCase();
    if (businessCountryRaw && !/^[A-Z]{2}$/.test(businessCountryRaw)) {
      setError("Ungültiger Ländercode – bitte zwei Großbuchstaben eingeben, z. B. DE.");
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const alle: Record<string, string | boolean | number | null> = {
        companyCity: str("companyCity"),
        opentableUrl: str("opentableUrl"),
        opentableKnopfAktiv: f.get("opentableKnopfAktiv") === "true",
        opentableRestaurantId: str("opentableRestaurantId"),
        opentableSprache: str("opentableSprache") ?? "de-DE",
        opentableMaxGaeste: Math.min(500, Math.max(1, Math.trunc(Number(f.get("opentableMaxGaeste"))) || 50)),
        opentableNurImHaus: f.get("opentableNurImHaus") === "true",
        googleReviewUrl: str("googleReviewUrl"),
        bewertungsbitteAktiv: f.get("bewertungsbitteAktiv") === "true",
        vorfreudeMailAktiv: f.get("vorfreudeMailAktiv") === "true",
        companyBrand: str("companyBrand"),
        companyOwner: str("companyOwner"),
        agbUrl: str("agbUrl"),
        companyPhotoUrl: str("companyPhotoUrl"),
        companyTagline: str("companyTagline"),
        emailSenderName: str("emailSenderName"),
        emailClosing: str("emailClosing"),
        emailSignature: str("emailSignature"),
        businessCountry: businessCountryRaw || null,
        businessRegion: str("businessRegion"),
        industryCategory: industryCategory === KEIN_WERT ? null : industryCategory,
      };
      // Basis genauso normalisiert wie die Eingabe (getrimmt, leer = null), sonst gaelte ein
      // unberuehrtes Feld mit Leerzeichen am Ende als geaendert.
      const b = { ...(basis ?? {}) } as Record<string, unknown>;
      // Migration 740: fehlt ein Wert im Ladestand (alter Stand), gilt die Vorgabe der Datenbank — sonst wäre jedes Speichern eine „Änderung“.
      b.opentableSprache = b.opentableSprache ?? "de-DE";
      b.opentableMaxGaeste = b.opentableMaxGaeste ?? 50;
      b.opentableNurImHaus = b.opentableNurImHaus ?? true;
      const geaendert = Object.fromEntries(
        Object.entries(alle).filter(([k, v]) => {
          const alt = b[k];
          if (typeof v === "boolean") return v !== Boolean(alt);
          if (typeof v === "number") return v !== alt;
          const altNorm = (typeof alt === "string" ? alt.trim() : "") || null;
          return v !== (k === "businessCountry" ? altNorm?.toUpperCase() ?? null : altNorm);
        }),
      );
      // sql/761: die Vertrauenszeile ist ein Objekt (verstecktes Feld als JSON) — eigener Vergleich, `stand` setzt der Server.
      // Gespeichert NULL (nie eingestellt) wird gegen VERTRAUENSZEILE_NIE_GESETZT (Schalter an) verglichen: ein bewusstes
      // Aus geht damit immer hinaus (Codex-Befund #1864), ein unberuehrtes Formular schreibt nichts.
      const vzRoh = f.get("vertrauenszeile");
      if (vzRoh === "ungueltig") {
        setError("Bitte die Angaben der Vertrauenszeile prüfen.");
        return;
      }
      if (typeof vzRoh === "string" && vzRoh) {
        const vz = vertrauenszeileAus(JSON.parse(vzRoh));
        if (!gleicheVertrauenszeile(vz, vertrauenszeileAus(b.vertrauenszeile))) (geaendert as Record<string, unknown>).vertrauenszeile = vz;
      }
      if (Object.keys(geaendert).length > 0) await apiMutate("/api/settings/payment-terms", "PUT", geaendert);
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  if (!ready) return <LoadingRows rows={2} />;
  const d = data ?? {};
  return (
    <form key={formKey} className="space-y-4" onInput={markDirty} onSubmit={(e) => void save(e)}>
      {error && <ErrorNote message={error} />}
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Firmenname, Adresse, Telefon, E-Mail, Registereintrag, USt-IdNr., Webseite und Datenschutz-Link pflegst du jetzt in der Karte „E-Mail-Footer“ unten. Kostenübernahme und Rechnung lesen weiter dieselben Werte.
      </p>
      {/*
        Vertragsfassung 2.0 (Antoine, 22.09.2026). Diese sechs Angaben standen bis dahin FEST im
        Text der Kostenübernahme — „Speranza GmbH", „München", „Domenico Speranza". Solange das so
        war, konnte kein zweiter Betrieb dieselbe Vorlage benutzen.

        Der Ort steht bewusst als EIGENES Feld neben der Adresse und wird nicht aus ihr
        geschnitten: er trägt den Gerichtsstand, und „Bad Tölz" oder „Frankfurt am Main" gehen
        beim Schneiden schief. Eine geratene Stadt im Gerichtsstand wäre schlimmer als keine.
      */}
      <TextField name="companyCity" label="Ort (Gerichtsstand)" defaultValue={d.companyCity} placeholder="München" />
      {/* Saison-Mails (Antoine 26.09.2026): Knopf „Tisch reservieren“ — bis 9 Personen bucht der Gast selbst. */}
      <TextField
        name="opentableUrl"
        label="Reservierungslink (OpenTable)"
        defaultValue={d.opentableUrl}
        placeholder="https://www.opentable.de/r/ihr-restaurant"
      />
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Knopf „Tisch reservieren“ in den Saison-Mails (bis 9 Personen). Ab 10 Personen fragt der Gast über MAESTRO als Gruppe an.
      </p>
      <OpenTableEinstellung werte={d} />
      <GoogleBewertungEinstellung googleReviewUrl={d.googleReviewUrl} googlePlaceId={d.googlePlaceId} aktiv={d.bewertungsbitteAktiv} />
      {/* sql/761 (Antoine, 28.10.2026): die Vertrauenszeile unten in jedem Anfrage-Widget. */}
      <VertrauenszeileEinstellung wert={d.vertrauenszeile} />
      <VorfreudeMailEinstellung aktiv={d.vorfreudeMailAktiv} />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField name="companyBrand" label="Auftrittsname" defaultValue={d.companyBrand} placeholder="Ristorante Musterhof" />
        <TextField name="companyOwner" label="Inhaber (Grußformel)" defaultValue={d.companyOwner} placeholder="Maria Muster" />
      </div>
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Der Ort trägt den Gerichtsstand der Kostenübernahme. Der Auftrittsname ist der Name, unter
        dem der Betrieb nach außen erscheint — er ist oft nicht der Firmenname.
      </p>
      <TextField name="agbUrl" label="Link zu den AGB" defaultValue={d.agbUrl} placeholder="https://…/agb" />


      <div className="grid gap-4 sm:grid-cols-2">
        <TextField name="companyPhotoUrl" label="Foto-URL (Hero-Band in der Angebots-Mail)" defaultValue={d.companyPhotoUrl} placeholder="https://…/foto.jpg" />
        <TextField name="companyTagline" label="Tagline" defaultValue={d.companyTagline} placeholder="Events & Catering · Berlin" />
      </div>
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Ohne Foto-URL bleibt die Angebots-Mail wie bisher (kein Foto-Band).
      </p>
      {/* Logo gibt es nur EINMAL, im Tab „Branding“ (Antoine, 28.09.2026: „Sollte nur einmal geben
          und überall genutzt werden“). Hier nur der Verweis dorthin. */}
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Das Logo (Briefkopf im Angebots-PDF, Mails und Gastseiten) pflegst du unter{" "}
        <Link to="/einstellungen?tab=branding" className="text-primary hover:underline">Branding</Link>.
      </p>
      <div className="space-y-4 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Geschäftsprofil</h3>
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Grundlage für den späteren Business-Analytics-Hub (Wirtschaftsraum-Vergleiche, KI-Kontext) — heute rein informativ, ohne Auswertung dahinter.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField name="businessCountry" label="Land (ISO-Ländercode)" defaultValue={d.businessCountry} placeholder="z. B. DE" />
          <TextField name="businessRegion" label="Wirtschaftsraum / Region" defaultValue={d.businessRegion} placeholder="z. B. Berlin/Brandenburg" />
        </div>
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Branche</span>
          <Select
            ariaLabel="Branche"
            value={industryCategory}
            onValueChange={(v) => {
              setIndustryCategory(v);
              markDirty();
            }}
            options={[{ value: KEIN_WERT, label: "Nicht angegeben" }, ...INDUSTRY_CATEGORIES]}
          />
        </label>
      </div>
      <div className="space-y-4 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">E-Mail-Signatur</h3>
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Grußformel + Signatur erscheinen am Ende von Angebots- und Nachfass-Mails. Ohne Eintrag: Standard-Grußformel + Absendername.
        </p>
        <TextField name="emailSenderName" label="Absendername" defaultValue={d.emailSenderName} placeholder="Musterhof Catering" />
        <TextField name="emailClosing" label="Grußformel" defaultValue={d.emailClosing} placeholder="Mit freundlichen Grüßen" />
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Signatur (mehrzeilig, optional)</span>
          <Textarea
            name="emailSignature"
            rows={5}
            defaultValue={d.emailSignature ?? ""}
            placeholder={"Max Mustermann\nMusterhof Catering\nTel. 030 12345678 · www.musterhof-catering.de"}
            className="resize-y"
          />
        </label>
      </div>
      <div className="flex items-center gap-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
        <Button type="submit" className="!py-2" disabled={busy}>{busy ? "Speichert …" : "Speichern"}</Button>
        {saved && <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-tertiary dark:text-tertiary-fixed-dim"><Icon name="check_circle" className="text-[18px]" /> Gespeichert</span>}
      </div>
    </form>
  );
}
/*
 * Punkt 145 (Antoine, 06.10.2026, Mockup „Footer und Ansprechpartner“ freigegeben): EIN Ort für den
 * beigen Footer unter jeder Kundenmail. Die Firmenfelder zogen aus „Firmen-Kopf“ hierher um —
 * dieselben Spalten, Kostenübernahme und Rechnung lesen sie unverändert.
 *
 * Kontrolliert statt FormData, weil die Vorschau bei jedem Tastendruck mitläuft. Gespeichert wird
 * wie im Firmen-Kopf nur, was sich gegenüber dem geladenen Stand geändert hat (veralteter Tab).
 */
const FOOTER_TEXTFELDER = [
  "companyLegalName",
  "companyAddress",
  "companyPhone",
  "companyEmail",
  "companyRegister",
  "companyVatId",
  "companyWebsite",
  "impressumUrl",
  "privacyUrl",
  "emailFooterText",
  "emailFooterTextEn",
] as const;
const FOOTER_SCHALTER = ["emailFooterWebseiteAn", "emailFooterImpressumAn", "emailFooterDatenschutzAn"] as const;
type FooterFelder = Record<(typeof FOOTER_TEXTFELDER)[number], string> &
  Record<(typeof FOOTER_SCHALTER)[number], boolean> & { emailFooterMarke: "wortmarke" | "logo" };

/** Dieselben Beschriftungen wie `FOOTER_LABEL` in apps/api/src/lib/mailer.ts. */
export const FOOTER_BESCHRIFTUNG = {
  de: { telefon: "Telefon", email: "E-Mail", ust: "USt-IdNr.", webseite: "Webseite", impressum: "Impressum", datenschutz: "Datenschutz" },
  en: { telefon: "Phone", email: "Email", ust: "VAT ID", webseite: "Website", impressum: "Legal notice", datenschutz: "Privacy" },
} as const;

function footerStandAus(d: PaymentTermsData | null | undefined): FooterFelder {
  const s = (d ?? {}) as Record<string, unknown>;
  return {
    ...(Object.fromEntries(FOOTER_TEXTFELDER.map((k) => [k, typeof s[k] === "string" ? (s[k] as string) : ""])) as Record<(typeof FOOTER_TEXTFELDER)[number], string>),
    ...(Object.fromEntries(FOOTER_SCHALTER.map((k) => [k, Boolean(s[k])])) as Record<(typeof FOOTER_SCHALTER)[number], boolean>),
    emailFooterMarke: d?.emailFooterMarke === "logo" ? "logo" : "wortmarke",
  };
}

/** Nur geänderte Felder, leere Texte als null — so wie der Server sie speichert. */
export function footerAenderungen(neu: FooterFelder, alt: FooterFelder): Record<string, string | boolean | null> {
  const aus: Record<string, string | boolean | null> = {};
  for (const k of FOOTER_TEXTFELDER) if (neu[k].trim() !== alt[k].trim()) aus[k] = neu[k].trim() || null;
  for (const k of FOOTER_SCHALTER) if (neu[k] !== alt[k]) aus[k] = neu[k];
  if (neu.emailFooterMarke !== alt.emailFooterMarke) aus.emailFooterMarke = neu.emailFooterMarke;
  return aus;
}

export function FooterVorschau({ stand, lang, absender, logoUrl }: { stand: FooterFelder; lang: "de" | "en"; absender: string; logoUrl?: string | null }) {
  const l = FOOTER_BESCHRIFTUNG[lang];
  const t = (k: (typeof FOOTER_TEXTFELDER)[number]) => stand[k].trim();
  const kontakt = [t("companyPhone") && `${l.telefon}: ${t("companyPhone")}`, t("companyEmail") && `${l.email}: ${t("companyEmail")}`].filter(Boolean).join(" · ");
  const links = [
    [stand.emailFooterWebseiteAn, t("companyWebsite"), l.webseite],
    [stand.emailFooterImpressumAn, t("impressumUrl"), l.impressum],
    [stand.emailFooterDatenschutzAn, t("privacyUrl"), l.datenschutz],
  ].filter(([an, url]) => an && url) as [boolean, string, string][];
  const zeilen = [
    [t("companyLegalName"), t("companyAddress")].filter(Boolean).join(" · "),
    kontakt,
    t("companyRegister"),
    t("companyVatId") && `${l.ust}: ${t("companyVatId")}`,
  ].filter(Boolean);
  const freitext = (lang === "en" && t("emailFooterTextEn")) || t("emailFooterText");
  return (
    <div data-testid="footer-vorschau" lang={lang} className="rounded-b-xl border-t border-[#efe8da] bg-[#fbf8f1] px-6 py-5 font-sans text-[#262119]">
      {stand.emailFooterMarke === "logo" && logoUrl ? (
        <img src={logoUrl} alt={absender} width={120} className="block h-auto max-w-[120px]" />
      ) : (
        <div className="flex items-center gap-2.5">
          <span aria-hidden className="h-[26px] w-[26px] shrink-0 rounded-[7px] bg-primary text-center font-serif text-[14px] font-bold leading-[26px] text-on-primary">
            {absender.trim().charAt(0).toUpperCase()}
          </span>
          <span className="text-[12px] font-bold uppercase tracking-[0.14em]">{absender}</span>
        </div>
      )}
      {stand.emailFooterMarke === "logo" && !logoUrl && (
        <p className="mt-1 text-[11px] text-error">Noch kein Logo hinterlegt — die Mail zeigt die Wortmarke.</p>
      )}
      {(zeilen.length > 0 || links.length > 0 || freitext) && <div className="my-2.5 h-px bg-[#efe8da]" />}
      {(zeilen.length > 0 || links.length > 0) && (
        <div className="break-words text-[11px] leading-[1.8] text-[#9a9080]">
          {zeilen.map((z) => <div key={z}>{z}</div>)}
          {links.length > 0 && (
            <div>
              {links.map(([, url, label], i) => (
                <span key={label}>
                  {i > 0 && " · "}
                  <a href={/^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`} target="_blank" rel="noreferrer" className="text-[#6f675a] underline">{label}</a>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {freitext && <div className="mt-2 whitespace-pre-line text-[11px] leading-[1.7] text-[#6f675a]">{freitext}</div>}
    </div>
  );
}

export function EmailFooterForm() {
  const { data, ready, formKey, markDirty, markSaved, reload } = usePaymentTermsData();
  const [stand, setStand] = useState<FooterFelder>(() => footerStandAus(data));
  const [lang, setLang] = useState<"de" | "en">("de");
  const [busy, setBusy] = useState<"speichern" | "testmail" | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const basis = useMemo(() => footerStandAus(data), [formKey, ready]);
  useEffect(() => setStand(basis), [basis]);
  const setze = <K extends keyof FooterFelder>(k: K, v: FooterFelder[K]) => {
    setStand((s) => ({ ...s, [k]: v }));
    markDirty();
    setMeldung(null);
  };
  async function speichern() {
    const aenderung = footerAenderungen(stand, basis);
    setBusy("speichern");
    setError(null);
    try {
      if (Object.keys(aenderung).length > 0) await apiMutate("/api/settings/payment-terms", "PUT", aenderung);
      markSaved();
      setMeldung("Gespeichert");
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }
  async function testmail() {
    setBusy("testmail");
    setError(null);
    try {
      const entwurf = Object.fromEntries(
        Object.entries(stand).map(([k, v]) => [k, typeof v === "string" ? v.trim() || null : v]),
      );
      const res = (await apiMutate("/api/settings/email-footer/test-mail", "POST", { ...entwurf, lang })) as { data?: { sentTo?: string } } | null;
      setMeldung(`Testmail an ${res?.data?.sentTo ?? "dich"} verschickt.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }
  if (!ready) return <LoadingRows rows={3} />;
  const d = data ?? {};
  const absender = d.emailSenderName?.trim() || d.companyBrand?.trim() || stand.companyLegalName.trim() || "Ihr Betrieb";
  const feld = (k: (typeof FOOTER_TEXTFELDER)[number], label: string, placeholder?: string) => (
    <label className="block">
      <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">{label}</span>
      <Input value={stand[k]} placeholder={placeholder} onChange={(e) => setze(k, e.target.value)} />
    </label>
  );
  const link = (an: (typeof FOOTER_SCHALTER)[number], url: "companyWebsite" | "impressumUrl" | "privacyUrl", label: string, placeholder: string) => (
    <div className="flex items-center justify-between gap-4 border-b border-outline-variant/30 py-2 last:border-b-0 dark:border-outline/30">
      <label className="block min-w-0 flex-1">
        <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">{label}</span>
        <Input type="url" value={stand[url]} placeholder={placeholder} onChange={(e) => setze(url, e.target.value)} />
      </label>
      <Toggle checked={stand[an]} onChange={(v) => setze(an, v)} label={`${label} im Footer zeigen`} />
    </div>
  );
  const abschnitt = "font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim";
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-outline-variant/30 pb-4 dark:border-outline/30">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">E-Mail-Footer</h3>
        <span className="rounded-full border border-outline-variant/50 px-2 py-0.5 font-body-sm text-[11px] text-secondary">gilt für alle Kundenmails</span>
      </div>
      {error && <ErrorNote message={error} />}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="min-w-0 space-y-5">
          <fieldset className="space-y-2">
            <legend className={abschnitt}>Kopf des Footers</legend>
            <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Marke im Footer">
              {([
                ["wortmarke", "Wortmarke", "Initial in Markenfarbe + Name"],
                ["logo", "Logo", "das Logo aus Einstellungen → Branding"],
              ] as const).map(([wert, titel, hinweis]) => (
                <label key={wert} className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-outline-variant/50 p-3 has-[:checked]:border-primary dark:border-outline/40">
                  <Radio name="emailFooterMarke" value={wert} checked={stand.emailFooterMarke === wert} onChange={() => setze("emailFooterMarke", wert)} />
                  <span>
                    <span className="block font-body-md text-body-md font-semibold">{titel}</span>
                    <span className="block font-body-sm text-[11.5px] text-secondary">{hinweis}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="space-y-3">
            <p className={abschnitt}>Firmenangaben</p>
            <p className="flex items-start gap-1.5 rounded-lg bg-surface-container-low p-2.5 font-body-sm text-[11.5px] text-secondary dark:bg-surface-variant/20">
              <Icon name="swap_horiz" className="text-[16px]" /> Diese Felder ziehen aus „Firmen-Kopf“ hierher um. Kostenübernahme und Rechnung lesen weiter dieselben Werte.
            </p>
            {feld("companyLegalName", "Firmenname (rechtlich)", "Musterhof Catering GmbH")}
            {feld("companyAddress", "Adresse", "Musterstraße 12 · 10115 Berlin")}
            {d.companyAddress ? (
              <p className="-mt-2 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                {d.companyLat != null && d.bundesland
                  ? `Erkanntes Bundesland: ${BUNDESLAENDER.find((b) => b.code === d.bundesland)?.name ?? d.bundesland} — automatisch aus dieser Adresse ermittelt.`
                  : "Das Bundesland wird beim Speichern automatisch anhand dieser Adresse ermittelt."}
              </p>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              {feld("companyPhone", "Telefon", "+49 30 12345678")}
              {feld("companyEmail", "E-Mail", "info@musterhof-catering.de")}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {feld("companyRegister", "Registereintrag", "Amtsgericht Berlin HRB 123456")}
              {feld("companyVatId", "USt-IdNr.", "DE123456789")}
            </div>
          </div>
          <div>
            <p className={abschnitt}>Links</p>
            {link("emailFooterWebseiteAn", "companyWebsite", "Webseite", "https://www.musterhof-catering.de")}
            {link("emailFooterImpressumAn", "impressumUrl", "Impressum", "https://…/impressum")}
            {link("emailFooterDatenschutzAn", "privacyUrl", "Datenschutz", "https://…/datenschutz")}
          </div>
          <div className="space-y-2">
            <p className={abschnitt}>Eigener Text (optional)</p>
            <p className="font-body-sm text-[11.5px] text-secondary">
              Zum Beispiel Geschäftsführer oder Bankverbindung. Steht unter den Firmenangaben. Fehlt die englische Fassung, nehmen englische Mails den deutschen Text.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold">Deutsch</span>
                <Textarea rows={3} className="resize-y" value={stand.emailFooterText} onChange={(e) => setze("emailFooterText", e.target.value)} />
              </label>
              <label className="block">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold">Englisch</span>
                <Textarea rows={3} className="resize-y" value={stand.emailFooterTextEn} onChange={(e) => setze("emailFooterTextEn", e.target.value)} />
              </label>
            </div>
          </div>
        </div>
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className={abschnitt}>Vorschau</p>
            <div role="group" aria-label="Sprache der Vorschau" className="inline-flex gap-0.5 rounded-lg border border-outline-variant/50 p-0.5">
              {([["de", "Deutsch"], ["en", "Englisch"]] as const).map(([wert, titel]) => (
                <button
                  key={wert}
                  type="button"
                  aria-pressed={lang === wert}
                  onClick={() => setLang(wert)}
                  className={`rounded-md px-3 py-1 font-body-sm text-[12px] font-semibold ${lang === wert ? "bg-surface text-primary shadow-sm" : "text-secondary"}`}
                >
                  {titel}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl bg-surface-container-low p-4 dark:bg-surface-variant/20">
            <div className="mx-auto max-w-[600px] overflow-hidden rounded-xl bg-white">
              <p className="px-6 py-5 font-sans text-[14px] text-[#262119]">
                {lang === "en" ? "Dear Ms Berger, please find attached your offer." : "Guten Tag Frau Berger, anbei Ihr Angebot."}
              </p>
              <FooterVorschau stand={stand} lang={lang} absender={absender} logoUrl={d.companyLogoUrl} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
        {meldung && (
          <span role="status" className="mr-auto inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
            <Icon name="check_circle" className="text-[18px]" /> {meldung}
          </span>
        )}
        <Button type="button" variant="secondary" className="!py-2" disabled={busy !== null} onClick={() => void testmail()}>
          {busy === "testmail" ? "Sendet …" : "Testmail an mich"}
        </Button>
        <Button type="button" className="!py-2" disabled={busy !== null} onClick={() => void speichern()}>
          {busy === "speichern" ? "Speichert …" : "Speichern"}
        </Button>
      </div>
    </div>
  );
}
/** Buchungsstrecke B4 (Migration 603): Begrüßungsvideo — Daten aus derselben Einstellungszeile. */
function BegruessungsvideoForm() {
  const { data, ready, reload } = usePaymentTermsData();
  if (!ready) return <LoadingRows rows={2} />;
  return (
    <BegruessungsvideoEinstellung
      videoUrl={data?.begruessungsvideoUrl}
      vorschauUrl={data?.begruessungsvideoVorschauUrl}
      untertitelUrl={data?.begruessungsvideoUntertitelUrl}
      onChanged={reload}
    />
  );
}
/**
 * Buchungsstrecke D4 (Migration 593): was die Bestätigung nach einer Anfrage zusätzlich sagen darf.
 * Zwei Schalter, beide ab Werk aus, jeder speichert sofort. Die Aussagen selbst prüft der Server
 * (get_public_sofortantwort): „Tag ist frei“ nur ohne zugesagten Vorgang am Datum, der Preisrahmen
 * nur aus mindestens zwei Menüs mit Preis je Gast (Preis-Engine). Erscheint nur zusammen mit dem
 * Ansprechpartner — ohne Namen gibt es keine neue Bestätigung.
 */
export function SofortantwortSchalter() {
  const [tagFrei, setTagFrei] = useState(false);
  const [preisrahmen, setPreisrahmen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { ready, markDirty, markSaved } = usePaymentTermsData((fresh) => {
    setTagFrei(fresh.sofortantwortTagFrei ?? false);
    setPreisrahmen(fresh.sofortantwortPreisrahmen ?? false);
  });
  async function speichere(feld: "sofortantwortTagFrei" | "sofortantwortPreisrahmen", v: boolean) {
    const setze = feld === "sofortantwortTagFrei" ? setTagFrei : setPreisrahmen;
    markDirty();
    setze(v);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", { [feld]: v });
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setze(!v);
    } finally {
      setBusy(false);
    }
  }
  if (!ready) return <LoadingRows rows={1} />;
  return (
    <div className="space-y-3" data-testid="sofortantwort-schalter">
      <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Sofort-Antwort mit Inhalt</p>
      {error && <ErrorNote message={error} />}
      <ToggleSwitch
        checked={tagFrei}
        onChange={(v) => void speichere("sofortantwortTagFrei", v)}
        disabled={busy}
        label="„Tag ist frei“ nennen"
        hint="Der Gast sieht in der Bestätigung „Tag ist frei“, wenn an seinem Wunschtermin (höchstens 24 Monate voraus) kein zugesagter oder bezahlter Vorgang liegt – auch mehrtägige Buchungen zählen. Offene Angebote zählen NICHT: Wartet ein anderer Gast noch auf seine Entscheidung, gilt der Tag trotzdem als frei. Ist der Tag belegt, steht dort nichts – nie „belegt“. Reserviert wird dadurch nichts. Nur einschalten, wenn alle Feiern in MAESTRO eingetragen sind; Buchungen per Telefon oder im Kalender kennt MAESTRO nicht. Bedenke auch: Dritte könnten über Anfragen abtasten, welche Tage bei dir frei sind."
      />
      <ToggleSwitch
        checked={preisrahmen}
        onChange={(v) => void speichere("sofortantwortPreisrahmen", v)}
        disabled={busy}
        label="Preisrahmen der Menüs nennen"
        hint="Die Bestätigung sagt „Unsere Menüs liegen zwischen X € und Y € pro Person, inkl. MwSt.“ – berechnet nur aus deinen Menü-Paketen mit Preis je Gast, die für die angefragte Gästezahl gelten. Pauschalen, Buffets und archivierte Pakete zählen nicht. Gilt für die Gästezahl ein Menü-Paket ohne eigenen Preis je Gast (Preis aus den Positionen) oder gibt es weniger als zwei verschiedene Menüpreise, entfällt der Satz."
      />
      {saved && (
        <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
          <Icon name="check_circle" className="text-[18px]" /> Gespeichert
        </span>
      )}
    </div>
  );
}
/** Buchungsstrecke C2: wählbare Dauer der Terminreservierung (Tage; Server erlaubt 1–30). */
const RESERVIERUNG_TAGE = [3, 5, 7, 10, 14];
const RESERVIERUNG_TAGE_OPTIONEN = RESERVIERUNG_TAGE.map((d) => ({ value: String(d), label: `${d} Tage` }));

export function OeffentlicheAngebotsseiteForm() {
  const [gastAblehnenEnabled, setGastAblehnenEnabled] = useState(true);
  // Punkt 35 (Antoine, 16.09.2026): „In den Einstellungen muss es einen Button geben ob nach
  // Ablauf weiterhin alles buchbar/zahlbar ist oder nicht." Er steht bewusst HIER und nicht im
  // Reiter „Zahlungen": er regelt die Erreichbarkeit der oeffentlichen Angebotsseite, nicht die
  // Zahlungskonditionen (docs/KONZEPT-ANGEBOT-ABLAUF.md § 3.1). Fachlich der Zwilling des
  // Schalters darueber -- der eine regelt, wie der Gast Nein sagen kann, der andere, wie lange
  // er Ja sagen kann. Voreinstellung AUS: das heutige Verhalten, bitgleich.
  const [annahmeNachAblauf, setAnnahmeNachAblauf] = useState(false);
  /**
   * Buchungsstrecke C2 (Antoine, 29.09.2026): „Terminreservierung wird als Schalter gebaut, Vorgabe
   * AUS, auch bei STORIA, bis Antoine bestätigt, dass Termine wirklich geblockt werden.“ Er steht
   * hier, weil er vor allem regelt, was der Gast auf dieser Seite (und in der Angebotsmail) liest.
   */
  const [reservierungAktiv, setReservierungAktiv] = useState(false);
  const [reservierungTage, setReservierungTage] = useState(7);
  // Buchungsstrecke B2 (Migration 591): die neue Angebotsseite — Vorgabe AUS, STORIA per Migration an.
  const [angebotsseiteV2, setAngebotsseiteV2] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { ready, markDirty, markSaved } = usePaymentTermsData((fresh) => {
    setGastAblehnenEnabled(fresh.gastAblehnenEnabled ?? true);
    setAnnahmeNachAblauf(fresh.annahmeNachAblaufEnabled ?? false);
    setReservierungAktiv(fresh.terminreservierungAktiv ?? false);
    setReservierungTage(fresh.terminreservierungTage ?? 7);
    setAngebotsseiteV2(fresh.angebotsseiteV2 ?? false);
  });
  /** C2: Schalter ODER Dauer speichern, einzeln wie die Schalter darüber; bei Fehler zurück. */
  async function speichereReservierung(felder: { terminreservierungAktiv?: boolean; terminreservierungTage?: number }) {
    const vorher = { aktiv: reservierungAktiv, tage: reservierungTage };
    markDirty();
    if (felder.terminreservierungAktiv !== undefined) setReservierungAktiv(felder.terminreservierungAktiv);
    if (felder.terminreservierungTage !== undefined) setReservierungTage(felder.terminreservierungTage);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", felder);
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setReservierungAktiv(vorher.aktiv);
      setReservierungTage(vorher.tage);
    } finally {
      setBusy(false);
    }
  }
  async function speichere(v: boolean) {
    markDirty();
    setGastAblehnenEnabled(v);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", { gastAblehnenEnabled: v });
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setGastAblehnenEnabled(!v);
    } finally {
      setBusy(false);
    }
  }
  async function speichereAblauf(v: boolean) {
    markDirty();
    setAnnahmeNachAblauf(v);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", { annahmeNachAblaufEnabled: v });
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setAnnahmeNachAblauf(!v);
    } finally {
      setBusy(false);
    }
  }
  async function speichereV2(v: boolean) {
    markDirty();
    setAngebotsseiteV2(v);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", { angebotsseiteV2: v });
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setAngebotsseiteV2(!v);
    } finally {
      setBusy(false);
    }
  }
  if (!ready) return <LoadingRows rows={1} />;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Öffentliche Angebotsseite</h3>
        <LiveBadge />
      </div>
      {error && <ErrorNote message={error} />}
      <ToggleSwitch
        checked={gastAblehnenEnabled}
        onChange={(v) => void speichere(v)}
        disabled={busy}
        label="Gäste können Angebote online absagen"
        hint="Zeigt auf der öffentlichen Angebotsseite einen dezenten Absagen-Weg mit optionalem Grund. Der Grund fließt als Lernsignal in MAESTROs Gedächtnis ein (wie bei einer Absage, die du selbst einträgst). Abgeschaltet bleibt nur der klassische Weg: Der Gast meldet sich per Mail oder lässt das Angebot verfallen."
      />
      <ToggleSwitch
        checked={annahmeNachAblauf}
        onChange={(v) => void speichereAblauf(v)}
        disabled={busy}
        label="Angebote nach Ablauf weiter annehmbar"
        hint="Ist das an, kann ein Gast sein Angebot auch nach dem „Gültig bis“-Datum noch annehmen und bezahlen — zu dem Preis, der im Angebot steht. Auf der Angebotsseite steht weiterhin sichtbar, dass die Frist verstrichen ist, und du bekommst bei jeder verspäteten Zusage eine Mail, die das ausdrücklich benennt. Ist das aus, wird das Angebot nach dem Datum geschlossen: Der Gast sieht eine Ablaufmeldung mit deinen Kontaktdaten und kann nicht mehr zusagen. Angebote ohne „Gültig bis“-Datum sind von diesem Schalter nicht betroffen — die laufen nie ab."
      />
      <ToggleSwitch
        checked={reservierungAktiv}
        onChange={(v) => void speichereReservierung({ terminreservierungAktiv: v })}
        disabled={busy}
        label="Termin nach dem Angebotsversand für den Gast freihalten"
        hint="Ist das an, schreibt MAESTRO beim Versand eines Angebots eine Frist an den Vorgang und nennt sie dem Gast in der Angebotsmail, auf der Angebotsseite und in der zweiten und dritten Erinnerung: „Wir halten Ihnen den Termin bis … frei.“ Im Board steht an der Karte „Termin reserviert bis …“, und jeder andere offene Vorgang am selben Tag zeigt einen Hinweis. MAESTRO verhindert kein zweites Angebot für denselben Tag — freihalten musst du den Termin selbst. Nach Ablauf, nach Zusage oder Absage verschwinden alle Hinweise von selbst. Gilt nur für Angebote, die ab jetzt verschickt werden."
      >
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Freihalten für</span>
          <div className="w-32">
            <Select
              testId="terminreservierung-tage"
              ariaLabel="Termin freihalten für"
              value={String(reservierungTage)}
              disabled={busy || !reservierungAktiv}
              onValueChange={(v) => void speichereReservierung({ terminreservierungTage: Number(v) })}
              options={RESERVIERUNG_TAGE.includes(reservierungTage) ? RESERVIERUNG_TAGE_OPTIONEN : [...RESERVIERUNG_TAGE_OPTIONEN, { value: String(reservierungTage), label: `${reservierungTage} Tage` }]}
            />
          </div>
          <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">ab Versand</span>
        </div>
      </ToggleSwitch>
      <ToggleSwitch
        checked={angebotsseiteV2}
        onChange={(v) => void speichereV2(v)}
        disabled={busy}
        label="Neue Angebotsseite"
        hint="Die Angebotsseite führt den Gast: deine persönliche Nachricht aus dem Anschreiben und dein Ansprechpartner oben, Preise pro Person und gesamt gleich groß, echte Google-Bewertungen (nur mit einem live geschalteten Bewertungs-Widget), „Frage an …“ über denselben Nachrichtenweg wie der Änderungswunsch, „Mit Mitentscheider teilen“ und unten auf dem Handy eine Leiste mit Summe. Anpassung und Absage stehen als dezente Links am Ende. Annahme, Anzahlung und Zahlung bleiben genau wie bisher. Aus: die bisherige Seite."
      />
      {saved && (
        <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
          <Icon name="check_circle" className="text-[18px]" /> Gespeichert
        </span>
      )}
    </div>
  );
}
/** Datei -> {mediaType, dataBase64} (ohne data:-Präfix) fürs JSON-Body-Upload. Gleiches Muster
 *  wie pages/chat-public.tsx (dort seitenlokal, kein gemeinsames Paket für diese eine Funktion). */
async function fileToBase64(file: File): Promise<{ mediaType: string; dataBase64: string }> {
  const buf = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return { mediaType: file.type || "application/octet-stream", dataBase64: btoa(binary) };
}

/**
 * EIN BILD NACH PNG WANDELN — im Browser, vor dem Hochladen.
 *
 * Antoine am 21.09.2026 zum Angebots-PDF: **„Inkl. Storia Logo."** Sein Logo liegt als `.webp`
 * vor (`ristorantestoria.de/assets/storia-logo-…webp`) — und **die eingebauten PDF-Bildformate
 * kennen nur PNG und JPG.** Ein webp im Briefkopf wäre kein Bild, sondern ein Fehlschlag.
 *
 * Der Browser kann jedes Format, das er anzeigt, auch als PNG wieder ausgeben. Deshalb passiert
 * die Umwandlung hier und nicht auf dem Server: kein Fremddienst, keine zusätzliche Bibliothek,
 * und der Betreiber lädt einfach die Datei hoch, die er hat.
 *
 * PNG und nicht JPG, weil ein Logo Transparenz haben kann — ein JPG legte einen weissen Kasten
 * um den Schriftzug. Ist die Datei schon ein PNG, wird sie unverändert durchgereicht: eine
 * überflüssige Umwandlung kostet Schärfe.
 */
async function alsPngWandeln(file: File): Promise<File> {
  if (file.type === "image/png") return file;
  const bild = await createImageBitmap(file);
  try {
    const leinwand = document.createElement("canvas");
    leinwand.width = bild.width;
    leinwand.height = bild.height;
    const stift = leinwand.getContext("2d");
    if (!stift) return file; // kein 2D-Kontext (sehr alte Umgebung) — lieber das Original hochladen
    stift.drawImage(bild, 0, 0);
    const blob = await new Promise<Blob | null>((fertig) => leinwand.toBlob(fertig, "image/png"));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".png", { type: "image/png" });
  } finally {
    bild.close();
  }
}

/**
 * A4: Foto des Ansprechpartners — mittig quadratisch zugeschnitten, höchstens 480 px, als JPEG.
 * Fällt etwas aus (kein 2D-Kontext), geht das Original hoch; der Server prüft ohnehin den Typ.
 */
async function alsPortraitWandeln(file: File): Promise<File> {
  const bild = await createImageBitmap(file);
  try {
    const kante = Math.min(bild.width, bild.height);
    const ziel = Math.min(480, kante);
    const leinwand = document.createElement("canvas");
    leinwand.width = ziel;
    leinwand.height = ziel;
    const stift = leinwand.getContext("2d");
    if (!stift) return file;
    stift.drawImage(bild, (bild.width - kante) / 2, (bild.height - kante) / 2, kante, kante, 0, 0, ziel, ziel);
    const blob = await new Promise<Blob | null>((fertig) => leinwand.toBlob(fertig, "image/jpeg", 0.88));
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } finally {
    bild.close();
  }
}

const OFFER_IMAGE_SLOTS = [
  { key: "hero", label: "Hero-Bild (Kopfbereich)", field: "offerHeroImageUrl", focalField: "offerHeroFocalPoint" },
  { key: "option1", label: "Foto Karte 1", field: "offerOptionPhoto1Url", focalField: "offerOptionPhoto1FocalPoint" },
  { key: "option2", label: "Foto Karte 2", field: "offerOptionPhoto2Url", focalField: "offerOptionPhoto2FocalPoint" },
  { key: "option3", label: "Foto Karte 3", field: "offerOptionPhoto3Url", focalField: "offerOptionPhoto3FocalPoint" },
] as const;
// Gleicher Wert wie MAX_CONTENT_BLOCKS in routes/offer-content.ts - hier nur fuer den
// deaktivierten "+ Neuen Bereich"-Knopf, die Route bleibt die eigentliche Grenze.
const MAX_OFFER_CONTENT_BLOCKS = 10;

const BILD_TYPEN = ["image/png", "image/jpeg", "image/webp"];

/** Logo im Tab „Branding“ — die einzige Stelle zum Pflegen (company_logo_url); PDF, Mails und Gastseiten lesen es von dort. */
function BrandingLogo() {
  const { data, ready, reload } = usePaymentTermsData();
  // Erst nach dem Laden zeigen, sonst blitzt kurz „Hochladen“ auf, obwohl schon ein Logo da ist.
  if (!ready) return <div className="h-24 animate-pulse rounded-lg bg-surface-container-low" />;
  return (
    <div>
      <OfferImageSlot slotKey="logo" label="Logo" currentUrl={data?.companyLogoUrl ?? null} onChanged={reload} />
      <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Erscheint im Briefkopf von Angebots-PDF, Mails und Gastseiten. Andere Formate werden beim Hochladen
        automatisch in PNG umgewandelt.
      </p>
    </div>
  );
}

function OfferImageSlot({
  slotKey,
  label,
  currentUrl,
  focalPoint,
  onChanged,
}: {
  slotKey: string;
  label: string;
  currentUrl: string | null;
  /** Nur Fotos der Angebotsseite; beim Logo undefined = kein Ausschnitt. */
  focalPoint?: string | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [ausschnittOffen, setAusschnittOffen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ziehen, setZiehen] = useState(false);
  async function hochladen(file: File | undefined) {
    if (!file || busy) return;
    if (!BILD_TYPEN.includes(file.type)) {
      setError("Bitte ein Bild als PNG, JPG oder WebP.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Nur das Logo muss ein PNG sein (es landet im PDF). Die Fotos der Angebotsseite gehen als
      // WebP hoch (Antoine, 14.10.2026: „Alle neuen Fotos webp komprimieren.“), siehe lib/bild-webp.ts.
      // Das Foto des Ansprechpartners wird quadratisch zugeschnitten und verkleinert: Mails zeigen
      // es mit festen 56 × 56 (Outlook kennt kein object-fit), und ein WebP sähe Outlook gar nicht.
      const datei = slotKey === "logo" ? await alsPngWandeln(file) : await alsWebpWandeln(file);
      const { mediaType, dataBase64 } = await fileToBase64(datei);
      await apiMutate(`/api/settings/offer-images/${slotKey}`, "POST", { mediaType, dataBase64 });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function entfernen() {
    setBusy(true);
    setError(null);
    try {
      await apiMutate(`/api/settings/offer-images/${slotKey}`, "DELETE");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  const istLogo = slotKey === "logo";
  return (
    <div className="space-y-2">
      <span className="block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">{label}</span>
      {/* Ganze Zeile ist Ablagefläche: Bild aus Finder/Explorer hineinziehen = Hochladen. */}
      <div
        className={`flex items-center gap-3 rounded-lg border border-dashed p-2 transition-colors ${ziehen ? "border-primary bg-primary/5" : "border-transparent"}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!ziehen) setZiehen(true);
        }}
        onDragLeave={() => setZiehen(false)}
        onDrop={(e) => {
          e.preventDefault();
          setZiehen(false);
          void hochladen(e.dataTransfer.files?.[0]);
        }}
        data-testid={`offer-image-drop-${slotKey}`}
      >
        <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-outline-variant/50 bg-surface-container-low dark:border-outline/30">
          {currentUrl ? (
            <img
              src={currentUrl}
              alt=""
              className={`h-full w-full ${istLogo ? "object-contain p-1" : "object-cover"}`}
              style={fokusStil(focalPoint)}
              data-testid={`offer-image-preview-${slotKey}`}
            />
          ) : (
            <Icon name="image" className="text-secondary dark:text-secondary-fixed-dim" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <ButtonOptik as="label" variant="secondary" className="inline-flex !py-1.5 cursor-pointer">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              disabled={busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                void hochladen(file);
              }}
              data-testid={`offer-image-input-${slotKey}`}
            />
            {busy ? "Lädt …" : currentUrl ? "Ersetzen" : "Hochladen"}
          </ButtonOptik>
          {currentUrl && (
            <button type="button" className="ml-2 font-body-sm text-body-sm text-error hover:underline" disabled={busy} onClick={() => void entfernen()}>
              Entfernen
            </button>
          )}
          {currentUrl && focalPoint !== undefined && (
            <button
              type="button"
              className="ml-2 font-body-sm text-body-sm text-primary hover:underline"
              disabled={busy}
              onClick={() => setAusschnittOffen(true)}
              data-testid={`offer-image-ausschnitt-${slotKey}`}
            >
              Ausschnitt festlegen
            </button>
          )}
          <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
            {ziehen ? "Loslassen zum Hochladen" : `Oder Bild hierher ziehen.${!currentUrl && !istLogo ? " Ohne eigenes Bild: Standardbild." : ""}`}
          </p>
          {error && <p className="font-body-sm text-[11px] text-error">{error}</p>}
        </div>
      </div>
      {ausschnittOffen && currentUrl && (
        <BildAusschnittDialog
          open
          onClose={() => setAusschnittOffen(false)}
          label={label}
          url={currentUrl}
          wert={focalPoint ?? null}
          istHero={slotKey === "hero"}
          onSpeichern={async (wert) => {
            await apiMutate(`/api/settings/offer-images/${slotKey}/focal-point`, "PUT", { focalPoint: wert });
            onChanged();
          }}
        />
      )}
    </div>
  );
}

interface OfferContentBlockData {
  id: string;
  heading: string;
  body: string;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
}
function OfferContentBlockCard({ block, isFirst, isLast, onChanged }: { block: OfferContentBlockData; isFirst: boolean; isLast: boolean; onChanged: () => void }) {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const [heading, setHeading] = useState(block.heading);
  const [body, setBody] = useState(block.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = heading !== block.heading || body !== block.body;
  async function speichern() {
    setBusy(true);
    setError(null);
    try {
      await apiMutate(`/api/settings/offer-content-blocks/${block.id}`, "PUT", { heading, body });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function move(direction: "up" | "down") {
    setBusy(true);
    setError(null);
    try {
      await apiMutate(`/api/settings/offer-content-blocks/${block.id}/move`, "POST", { direction });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function toggleAktiv() {
    setBusy(true);
    setError(null);
    try {
      await apiMutate(`/api/settings/offer-content-blocks/${block.id}`, "PUT", { isActive: !block.isActive });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function loeschen() {
    if (!(await bestaetigen({ titel: `Bereich „${block.heading}“ wirklich löschen?`, bestaetigen: "Löschen", gefahr: true }))) return;
    setBusy(true);
    setError(null);
    try {
      await apiMutate(`/api/settings/offer-content-blocks/${block.id}`, "DELETE");
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }
  async function bildHochladen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const { mediaType, dataBase64 } = await fileToBase64(await alsWebpWandeln(file));
      await apiMutate(`/api/settings/offer-content-blocks/${block.id}/image`, "POST", { mediaType, dataBase64 });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className={`space-y-3 rounded-xl border border-outline-variant/40 p-4 dark:border-outline/30 ${block.isActive ? "" : "opacity-60"}`}
      data-testid={`offer-content-block-card-${block.id}`}
    >
      {bestaetigungsDialog}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button type="button" className="grid h-8 w-8 place-items-center rounded-lg text-secondary transition-colors hover:bg-surface-container-high disabled:opacity-40 dark:text-secondary-fixed-dim dark:hover:bg-surface-variant/30" disabled={busy || isFirst} onClick={() => void move("up")} aria-label="Nach oben verschieben">
            <Icon name="arrow_upward" className="text-[18px]" />
          </button>
          <button type="button" className="grid h-8 w-8 place-items-center rounded-lg text-secondary transition-colors hover:bg-surface-container-high disabled:opacity-40 dark:text-secondary-fixed-dim dark:hover:bg-surface-variant/30" disabled={busy || isLast} onClick={() => void move("down")} aria-label="Nach unten verschieben">
            <Icon name="arrow_downward" className="text-[18px]" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" className="font-body-sm text-body-sm text-secondary hover:underline dark:text-secondary-fixed-dim" disabled={busy} onClick={() => void toggleAktiv()}>
            {block.isActive ? "Sichtbar — ausblenden" : "Ausgeblendet — einblenden"}
          </button>
          <button type="button" className="font-body-sm text-body-sm text-error hover:underline" disabled={busy} onClick={() => void loeschen()}>
            Löschen
          </button>
        </div>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex h-20 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-outline-variant/50 bg-surface-container-low dark:border-outline/30">
          {block.imageUrl ? <img src={block.imageUrl} alt="" className="h-full w-full object-cover" /> : <Icon name="image" className="text-secondary dark:text-secondary-fixed-dim" />}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <Input
            value={heading}
            maxLength={200}
            placeholder="Überschrift, z. B. „Lernen Sie unser Haus kennen“"
            onChange={(e) => setHeading(e.target.value)}
          />
          <Textarea className="resize-y" rows={3} maxLength={4000} value={body} placeholder="Text" onChange={(e) => setBody(e.target.value)} />
          <div className="flex flex-wrap items-center gap-3">
            <ButtonOptik as="label" variant="secondary" className="inline-flex !py-1.5 cursor-pointer">
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={busy} onChange={(e) => void bildHochladen(e)} />
              {block.imageUrl ? "Bild ersetzen" : "Bild hochladen"}
            </ButtonOptik>
            {dirty && (
              <Button type="button" className="!py-1.5" disabled={busy || !heading.trim() || !body.trim()} onClick={() => void speichern()}>
                {busy ? "Speichert …" : "Speichern"}
              </Button>
            )}
          </div>
          {error && <p className="font-body-sm text-[11px] text-error">{error}</p>}
        </div>
      </div>
    </div>
  );
}
function AngebotsseitenInhalteForm() {
  const { data: settings, ready, reload: reloadSettings } = usePaymentTermsData();
  const { data: blocksRaw, reload: reloadBlocks } = useApi<OfferContentBlockData[]>("/api/settings/offer-content-blocks");
  const blocks = blocksRaw ?? [];
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  function reloadAll() {
    reloadSettings();
    reloadBlocks();
  }
  async function neuerBereich() {
    setCreating(true);
    setError(null);
    try {
      await apiMutate("/api/settings/offer-content-blocks", "POST", { heading: "Neue Überschrift", body: "Neuer Text — hier beschreiben." });
      reloadBlocks();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  }
  if (!ready) return <LoadingRows rows={2} />;
  const d = settings ?? {};
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Bilder der Angebotsseite</h3>
        <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Ersetzt die vier Standardbilder auf der öffentlichen Angebotsseite. JPEG, PNG oder WebP, höchstens 8 MB je Bild.
        </p>
        <ul className="mt-2 list-disc pl-5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          <li>Hero-Bild: Querformat, mindestens 2400 × 1000 Pixel (etwa 12 : 5). Oben und unten wird beschnitten; mit „Ausschnitt festlegen“ bestimmst du, welche Stelle sichtbar bleibt.</li>
          <li>Fotos Karte 1–3: Querformat, mindestens 1200 × 600 Pixel (2 : 1).</li>
          <li>Unter 1 MB lädt die Seite beim Gast am schnellsten, WebP ist dafür am besten.</li>
        </ul>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {OFFER_IMAGE_SLOTS.map((slot) => (
            <OfferImageSlot
              key={slot.key}
              slotKey={slot.key}
              label={slot.label}
              currentUrl={d[slot.field] ?? null}
              focalPoint={d[slot.focalField] ?? null}
              onChanged={reloadAll}
            />
          ))}
        </div>
      </div>
      <div className="border-t border-outline-variant/30 pt-6 dark:border-outline/30">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Eigene Inhaltsbereiche</h3>
        <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Frei anlegbare Bereiche mit Überschrift, Bild und Text — z. B. „Lernen Sie unser Haus kennen“. Erscheinen auf der öffentlichen Angebotsseite nach den Varianten.
        </p>
        {error && <ErrorNote message={error} />}
        <div className="mt-4 space-y-3">
          {blocks.map((b, i) => (
            <OfferContentBlockCard key={b.id} block={b} isFirst={i === 0} isLast={i === blocks.length - 1} onChanged={reloadBlocks} />
          ))}
        </div>
        <Button
          variant="secondary"
          type="button"
          className="mt-4 !py-2"
          disabled={creating || blocks.length >= MAX_OFFER_CONTENT_BLOCKS}
          onClick={() => void neuerBereich()}
        >
          {creating ? "Legt an …" : "+ Neuen Bereich hinzufügen"}
        </Button>
        {blocks.length >= MAX_OFFER_CONTENT_BLOCKS && (
          <p className="mt-1 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">Maximal {MAX_OFFER_CONTENT_BLOCKS} Bereiche.</p>
        )}
      </div>
    </div>
  );
}
function MailFreigabeForm() {
  const [modus, setModus] = useState<"manuell" | "automatisch">("manuell");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { ready, markDirty, markSaved } = usePaymentTermsData((fresh) => {
    setModus(fresh.mailFreigabeModus ?? "manuell");
  });
  async function speichere(automatisch: boolean) {
    const next = automatisch ? "automatisch" : "manuell";
    markDirty();
    setModus(next);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", { mailFreigabeModus: next });
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setModus(automatisch ? "manuell" : "automatisch");
    } finally {
      setBusy(false);
    }
  }
  if (!ready) return <LoadingRows rows={1} />;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Mail-Versand</h3>
        <LiveBadge />
      </div>
      {error && <ErrorNote message={error} />}
      <ToggleSwitch
        checked={modus === "automatisch"}
        onChange={(v) => void speichere(v)}
        disabled={busy}
        label="Mails automatisch versenden"
        hint="Betrifft Angebots-Nachfassen, Rechnungen, Gutschriften, Mahnungen und Erinnerungen — sie gehen mit dem nächsten Versandlauf direkt hinaus, ohne dass jemand freigibt. Ausgeschaltet (Vorgabe): jeder dieser Ausgänge wartet unter „Freigaben“ auf einen Klick, bevor er verschickt wird. Bestätigungen, Kostenübernahme-Mails und der manuelle Rechnungsversand sind davon nicht betroffen — sie gehen immer automatisch raus."
      />
      {saved && (
        <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
          <Icon name="check_circle" className="text-[18px]" /> Gespeichert
        </span>
      )}
    </div>
  );
}
/**
 * Reiter „Benachrichtigungen" (Paket D1, Migration 315).
 *
 * ── STAND PUNKT 36 B/C (23.09.2026) — der Abschnitt darunter gilt nur noch für die Zustellmeldungen ──
 * Die Mails über Anfragen, Zusagen, Absagen, Kostenübernahmen und Zahlungen gehen seit Punkt 36 an
 * die Betriebsadresse UND an jedes Mitglied mit E-Mail (sql/443), jedes Mitglied wählt sie in
 * „Mein Konto" selbst ab (sql/493, Antoine 23.09.2026: „pro Nutzer wählbar"). Nur die beiden
 * Zustell-Schalter (Bounce/Erfolg) gehen weiterhin ausschließlich an `company_email`.
 *
 * ── Die eine Aussage, ohne die der Reiter irrefuehrend waere ───────────────────────────────────
 * Alle drei Benachrichtigungen gehen an die BETRIEBSADRESSE (`tenant_settings.company_email`) —
 * nicht an das persoenliche Postfach des Mitglieds, das den Schalter umlegt. Wer das nicht weiss,
 * schaltet „Mail-Bounce" ein und wartet auf eine Nachricht, die woanders ankommt. Deshalb steht
 * die Adresse hier gross im Kopf des Reiters, mit Namen, und ohne hinterlegte Adresse sagt der
 * Reiter ausdruecklich, dass die Schalter dann wirkungslos sind (das ist keine Vermutung: der
 * Server verschickt ohne `company_email` nichts, belegt in
 * `apps/api/test/benachrichtigungs-schalter.test.ts`).
 *
 * Warum nicht an die einzelnen Mitglieder: die beiden ausloesenden Pfade laufen ohne
 * Nutzerkontext — der Anfrage-Intake unauthentifiziert als `maestro_public`, der Zustellstatus
 * kontextlos als `maestro_webhook`. Beide Rollen haben keinen Zugriff auf `tenant_users`, und
 * `tenant_users.email` ist ausserdem nur bei Einladung befuellt. Ein Versand an alle Mitglieder
 * braeuchte zusaetzlich eine Vorliebe je Person; ohne die waere er Spam.
 *
 * ── Rollen ────────────────────────────────────────────────────────────────────────────────────
 * Schreiben nur Owner/Admin — dieselbe Regel wie bei JEDEM anderen schreibenden
 * Settings-Endpunkt (`PUT /api/settings/payment-terms` und seine acht Geschwister pruefen alle
 * `ctx.role`). Lesen darf jedes Mitglied; die Anzeige ist kein Geheimnis. Der Schalter ist fuer
 * Staff sichtbar, aber gesperrt — mit demselben Hinweistext wie die Modul-Kacheln.
 */
/** Buchungsstrecke D1: wählbare Schwellen des Tempo-Alarms (Minuten; Server erlaubt 15–1440). */
const TEMPO_SCHWELLEN = [30, 60, 90, 120, 180, 240];
/** Punkt 131: Auswahl „ab … Personen“ (Vorgabe 50). */
const GROSSE_GRUPPE_SCHWELLEN = [20, 30, 40, 50, 60, 80, 100];
type GrosseGruppe = { grosseGruppeAktiv: boolean; grosseGruppeAb: number };
const TEMPO_SCHWELLEN_OPTIONEN = TEMPO_SCHWELLEN.map((m) => ({
  value: String(m),
  label: m < 60 ? `${m} Minuten` : m % 60 === 0 ? `${m / 60} ${m === 60 ? "Stunde" : "Stunden"}` : `${Math.floor(m / 60)},5 Stunden`,
}));

export function BenachrichtigungenTab({ canEdit }: { canEdit: boolean }) {
  const [neueAnfrage, setNeueAnfrage] = useState(true);
  const [bounce, setBounce] = useState(true);
  const [erfolg, setErfolg] = useState(false);
  /** Buchungsstrecke D1: Tempo-Alarm (Vorgabe an, 120 Minuten). */
  const [tempoAktiv, setTempoAktiv] = useState(true);
  const [tempoMinuten, setTempoMinuten] = useState(120);
  const [grosse, setGrosse] = useState<GrosseGruppe>({ grosseGruppeAktiv: true, grosseGruppeAb: 50 });
  const [companyEmail, setCompanyEmail] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Punkt 82: die Liste der Arten und die gespeicherte Wahl — beide vom Server. */
  const [mailArten, setMailArten] = useState<{ schluessel: string; bezeichnung: string; anlass: string; erklaerung?: string; abWerk: boolean }[]>([]);
  const [mailKopien, setMailKopien] = useState<Record<string, boolean>>({});
  /** Punkt 82: die bearbeitbaren Mailtexte — Beschreibung samt Werkstext vom Server, dazu das,
   *  was der Betrieb selbst geschrieben hat. */
  const [vorlagen, setVorlagen] = useState<VorlagenBeschreibung[]>([]);
  const [platzhalter, setPlatzhalter] = useState<PlatzhalterBeschreibung[]>([]);
  const [eigeneVorlagen, setEigeneVorlagen] = useState<Record<string, GespeicherteMailVorlage>>({});
  const { ready, markDirty, markSaved } = usePaymentTermsData((fresh) => {
    setNeueAnfrage(fresh.benachrichtigungNeueAnfrageEnabled ?? true);
    setBounce(fresh.benachrichtigungMailBounceEnabled ?? true);
    setErfolg(fresh.benachrichtigungMailErfolgEnabled ?? false);
    setTempoAktiv(fresh.tempoAlarmAktiv ?? true);
    setTempoMinuten(fresh.tempoAlarmMinuten ?? 120);
    setGrosse({
      grosseGruppeAktiv: fresh.grosseGruppeAktiv ?? true,
      grosseGruppeAb: fresh.grosseGruppeAb ?? 50,
    });
    setCompanyEmail(fresh.companyEmail ?? null);
    setMailArten(fresh.mailArten ?? []);
    setMailKopien(fresh.mailKopien ?? {});
    setVorlagen(fresh.mailVorlagenListe ?? []);
    setPlatzhalter(fresh.platzhalter ?? []);
    setEigeneVorlagen(fresh.mailVorlagen ?? {});
  });

  /**
   * Punkt 82: EINEN Kopie-Schalter umlegen.
   *
   * Geschickt wird nur dieser eine — der Server legt ihn über die gespeicherte Wahl, statt sie zu
   * ersetzen. Würde die Oberfläche das ganze Objekt schicken, hinge die Richtigkeit daran, dass
   * ihr Stand aktuell ist; ein zweiter geöffneter Reiter würde dann die Wahl des ersten
   * zurückdrehen.
   *
   * „Nicht gewählt" heißt NICHT „aus": solange der Betrieb nichts angefasst hat, zeigt der
   * Schalter die Werkseinstellung der Art (`abWerk`) — bei der Eingangsbestätigung und beim
   * Angebot ist das „an", weil es heute schon so ist.
   */
  function istAn(art: { schluessel: string; abWerk: boolean }): boolean {
    const wahl = mailKopien[art.schluessel];
    return typeof wahl === "boolean" ? wahl : art.abWerk;
  }

  async function speichereKopie(schluessel: string, wert: boolean) {
    const vorher = mailKopien;
    markDirty();
    setMailKopien({ ...mailKopien, [schluessel]: wert });
    setBusy(`kopie:${schluessel}`);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", { mailKopien: { [schluessel]: wert } });
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Optimistische Anzeige zurücknehmen — wie bei den drei Schaltern darüber.
      setMailKopien(vorher);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Punkt 82: EINE Mailvorlage speichern.
   *
   * Es geht nur die eine hinaus, die gerade bearbeitet wurde — der Server legt sie über den
   * Bestand. Würde die Oberfläche alle schicken, überschriebe ein zweiter offener Reiter die
   * Arbeit des ersten.
   */
  async function speichereVorlage(schluessel: string, teile: VorlagenTeile) {
    markDirty();
    await apiMutate("/api/settings/payment-terms", "PUT", { mailVorlagen: { [schluessel]: teile } });
    // Feldweise wie der Server (Rest R5): leer geschickt heißt gelöscht.
    setEigeneVorlagen((v) => {
      const eintrag: GespeicherteMailVorlage = { ...(v[schluessel] ?? {}) };
      for (const [feld, wert] of Object.entries(teile) as [keyof GespeicherteMailVorlage, string][]) {
        if (wert.trim()) eintrag[feld] = wert;
        else delete eintrag[feld];
      }
      return { ...v, [schluessel]: eintrag };
    });
    markSaved();
  }

  async function speichere(feld: "benachrichtigungNeueAnfrageEnabled" | "benachrichtigungMailBounceEnabled" | "benachrichtigungMailErfolgEnabled", wert: boolean) {
    const setzer = {
      benachrichtigungNeueAnfrageEnabled: setNeueAnfrage,
      benachrichtigungMailBounceEnabled: setBounce,
      benachrichtigungMailErfolgEnabled: setErfolg,
    }[feld];
    markDirty();
    setzer(wert);
    setBusy(feld);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", { [feld]: wert });
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Optimistische Anzeige zuruecknehmen: ein Schalter, der nach einem Fehlschlag in der
      // neuen Stellung stehen bleibt, behauptet eine Einstellung, die der Server nicht hat.
      setzer(!wert);
    } finally {
      setBusy(null);
    }
  }

  /**
   * Buchungsstrecke D1: Tempo-Alarm speichern — Schalter ODER Schwelle, einzeln wie die Schalter
   * darüber. Bei Fehler wird die Anzeige zurückgenommen.
   */
  async function speichereTempo(felder: { tempoAlarmAktiv?: boolean; tempoAlarmMinuten?: number }) {
    const vorher = { aktiv: tempoAktiv, minuten: tempoMinuten };
    markDirty();
    if (felder.tempoAlarmAktiv !== undefined) setTempoAktiv(felder.tempoAlarmAktiv);
    if (felder.tempoAlarmMinuten !== undefined) setTempoMinuten(felder.tempoAlarmMinuten);
    setBusy("tempo");
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", felder);
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setTempoAktiv(vorher.aktiv);
      setTempoMinuten(vorher.minuten);
    } finally {
      setBusy(null);
    }
  }

  /** Punkt 131: Schild „Große Gruppe“ speichern — wie `speichereTempo`, Fehler nimmt die Anzeige zurück. */
  async function speichereGrosseGruppe(felder: Partial<GrosseGruppe>) {
    const vorher = grosse;
    markDirty();
    setGrosse({ ...grosse, ...felder });
    setBusy("grosse-gruppe");
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", felder);
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setGrosse(vorher);
    } finally {
      setBusy(null);
    }
  }

  if (!ready) return <LoadingRows rows={3} />;
  return (
    <div className="grid grid-cols-1 gap-gutter lg:grid-cols-3">
      <Card className="p-6 lg:col-span-2">
        <div className="mb-6 flex items-center gap-2 border-b border-outline-variant/30 pb-4 dark:border-outline/30">
          <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Benachrichtigungen</h3>
          <LiveBadge />
        </div>
        <div
          data-testid="benachrichtigungen-empfaenger"
          className={`mb-6 flex items-start gap-2.5 rounded-lg border px-4 py-3 ${
            companyEmail
              ? "border-outline-variant/40 bg-surface-container-low dark:border-outline/30 dark:bg-surface-variant/10"
              : "border-error/40 bg-error/10"
          }`}
        >
          <Icon name={companyEmail ? "mail" : "warning"} className={`mt-0.5 text-[18px] ${companyEmail ? "text-secondary" : "text-error"}`} />
          <div className="min-w-0">
            {companyEmail ? (
              <>
                <p className="font-body-md text-body-md text-on-background dark:text-inverse-on-surface">
                  Die Mails über Anfragen, Zusagen, Absagen, Kostenübernahmen und Zahlungen gehen an die{" "}
                  <strong>Betriebsadresse</strong> <span className="font-mono">{companyEmail}</span> und an jedes
                  Teammitglied mit hinterlegter E-Mail-Adresse.
                </p>
                <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  Jedes Mitglied wählt unter{" "}
                  <Link to="/mein-konto?tab=benachrichtigungen" className="text-primary hover:underline">
                    Mein Konto → Benachrichtigungen
                  </Link>{" "}
                  selbst, welche davon es bekommt; die Betriebsadresse bekommt immer alle. Die Schalter hier gelten für
                  den ganzen Betrieb. Die Adresse ändern Sie unter{" "}
                  <Link to="/einstellungen" className="text-primary hover:underline">
                    Betrieb → Firmen-Kopf
                  </Link>
                  .
                </p>
              </>
            ) : (
              <>
                <p className="font-body-md text-body-md font-semibold text-error">
                  Keine Betriebsadresse hinterlegt.
                </p>
                <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  Die Mails gehen dann nur an die Teammitglieder mit hinterlegter E-Mail-Adresse — und an niemanden, der
                  sie unter Mein Konto abgewählt hat. Tragen Sie die Betriebsadresse unter{" "}
                  <Link to="/einstellungen" className="text-primary hover:underline">
                    Betrieb → Firmen-Kopf
                  </Link>{" "}
                  ein, dann bekommt sie jede dieser Mails.
                </p>
              </>
            )}
          </div>
        </div>
        {error && <ErrorNote message={error} />}
        <div className="space-y-4">
          <ToggleSwitch
            checked={neueAnfrage}
            onChange={(v) => void speichere("benachrichtigungNeueAnfrageEnabled", v)}
            disabled={!canEdit || busy !== null}
            disabledHint={!canEdit ? "Nur Inhaber dürfen Benachrichtigungen ändern." : undefined}
            label="Neue Anfrage"
            hint="Sobald über Formular, Widget oder Chat eine neue Anfrage eingeht, meldet MAESTRO sie an die Betriebsadresse und an jedes Teammitglied, das „Neue Anfrage“ nicht abgewählt hat. Das ist eine Meldung über den Eingang, nicht die Mail, die Ihr Gast bekommt. Ausgeschaltet: die Anfrage landet weiterhin im Dashboard unter „Anfragen“ und im Posteingang, nur ohne Mail an Sie – es geht nichts verloren. Die Eingangsbestätigung an den Gast ist davon nicht betroffen, die geht immer raus; ob Sie davon eine Kopie mitlesen, entscheidet der gleichnamige Schalter weiter unten."
          />
          <ToggleSwitch
            checked={bounce}
            onChange={(v) => void speichere("benachrichtigungMailBounceEnabled", v)}
            disabled={!canEdit || busy !== null}
            disabledHint={!canEdit ? "Nur Inhaber dürfen Benachrichtigungen ändern." : undefined}
            label="E-Mail kam zurück (Bounce oder Beschwerde)"
            hint="Kommt eine verschickte Mail zurück – falsche Adresse, volles Postfach – oder markiert ein Empfänger sie als Spam, meldet MAESTRO das mit Empfängeradresse und Grund. Sie können die Adresse dann korrigieren, statt vergeblich erneut zu senden. Betroffen sind alle Mails an Gäste, nicht nur eine Art. Ausgeschaltet: MAESTRO merkt sich den Rückläufer trotzdem, sagt Ihnen aber nichts – Sie finden ihn dann nur im Mail-Protokoll."
          >
            {/* Ehrlichkeit vor Funktionsversprechen: der Zustellstatus hat derzeit keine lebende
                Quelle (Resend-Webhook nach dem Wechsel auf Cloudflare, 20.08.2026). Der Schalter
                wird gespeichert und ist verdrahtet — er kann heute nur nichts melden. Das gehoert
                in die Oberflaeche, nicht nur in einen Code-Kommentar. */}
            <p className="mt-2 flex items-start gap-1.5 rounded-md bg-[#F0EAE0] px-2.5 py-1.5 font-body-sm text-body-sm text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
              <Icon name="info" className="mt-0.5 text-[15px]" />
              <span>
                Noch ohne Wirkung: MAESTRO bekommt vom Mail-Versand derzeit keine Zustellmeldungen zurück. Ein
                sofort erkannter Fehlschlag wird Ihnen weiterhin direkt beim Senden angezeigt.
              </span>
            </p>
          </ToggleSwitch>
          <ToggleSwitch
            checked={erfolg}
            onChange={(v) => void speichere("benachrichtigungMailErfolgEnabled", v)}
            disabled={!canEdit || busy !== null}
            disabledHint={!canEdit ? "Nur Inhaber dürfen Benachrichtigungen ändern." : undefined}
            label="E-Mail wurde zugestellt"
            hint="Das Gegenstück zum Schalter darüber: eine kurze Bestätigung für jede Mail, die beim Empfänger angekommen ist. Sinnvoll, wenn Sie einer Adresse gerade nicht trauen – im Normalbetrieb sind das sehr viele Mails, deshalb ist der Schalter ab Werk aus. Ausgeschaltet erfahren Sie weiterhin, wenn etwas zurückkommt; nur der Erfolgsfall bleibt still."
          >
            <p className="mt-2 flex items-start gap-1.5 rounded-md bg-[#F0EAE0] px-2.5 py-1.5 font-body-sm text-body-sm text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
              <Icon name="info" className="mt-0.5 text-[15px]" />
              <span>Noch ohne Wirkung: MAESTRO bekommt vom Mail-Versand derzeit keine Zustellmeldungen zurück.</span>
            </p>
          </ToggleSwitch>
          {/* Buchungsstrecke D1 (Antoine 29.09.2026: „2 Stunden, nur in Öffnungszeiten, an alle mit
              Benachrichtigung „Anfragen““). */}
          <ToggleSwitch
            checked={tempoAktiv}
            onChange={(v) => void speichereTempo({ tempoAlarmAktiv: v })}
            disabled={!canEdit || busy !== null}
            disabledHint={!canEdit ? "Nur Inhaber dürfen Benachrichtigungen ändern." : undefined}
            label="Tempo-Alarm: Anfrage wartet"
            hint="Liegt eine Anfrage innerhalb Ihrer Öffnungszeiten länger als eingestellt ohne persönliche Antwort, meldet MAESTRO das einmal per Mail an alle, die „Neue Anfrage“ nicht abgewählt haben, und per Telegram, und markiert die Karte im Board rot. Die automatische Eingangsbestätigung zählt nicht als Antwort. Ohne hinterlegte Öffnungszeiten gilt 8 bis 20 Uhr."
          >
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Melden nach</span>
              <div className="w-40">
                <Select
                  testId="tempo-alarm-schwelle"
                  ariaLabel="Tempo-Alarm melden nach"
                  value={String(tempoMinuten)}
                  disabled={!canEdit || busy !== null || !tempoAktiv}
                  onValueChange={(v) => void speichereTempo({ tempoAlarmMinuten: Number(v) })}
                  options={TEMPO_SCHWELLEN.includes(tempoMinuten) ? TEMPO_SCHWELLEN_OPTIONEN : [...TEMPO_SCHWELLEN_OPTIONEN, { value: String(tempoMinuten), label: `${tempoMinuten} Minuten` }]}
                />
              </div>
              <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">in den Öffnungszeiten</span>
            </div>
          </ToggleSwitch>
          {/* Punkt 131 (docs/PLAN-GROSSGRUPPEN-MELDUNG.md): nur das Schild auf der Tafel, keine
              eigene Mail. Antoine 05.10.2026: „eine E-Mail reicht keine weiteren E-Mails.“ */}
          <ToggleSwitch
            checked={grosse.grosseGruppeAktiv}
            onChange={(v) => void speichereGrosseGruppe({ grosseGruppeAktiv: v })}
            disabled={!canEdit || busy !== null}
            disabledHint={!canEdit ? "Nur Inhaber dürfen Benachrichtigungen ändern." : undefined}
            label="Große Gruppen kennzeichnen"
            hint="Anfragen ab der eingestellten Personenzahl tragen auf der Anfragen-Tafel das Schild „Große Gruppe“. Eine eigene Mail gibt es dafür nicht – die Anfrage-Mail kommt wie gewohnt."
          >
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">ab</span>
              <div className="w-40">
                <Select
                  testId="grosse-gruppe-schwelle"
                  ariaLabel="Große Gruppe ab Personen"
                  value={String(grosse.grosseGruppeAb)}
                  disabled={!canEdit || busy !== null || !grosse.grosseGruppeAktiv}
                  onValueChange={(v) => void speichereGrosseGruppe({ grosseGruppeAb: Number(v) })}
                  options={[...new Set([...GROSSE_GRUPPE_SCHWELLEN, grosse.grosseGruppeAb])]
                    .sort((a, b) => a - b)
                    .map((n) => ({ value: String(n), label: `${n} Personen` }))}
                />
              </div>
            </div>
          </ToggleSwitch>
        </div>

        {/*
          PUNKT 82 — KOPIEN DER GAST-MAILS. Antoine am 20.09.2026: „Das gilt für alle Mails die an
          den Gast gehen, die gehen im Kopie an den Betreiber. […] Pro Mail in Kopie einen Toggle."

          DER UNTERSCHIED ZU DEN DREI SCHALTERN DARÜBER, und er steht auch im Text: dort meldet
          MAESTRO dem Betrieb ETWAS ÜBER einen Vorgang. Hier bekommt er die Mail, die der Gast
          bekommt — Wort für Wort dieselbe. Deshalb ein eigener Abschnitt und keine vierte Zeile
          in derselben Liste.

          DIE LISTE KOMMT VOM SERVER (`mailArten`) und wird hier nicht gepflegt — sonst gäbe es
          die Bezeichnungen zweimal, und sie liefen auseinander, sobald eine Art dazukommt.
        */}
        {/*
          PUNKT 82 — DIE INHALTE DER MAILS. Antoine am 22.09.2026, mit Bildschirmfoto dieses
          Reiters: „Was hier fehlt sind die Inhalte der einzelnen Mails. Hatten wir ausführlich
          besprochen."

          Der Abschnitt steht VOR den Kopie-Schaltern, weil er die wichtigere Frage beantwortet:
          erst was drinsteht, dann wer mitliest.
        */}
        {vorlagen.length > 0 && (
          <div className="mt-8 border-t border-outline-variant/30 pt-6 dark:border-outline/30">
            <h4 className="font-title-md text-title-md text-on-background dark:text-inverse-on-surface">
              Was in den Mails steht
            </h4>
            <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Betreff und Text können Sie selbst schreiben. Die <strong>Platzhalter</strong> darunter
              setzen Sie per Klick an die Stelle, an der Sie gerade tippen — MAESTRO füllt sie beim
              Versand mit den Daten des Vorgangs. <strong>Ab Werk ist alles schon ausgefüllt</strong>;
              wer nichts ändert, bekommt trotzdem eine vollständige Mail.
            </p>
            {/* Punkt 82 (Rest): gruppiert nach Empfänger — die Liste kommt vom Server. */}
            {(
              [
                ["betrieb", "Mails an Sie"],
                ["gast", "Mails an Ihre Gäste"],
              ] as const
            ).map(([empfaenger, titel]) => {
              const dieser = vorlagen.filter((v) => v.empfaenger === empfaenger);
              if (dieser.length === 0) return null;
              return (
                <div key={empfaenger} className="mt-4">
                  <p className="font-label-md text-[11px] uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim">{titel}</p>
                  <div className="mt-2 space-y-3">
                    {dieser.map((v) => (
                      <MailVorlagenEditor
                        key={v.schluessel}
                        vorlage={v}
                        platzhalter={platzhalter}
                        gespeichert={eigeneVorlagen[v.schluessel]}
                        canEdit={canEdit}
                        onSave={(teile) => speichereVorlage(v.schluessel, teile)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {mailArten.length > 0 && (
          <div className="mt-8 border-t border-outline-variant/30 pt-6 dark:border-outline/30">
            <h4 className="font-title-md text-title-md text-on-background dark:text-inverse-on-surface">
              Kopien der Mails an Gäste
            </h4>
            <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Hier bekommen Sie <strong>dieselbe Mail, die Ihr Gast bekommt</strong> — als Kopie an die
              Betriebsadresse. So sehen Sie schwarz auf weiß, was hinausgegangen ist. Diese Schalter ändern
              <strong> nie</strong>, ob die Mail an den Gast geht: sie geht in jedem Fall raus.
            </p>
            <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Beim Angebot, beim freien Text und beim erneuten Senden können Sie eine einzelne Mail im
              Senden-Fenster trotzdem abwählen — das Häkchen dort behält das letzte Wort.
            </p>
            <div className="mt-4 space-y-4">
              {mailArten.map((art) => (
                <ToggleSwitch
                  key={art.schluessel}
                  checked={istAn(art)}
                  onChange={(v) => void speichereKopie(art.schluessel, v)}
                  disabled={!canEdit || busy !== null}
                  disabledHint={!canEdit ? "Nur Inhaber dürfen Benachrichtigungen ändern." : undefined}
                  label={art.bezeichnung}
                  /* Antoine, 22.09.2026: „Und etwas ausführlicheren Erklärungen, welcher Toggle
                     was macht." Der Anlass sagt WANN, die Erklärung sagt WAS DRINSTEHT und WAS
                     PASSIERT, WENN DER SCHALTER AUS IST — die Frage, die ein Schalter sonst
                     offenlässt. Beides kommt vom Server, damit die Texte nicht zweimal existieren
                     und auseinanderlaufen. */
                  hint={art.erklaerung ? `${art.anlass} ${art.erklaerung}` : art.anlass}
                />
              ))}
            </div>
          </div>
        )}

        {saved && (
          <p className="mt-4 inline-flex items-center gap-1 font-body-sm text-body-sm text-primary">
            <Icon name="check_circle" className="text-[18px]" /> Gespeichert
          </p>
        )}
      </Card>
    </div>
  );
}
/**
 * Reiter „Absenderdomain" — "Bring your own domain", seit 02.09.2026 ueber Brevo (urspruenglich
 * Paket C1/SES, Migration 316; SES wurde nie aktiviert). Provider-neutral: die anzuzeigenden
 * DNS-Eintraege kommen als generische Liste vom Backend (packages/db/sql/353), unabhaengig davon,
 * welcher Provider gerade aktiv ist.
 *
 * ── Rollen ────────────────────────────────────────────────────────────────────────────────────
 * Schreiben (Domain eintragen, Status pruefen) nur Owner/Admin — dieselbe Regel wie bei jedem
 * anderen schreibenden Settings-Endpunkt. Lesen darf jedes Mitglied.
 */
interface AbsenderdomainDnsEintrag {
  label: string;
  hostName: string;
  recordType: string;
  value: string;
  verified: boolean;
}
/**
 * Der Eigentumsnachweis (Migration 354). Zwei GETRENNTE Zustaende, die bewusst nicht in einen
 * einzigen Haken fallen:
 *   nachweis.status === 'freigegeben'  -> „Ihnen gehoert diese Domain" (MAESTROs eigene Pruefung)
 *   sendingDomainStatus === 'verified' -> „der Provider kann darueber zustellen" (DNS/DKIM)
 * Erst beides zusammen ergibt einen eigenen Absender. Ein Haken fuer beides waere genau die
 * Vermischung, die die Uebernahme fremder Domains ermoeglicht hat.
 */
interface AbsenderdomainNachweis {
  domain: string;
  status: "beansprucht" | "freigegeben";
  nachweisArt: string | null;
  nachweisAm: string | null;
  challengeEmpfaenger: string | null;
  challengeVersendetAm: string | null;
  challengeVersuche: number;
}
interface AbsenderdomainDaten {
  sendingDomain: string | null;
  sendingDomainStatus: "pending" | "verified" | "failed";
  sendingDomainDnsRecords: AbsenderdomainDnsEintrag[] | null;
  /** Die Freigabe (`status === 'freigegeben'`), falls es eine gibt. */
  freigabe?: AbsenderdomainNachweis | null;
  /**
   * Der offene Anspruch (`status === 'beansprucht'`), falls es einen gibt — UNABHAENGIG davon,
   * ob daneben schon eine Freigabe steht.
   *
   * Genau das fehlte (Review-Befund 03.09.2026): das Backend lieferte nur EINE Zeile, freigegeben
   * zuerst. Beim Domainwechsel im laufenden Betrieb (X freigegeben, Y beansprucht) gewann immer
   * X, `offenerAnspruch` wurde hier `null`, und das Code-Feld erschien NIE. Die Nachweis-Mail
   * fuer Y war raus, der Code nirgends eintragbar; der einzige Ausweg waere „Domain entfernen"
   * gewesen — also der sofortige Verlust der laufenden Absenderdomain.
   */
  offenerAnspruch?: AbsenderdomainNachweis | null;
  /** Rueckwaertsvertraeglich: Freigabe zuerst, sonst der juengste Anspruch. */
  nachweis: AbsenderdomainNachweis | null;
  /**
   * Warum die eingetragene `sendingDomain` NICHT (mehr) beanspruchbar ist — `null`/fehlend, wenn
   * sie es ist. Kommt vom Server, weil nur dort die Umgebung bekannt ist (Migration 354,
   * `routes/settings.ts#sendingDomainSperre`).
   *
   * Ohne dieses Feld log die Oberflaeche in genau einem Bestandsfall (Review-Befund 03.09.2026,
   * K3): steht in `tenant_settings.sending_domain` die PLATTFORM-Absenderdomain selbst, sagte
   * sie „Ihre Post geht von der gemeinsamen Standardadresse raus, nicht unter
   * events-storia.de" — die gemeinsame Standardadresse IST aber `info@events-storia.de`. Und
   * ihr Rat „Tragen Sie sie oben erneut ein" endete in 422 („Diese Domain kann nicht als eigene
   * Absenderdomain hinterlegt werden"): der einzige bekannte Fall, in dem die Oberflaeche zu
   * etwas riet, was sie nicht kann.
   */
  sendingDomainSperre?: "plattform_absender" | "gesperrt" | null;
}

/**
 * Rollenadressen nach RFC 2142, an die ein Nachweis-Code gehen darf.
 *
 * Die Wahrheit steht in `apps/api/src/lib/absenderdomain.ts#NACHWEIS_ROLLENADRESSEN` und wird
 * serverseitig als Zod-Enum durchgesetzt; diese Liste ist nur die Auswahl in der Oberflaeche.
 * Sie existiert, weil die 502-Meldung des Servers ausdruecklich auf sie verweist („ggf. eine
 * andere Rollenadresse waehlen") — bis hierher gab es dafuer aber kein Bedienelement, und eine
 * Domain ohne `info@`-Postfach war ueber die Oberflaeche gar nicht onboardbar (Review-Befund
 * 03.09.2026).
 */
const NACHWEIS_ROLLEN = ["info", "postmaster", "hostmaster", "webmaster", "admin", "abuse"] as const;

/**
 * Ein einzelner DNS-Eintragswert (Host oder Ziel) mit Kopier-Knopf.
 *
 * Der Wert wird UMGEBROCHEN, nicht abgeschnitten (Antoine 03.09.2026: erst "abgeschnitten mit
 * ...", dann "text immer noch abgeschnitten"). Diese Werte muessen 1:1 beim DNS-Provider
 * eingetragen werden — wer sie nicht vollstaendig sieht, kann nicht pruefen, ob er das Richtige
 * eingefuegt hat. `break-all`, weil DNS-Werte keine Leerzeichen zum Umbrechen haben. Der
 * Kopier-Knopf bleibt als bequemer Weg, der Tooltip als Zusatz.
 */
function DnsWertMitKopieren({ wert }: { wert: string }) {
  const [kopiert, setKopiert] = useState(false);
  useKopiertZuruecksetzen(kopiert, setKopiert, 2000);
  async function kopieren() {
    try {
      await navigator.clipboard.writeText(wert);
      setKopiert(true);
    } catch {
      // Kein Clipboard-Zugriff (z. B. unsicherer Kontext) — Wert bleibt per Markieren erreichbar.
    }
  }
  return (
    <span className="flex min-w-0 items-start gap-1">
      <span className="min-w-0 break-all" title={wert}>
        {wert}
      </span>
      <button
        type="button"
        onClick={() => void kopieren()}
        title={kopiert ? "Kopiert" : "Wert kopieren"}
        aria-label={kopiert ? "Kopiert" : "Wert kopieren"}
        className="shrink-0 rounded p-0.5 text-secondary hover:bg-surface-container hover:text-on-background dark:hover:bg-surface-variant/20 dark:hover:text-inverse-on-surface"
      >
        <Icon name={kopiert ? "check" : "content_copy"} className="text-[15px]" />
      </button>
    </span>
  );
}

export function AbsenderdomainTab({ canEdit }: { canEdit: boolean }) {
  const { data, loading, error: ladeFehler, reload } = useApi<AbsenderdomainDaten | null>("/api/settings/sending-domain");
  const [domainEingabe, setDomainEingabe] = useState("");
  const [rolleEingabe, setRolleEingabe] = useState<(typeof NACHWEIS_ROLLEN)[number]>("info");
  const [codeEingabe, setCodeEingabe] = useState("");
  const [busy, setBusy] = useState<"registrieren" | "pruefen" | "bestaetigen" | "aktivieren" | "entfernen" | null>(null);
  const [aktionsFehler, setAktionsFehler] = useState<string | null>(null);

  async function domainRegistrieren(e: FormEvent) {
    e.preventDefault();
    const domain = domainEingabe.trim();
    if (!domain) return;
    setBusy("registrieren");
    setAktionsFehler(null);
    try {
      // `nachweisAdresse` nur mitschicken, wenn sie von der Vorgabe abweicht — der Server setzt
      // sonst selbst `info` ein, und der Normalfall bleibt eine Anfrage mit einem Feld.
      await apiMutate("/api/settings/sending-domain", "PUT", rolleEingabe === "info" ? { domain } : { domain, nachweisAdresse: rolleEingabe });
      setDomainEingabe("");
      setCodeEingabe("");
      await reload();
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Registrierung beim Versand-Provider WIEDERHOLEN — der Ausweg aus „Nachweis erbracht, der
   * Provider war gerade nicht erreichbar".
   *
   * Ohne diesen Knopf war der Zustand eine Sackgasse: „Jetzt pruefen" prueft eine andere Domain,
   * ein erneutes Eintragen tat nichts, und „Domain entfernen" haette die noch laufende
   * Absenderdomain gekostet (Review-Befund 03.09.2026).
   */
  async function registrierungWiederholen() {
    setBusy("aktivieren");
    setAktionsFehler(null);
    try {
      await apiMutate("/api/settings/sending-domain/aktivieren", "POST");
      await reload();
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  /** Zweiter Schritt: der Code aus der Nachweis-Mail. Erst er erzeugt die Freigabe. */
  async function nachweisBestaetigen(e: FormEvent) {
    e.preventDefault();
    const code = codeEingabe.trim();
    if (!code) return;
    setBusy("bestaetigen");
    setAktionsFehler(null);
    try {
      await apiMutate("/api/settings/sending-domain/bestaetigen", "POST", { code });
      setCodeEingabe("");
      await reload();
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : String(err));
      /*
       * AUCH IM FEHLERFALL NEU LADEN (Review-Befund 03.09.2026, K1).
       *
       * `reload()` stand nur im Erfolgspfad. Nach einem 502 („Der Eigentumsnachweis ist
       * erbracht, die Domain konnte aber nicht beim Versand-Provider registriert werden …
       * Bitte 'Registrierung wiederholen' auslösen") blieb `data` damit auf dem alten Stand,
       * `registrierungOffen` auf `false` — und der genannte Knopf wurde nicht gerendert. Die
       * Meldung verwies auf ein Bedienelement, das nicht auf dem Bildschirm war (Render-Sonde:
       * „KNOPF VORHANDEN: false | reload() aufgerufen: 0"), und der Betrieb musste die Seite
       * selbst neu laden.
       *
       * Es gilt nicht nur für den 502: auch ein falscher Code ändert serverseitigen Stand
       * (Versuchszähler an allen offenen Ansprüchen), den die Anzeige sonst nicht mitbekommt.
       */
      try {
        await reload();
      } catch {
        // Ein Ladefehler würde hier nur die eigentliche Fehlermeldung überschreiben.
      }
    } finally {
      setBusy(null);
    }
  }

  async function domainEntfernen() {
    setBusy("entfernen");
    setAktionsFehler(null);
    try {
      await apiMutate("/api/settings/sending-domain", "DELETE");
      await reload();
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function statusPruefen() {
    setBusy("pruefen");
    setAktionsFehler(null);
    try {
      await apiMutate("/api/settings/sending-domain/check", "POST");
      await reload();
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <LoadingRows rows={3} />;

  /**
   * Offener Anspruch = eine Domain ist eingetragen, der Eigentumsnachweis fehlt noch.
   *
   * Bevorzugt das eigene Feld des Backends; der Rueckfall auf `nachweis` haelt die Anzeige
   * korrekt, solange eine aeltere API-Antwort im Umlauf ist (Deploy-Fenster).
   */
  const offenerAnspruch =
    data?.offenerAnspruch ?? (data?.nachweis && data.nachweis.status === "beansprucht" ? data.nachweis : null);
  /** Die Freigabe — „diese Domain gehoert mir" nach MAESTROs eigener Pruefung. */
  const freigabe = data?.freigabe ?? (data?.nachweis && data.nachweis.status === "freigegeben" ? data.nachweis : null);
  /**
   * Der Nachweis liegt vor, nur die Registrierung beim Provider fehlt noch. Erkennbar an einem
   * offenen Anspruch MIT `nachweisAm` — der Code ist eingeloest, umgeschaltet ist nicht.
   */
  const registrierungOffen = Boolean(offenerAnspruch?.nachweisAm);

  /**
   * ══ DIE EINE FRAGE, DIE DIESER REITER BEANTWORTEN MUSS ═══════════════════════════════════
   * „Unter welcher Adresse geht meine Post gerade raus?" — und zwar wahrheitsgemaess.
   *
   * Der Haken „Eigentum nachgewiesen" hing bisher allein an `nachweis.status`, ohne Vergleich
   * mit der tatsaechlich eingetragenen Domain. Ein Bestandsbetrieb (Domain in tenant_settings,
   * noch keine Zeile in absender_domains) sah „DNS: Verifiziert" und sonst nichts — kein
   * Hinweis, keine Aufforderung — und versendete trotzdem von der gemeinsamen Standardadresse.
   * „Scheitert und sieht aus wie erledigt", also genau die Fehlerklasse, gegen die dieser Umbau
   * angetreten ist (Review-Befund 03.09.2026).
   *
   * Eigener Absender heisst deshalb: BEIDES stimmt, und beides fuer DIESELBE Domain — die
   * Freigabe aus `absender_domains` UND die Zustellbarkeit laut Provider. Das ist dieselbe
   * Bedingung, die `absenderdomain_freigabe()` serverseitig auswertet.
   */
  const eigentumNachgewiesen = Boolean(freigabe && data?.sendingDomain && freigabe.domain === data.sendingDomain);
  const eigenerAbsenderAktiv = eigentumNachgewiesen && data?.sendingDomainStatus === "verified";
  /**
   * Der Bestandsfall aus K3: die eingetragene Domain ist gar nicht beanspruchbar. Ohne diese
   * Unterscheidung nannte die Oberflaeche die gemeinsame Standardadresse als Gegensatz zu einer
   * Domain, die diese Adresse SELBST ist — und riet zu einem Schritt, der in 422 endet.
   * Beantwortet wird das serverseitig (nur dort ist die Umgebung bekannt); fehlt das Feld
   * (aeltere API-Antwort im Deploy-Fenster), bleibt es beim bisherigen Text.
   */
  const istPlattformAbsender = data?.sendingDomainSperre === "plattform_absender";
  const istGesperrt = data?.sendingDomainSperre === "gesperrt";

  const statusBadge = data
    ? {
        verified: { text: "Verifiziert", cls: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]" },
        pending: { text: "Ausstehend", cls: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]" },
        failed: { text: "Fehlgeschlagen", cls: "bg-error/10 text-error" },
      }[data.sendingDomainStatus]
    : null;

  return (
    <div className="grid grid-cols-1 gap-gutter lg:grid-cols-3">
      <Card className="p-6 lg:col-span-2">
        <div className="mb-6 flex items-center gap-2 border-b border-outline-variant/30 pb-4 dark:border-outline/30">
          <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Absenderdomain</h3>
        </div>
        {ladeFehler && <ErrorNote message={ladeFehler} />}
        {aktionsFehler && <ErrorNote message={aktionsFehler} />}

        <p className="mb-4 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Standardmäßig versendet MAESTRO für alle Betriebe von einer gemeinsamen Adresse. Wer
          stattdessen von der eigenen Domain senden möchte (z. B. example.com), trägt sie hier
          ein. In zwei Schritten: erst weisen Sie nach, dass Ihnen die Domain gehört, danach
          tragen Sie die DNS-Einträge ein.
        </p>

        {canEdit ? (
          <form
            onSubmit={(e) => void domainRegistrieren(e)}
            className="mb-6 flex flex-wrap items-end gap-3 border-b border-outline-variant/30 pb-6 dark:border-outline/30"
          >
            <label className="flex-1 min-w-[220px]">
              <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {data?.sendingDomain ? "Domain ändern" : "Eigene Domain eintragen"}
              </span>
              <Input
                type="text"
                value={domainEingabe}
                onChange={(e) => setDomainEingabe(e.target.value)}
                placeholder="example.com"
                disabled={busy !== null}
              />
            </label>
            {/* Ohne diese Auswahl war eine Domain ohne `info@`-Postfach ueber die Oberflaeche
                nicht onboardbar — obwohl die Fehlermeldung des Servers ausdrücklich riet, „eine
                andere Rollenadresse zu wählen". */}
            <label className="min-w-[170px]">
              <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Nachweis an</span>
              <Select
                ariaLabel="Rollenadresse für den Nachweis-Code"
                value={rolleEingabe}
                onValueChange={(v) => setRolleEingabe(v as (typeof NACHWEIS_ROLLEN)[number])}
                disabled={busy !== null}
                options={NACHWEIS_ROLLEN.map((rolle) => ({ value: rolle, label: `${rolle}@…` }))}
              />
            </label>
            <Button variant="primary" type="submit" disabled={busy !== null || !domainEingabe.trim()}>
              {busy === "registrieren" ? "Wird geprüft…" : "Domain hinterlegen"}
            </Button>
          </form>
        ) : (
          <p className="mb-6 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Nur Inhaber dürfen die Absenderdomain ändern.
          </p>
        )}

        {/* Schritt 1 — Eigentumsnachweis offen. Er steht VOR der DNS-Tabelle, weil ohne ihn
            ueberhaupt nichts weitergeht (und weil die DNS-Eintraege erst danach entstehen). */}
        {offenerAnspruch ? (
          <div className="mb-6 rounded-xl border border-outline-variant/40 bg-surface-container-low p-4 dark:border-outline/30 dark:bg-surface-variant/10">
            {registrierungOffen ? (
              /* Nachweis erbracht, Provider-Registrierung offen. Kein Code mehr nötig — er ist
                 eingelöst. Es fehlt genau ein Schritt, und für den gibt es hier einen Knopf. */
              <>
                <p className="mb-2 flex items-start gap-1.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  <Icon name="hourglass_top" className="mt-0.5 shrink-0 text-[15px] text-secondary" />
                  <span>
                    Schritt 2 von 2 — der Eigentumsnachweis für <span className="font-mono">{offenerAnspruch.domain}</span> ist erbracht,
                    die Registrierung beim Versand-Provider fehlt noch.{" "}
                    {data?.sendingDomain
                      ? <>Bis dahin versendet MAESTRO unverändert über <span className="font-mono">{data.sendingDomain}</span>.</>
                      : <>Bis dahin versendet MAESTRO von der gemeinsamen Standardadresse.</>}
                  </span>
                </p>
                {canEdit && (
                  <Button
                    variant="primary"
                    type="button"
                    onClick={() => void registrierungWiederholen()}
                    disabled={busy !== null}
                    className="mt-3 inline-flex items-center gap-1.5"
                  >
                    <Icon name="restart_alt" className="text-[16px]" />
                    {busy === "aktivieren" ? "Wird registriert…" : "Registrierung wiederholen"}
                  </Button>
                )}
              </>
            ) : (
              <>
                <p className="mb-2 flex items-start gap-1.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  <Icon name="mark_email_unread" className="mt-0.5 shrink-0 text-[15px] text-secondary" />
                  <span>
                    Schritt 1 von 2 — Eigentumsnachweis für <span className="font-mono">{offenerAnspruch.domain}</span>. Wir haben einen
                    Bestätigungscode an <span className="font-mono">{offenerAnspruch.challengeEmpfaenger}</span> geschickt. Er gilt 30
                    Minuten.{" "}
                    {data?.sendingDomain && data.sendingDomain !== offenerAnspruch.domain
                      ? /* Der Domainwechsel im laufenden Betrieb: hier steht ausdrücklich, dass
                           die alte Domain weiterläuft, bis der Nachweis erbracht ist. */
                        <>
                          Bis dahin versendet MAESTRO unverändert über <span className="font-mono">{data.sendingDomain}</span> — der
                          Wechsel passiert erst, wenn Sie den Code eintragen.
                        </>
                      : <>Ohne diesen Nachweis versendet MAESTRO weiter von der gemeinsamen Standardadresse.</>}
                  </span>
                </p>
                {canEdit && (
                  <form onSubmit={(e) => void nachweisBestaetigen(e)} className="mt-3 flex flex-wrap items-end gap-3">
                    <label className="min-w-[180px] flex-1">
                      <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Bestätigungscode</span>
                      <Input
                        type="text"
                        value={codeEingabe}
                        onChange={(e) => setCodeEingabe(e.target.value)}
                        placeholder="ABCDE-FGHJK"
                        className="font-mono uppercase"
                        disabled={busy !== null}
                      />
                    </label>
                    <Button variant="primary" type="submit" disabled={busy !== null || !codeEingabe.trim()}>
                      {busy === "bestaetigen" ? "Wird geprüft…" : "Code bestätigen"}
                    </Button>
                  </form>
                )}
              </>
            )}
          </div>
        ) : null}

        {data?.sendingDomain ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono font-body-md text-body-md text-on-background dark:text-inverse-on-surface">{data.sendingDomain}</span>
              {/* Zwei getrennte Haken, bewusst nicht zu einem verschmolzen: der erste sagt
                  „Ihnen gehoert die Domain" (MAESTROs eigener Nachweis), der zweite „der Provider
                  kann darueber zustellen" (DNS/DKIM). Nur zusammen ergeben sie einen eigenen
                  Absender. */}
              {eigentumNachgewiesen ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F0EAE0] px-2.5 py-0.5 font-body-sm text-body-sm font-medium text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                  <Icon name="verified_user" className="text-[14px]" />
                  Eigentum nachgewiesen
                </span>
              ) : (
                /* Bestandsfall: Domain steht in tenant_settings, in absender_domains gibt es
                   (noch) keine Freigabe dafür. Vorher stand hier NICHTS — der Betrieb sah einen
                   grünen DNS-Haken und hielt die Sache für erledigt. */
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F0EAE0] px-2.5 py-0.5 font-body-sm text-body-sm font-medium text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                  <Icon name="gpp_maybe" className="text-[14px]" />
                  Eigentum nicht nachgewiesen
                </span>
              )}
              {statusBadge && (
                <span className={`rounded-full px-2.5 py-0.5 font-body-sm text-body-sm font-medium ${statusBadge.cls}`}>{`DNS: ${statusBadge.text}`}</span>
              )}
            </div>

            {/* Die eine Zeile, die sagt, was TATSÄCHLICH passiert. Sie ist grün nur dann, wenn
                beides für dieselbe Domain stimmt — sonst nennt sie die gemeinsame Standardadresse
                beim Namen UND den fehlenden Schritt. */}
            {eigenerAbsenderAktiv ? (
              <p className="flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-3 py-2 font-body-sm text-body-sm text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                <Icon name="check_circle" className="mt-0.5 shrink-0 text-[15px]" />
                <span>
                  Ihre Post geht unter <span className="font-mono">info@{data.sendingDomain}</span> raus.
                </span>
              </p>
            ) : istPlattformAbsender ? (
              /* K3: die eingetragene Domain IST die gemeinsame Standardadresse (oder liegt
                 darunter). Der Satz „raus, nicht unter X" wäre hier schlicht falsch, und der
                 sonst gegebene Rat „tragen Sie sie oben erneut ein" endet in 422 — die Domain
                 ist der Plattform-Absender und für keinen Mandanten beanspruchbar. */
              <p className="flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-3 py-2 font-body-sm text-body-sm text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                <Icon name="info" className="mt-0.5 shrink-0 text-[15px]" />
                <span>
                  Ihre Post geht von der gemeinsamen Standardadresse raus — <span className="font-mono">{data.sendingDomain}</span> ist genau
                  diese Adresse und gehört MAESTRO, nicht Ihrem Betrieb. Sie lässt sich deshalb nicht als eigene Absenderdomain hinterlegen. Wenn
                  Sie unter einer eigenen Domain senden möchten, tragen Sie oben eine Domain ein, die Ihnen gehört.
                </span>
              </p>
            ) : istGesperrt ? (
              /* Gleiche Klasse, anderer Grund (Plattform-Basisdomain, Freemail, unbrauchbare
                 Form): der Rat „erneut eintragen" liefe auch hier in 422. */
              <p className="flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-3 py-2 font-body-sm text-body-sm text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                <Icon name="info" className="mt-0.5 shrink-0 text-[15px]" />
                <span>
                  Ihre Post geht derzeit von der gemeinsamen Standardadresse raus, nicht unter{" "}
                  <span className="font-mono">{data.sendingDomain}</span>. Diese Domain kann nicht als eigene Absenderdomain hinterlegt werden
                  (Plattform- oder Freemail-Domain) — tragen Sie oben eine Domain ein, die Ihrem Betrieb gehört.
                </span>
              </p>
            ) : (
              <p className="flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-3 py-2 font-body-sm text-body-sm text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                <Icon name="info" className="mt-0.5 shrink-0 text-[15px]" />
                <span>
                  Ihre Post geht derzeit von der gemeinsamen Standardadresse raus, nicht unter{" "}
                  <span className="font-mono">{data.sendingDomain}</span>.{" "}
                  {!eigentumNachgewiesen
                    ? registrierungOffen
                      ? "Der Eigentumsnachweis ist erbracht — es fehlt nur noch die Registrierung beim Versand-Provider (Knopf oben)."
                      : "Für diese Domain fehlt der Eigentumsnachweis. Tragen Sie sie oben erneut ein: Sie bekommen einen Bestätigungscode an eine Rollenadresse dieser Domain und schalten sie damit frei."
                    : "Die DNS-Einträge sind noch nicht bestätigt — tragen Sie sie bei Ihrem DNS-Provider ein und lösen Sie danach „Jetzt prüfen“ aus."}
                </span>
              </p>
            )}

            {data.sendingDomainStatus !== "verified" && data.sendingDomainDnsRecords?.length ? (
              <div className="overflow-hidden rounded-xl border border-outline-variant/40 dark:border-outline/30">
                <p className="flex items-start gap-1.5 border-b border-outline-variant/40 bg-surface-container-low px-[18px] py-3 font-body-sm text-body-sm text-on-background dark:border-outline/30 dark:bg-surface-variant/10 dark:text-inverse-on-surface">
                  <Icon name="info" className="mt-0.5 shrink-0 text-[15px] text-secondary" />
                  <span>
                    Tragen Sie diese Einträge bei Ihrem DNS-Provider ein. Die Prüfung kann bis zu
                    24–48 Stunden dauern.
                  </span>
                </p>
                {/* `table-fixed` statt `min-w`+Scrollbalken (Antoine 03.09.2026: "Scrollbalken ist
                    nicht schoen"), Werte umgebrochen statt gekuerzt (derselbe Tag: "text immer
                    noch abgeschnitten").
                    Dritte Runde, derselbe Tag: bei FUENF Spalten blieben fuer Name/Wert so wenige
                    Pixel, dass schon ein kurzer Hostname wie `brevo1._domainkey` mitten im Wort
                    umbrach ("brevo1._domainke / y") — vollstaendig, aber es LIEST sich
                    abgeschnitten. Deshalb sind "Zweck" und "Name" jetzt EINE Spalte (Zweck als
                    kleine Zeile ueber dem Hostnamen). Das schafft rund 170px, mit denen sowohl die
                    Hostnamen als auch der lange Brevo-Verifizierungscode in eine Zeile passen.
                    Schmaleres Zellenpadding (px-3 statt der 18px der .table-cell-Klasse) gibt
                    zusaetzliche Luft, wo jedes Pixel zaehlt. */}
                <div>
                  <table className="w-full table-fixed border-collapse text-left">
                    <colgroup>
                      <col className="w-[76px]" />
                      <col className="w-[30%]" />
                      <col />
                      <col className="w-[116px]" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="table-header !px-3">Typ</th>
                        <th className="table-header !px-3">Name</th>
                        <th className="table-header !px-3">Wert</th>
                        <th className="table-header !px-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sendingDomainDnsRecords.map((eintrag) => (
                        <tr
                          key={`${eintrag.recordType}-${eintrag.hostName}`}
                          className="border-t border-outline-variant/20 transition-colors hover:bg-surface-container-high/20 dark:border-outline/20 dark:hover:bg-surface-variant/20"
                        >
                          <td className="table-cell !px-3 align-top">
                            <span className="inline-flex items-center rounded-md bg-surface-container-low px-2 py-0.5 font-mono text-[11px] font-semibold tracking-wide text-secondary dark:bg-surface-variant/20 dark:text-secondary-fixed-dim">
                              {eintrag.recordType}
                            </span>
                          </td>
                          <td className="table-cell !px-3 align-top font-mono text-on-background dark:text-inverse-on-surface">
                            <span className="mb-1 block font-sans text-[11px] leading-tight text-secondary dark:text-secondary-fixed-dim">
                              {eintrag.label}
                            </span>
                            <DnsWertMitKopieren wert={eintrag.hostName} />
                          </td>
                          <td className="table-cell !px-3 align-top font-mono text-on-background dark:text-inverse-on-surface">
                            {/* Gleiche Kopfhoehe wie die Zweck-Zeile links, damit Name und Wert
                                auf derselben Grundlinie beginnen. */}
                            <span className="mb-1 block text-[11px] leading-tight opacity-0" aria-hidden="true">
                              &nbsp;
                            </span>
                            <DnsWertMitKopieren wert={eintrag.value} />
                          </td>
                          <td className="table-cell !px-3 whitespace-nowrap align-top">
                            <span className="mb-1 block text-[11px] leading-tight opacity-0" aria-hidden="true">
                              &nbsp;
                            </span>
                            {eintrag.verified ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-[#F0EAE0] px-2.5 py-0.5 font-body-sm text-[12px] font-medium text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                                <Icon name="check_circle" className="text-[14px]" />
                                Bestätigt
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-[#F0EAE0] px-2.5 py-0.5 font-body-sm text-[12px] font-medium text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                                <Icon name="schedule" className="text-[14px]" />
                                Ausstehend
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {data.sendingDomainStatus !== "verified" && data.sendingDomain && !data.sendingDomainDnsRecords?.length ? (
              <div className="rounded-lg border border-outline-variant/40 bg-surface-container-low p-4 dark:border-outline/30 dark:bg-surface-variant/10">
                <p className="flex items-start gap-1.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  <Icon name="info" className="mt-0.5 text-[15px] text-secondary" />
                  <span>
                    Diese Domain ist bereits bei Brevo hinterlegt — die DNS-Einträge sind hier
                    gerade nicht abrufbar. Bitte bei Bedarf direkt im Brevo-Konto nachsehen.
                  </span>
                </p>
              </div>
            ) : null}

            {canEdit && (
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => void statusPruefen()}
                  disabled={busy !== null}
                  className="inline-flex items-center gap-1.5"
                >
                  <Icon name="refresh" className="text-[16px]" />
                  {busy === "pruefen" ? "Prüfe…" : "Jetzt prüfen"}
                </Button>
                {/* Der freundliche Normalweg beim Betriebswechsel: wer abgibt, gibt die Domain
                    frei — danach kann der nachfolgende Betrieb sie mit eigenem Nachweis
                    uebernehmen, ohne dass jemand eingreifen muss. */}
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => void domainEntfernen()}
                  disabled={busy !== null}
                  className="inline-flex items-center gap-1.5 text-secondary"
                >
                  <Icon name="delete" className="text-[16px]" />
                  {busy === "entfernen" ? "Wird entfernt…" : "Domain entfernen"}
                </Button>
              </div>
            )}
          </div>
        ) : offenerAnspruch ? null : (
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Noch keine eigene Absenderdomain hinterlegt — es wird die gemeinsame Standardadresse
            verwendet.
          </p>
        )}
      </Card>
      <SperrlisteCard canEdit={canEdit} />
    </div>
  );
}

/**
 * Paket C3 — Sperrlisten-Selbstbedienung.
 *
 * BEWUSST kein automatischer Alarm bei einem Sendefehler: SES meldet eine gesperrte Adresse bei
 * SendEmail nicht synchron (die Zustellung wird still verworfen, sichtbar erst per Bounce-Event,
 * Paket C2 — noch nicht gebaut). Diese Karte ist deshalb ein eigenstaendiges Nachschlage-Werkzeug:
 * eine Adresse eintippen, sehen ob (und warum) sie gesperrt ist, bei Bedarf die Sperre aufheben.
 */
function SperrlisteCard({ canEdit }: { canEdit: boolean }) {
  const [email, setEmail] = useState("");
  const [ergebnis, setErgebnis] = useState<{ dienst?: "ses" | "brevo"; suppressed: boolean; reason: string | null; lastUpdateTime: string | null } | null>(null);
  const [busy, setBusy] = useState<"pruefen" | "aufheben" | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  async function pruefen(e: FormEvent) {
    e.preventDefault();
    const adresse = email.trim();
    if (!adresse) return;
    setBusy("pruefen");
    setFehler(null);
    setErgebnis(null);
    try {
      const data = await apiMutate<{ dienst?: "ses" | "brevo"; suppressed: boolean; reason: string | null; lastUpdateTime: string | null }>(
        `/api/settings/suppression/${encodeURIComponent(adresse)}`,
        "GET",
      );
      setErgebnis(data);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function aufheben() {
    const adresse = email.trim();
    if (!adresse) return;
    setBusy("aufheben");
    setFehler(null);
    try {
      await apiMutate(`/api/settings/suppression/${encodeURIComponent(adresse)}`, "DELETE");
      setErgebnis({ suppressed: false, reason: null, lastUpdateTime: null });
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="p-6 lg:col-span-2">
      <div className="mb-6 flex items-center gap-2 border-b border-outline-variant/30 pb-4 dark:border-outline/30">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Sperrliste</h3>
      </div>
      <p className="mb-4 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Prüfen Sie, ob eine E-Mail-Adresse auf der Sperrliste Ihres Mail-Dienstes (Brevo oder Amazon SES) steht
        (z. B. nach einem harten Bounce oder einer Beschwerde) — und heben Sie die Sperre bei Bedarf auf. {SPERRE_WARNUNG}
      </p>
      {fehler && <ErrorNote message={fehler} />}
      <form onSubmit={(e) => void pruefen(e)} className="flex flex-wrap items-end gap-3">
        <label className="flex-1 min-w-[220px]">
          <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">E-Mail-Adresse</span>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="kundin@example.com"
            disabled={busy !== null}
          />
        </label>
        <Button variant="secondary" type="submit" disabled={busy !== null || !email.trim()}>
          {busy === "pruefen" ? "Prüfe…" : "Prüfen"}
        </Button>
      </form>

      {ergebnis && (
        <div
          className={`mt-4 flex items-start gap-2.5 rounded-lg border px-4 py-3 ${
            ergebnis.suppressed
              ? "border-error/40 bg-error/10"
              : "border-outline-variant/40 bg-surface-container-low dark:border-outline/30 dark:bg-surface-variant/10"
          }`}
        >
          <Icon name={ergebnis.suppressed ? "block" : "check_circle"} className={`mt-0.5 text-[18px] ${ergebnis.suppressed ? "text-error" : "text-secondary"}`} />
          <div className="min-w-0 flex-1">
            {ergebnis.suppressed ? (
              <>
                <p className="font-body-md text-body-md font-semibold text-error">Diese Adresse ist gesperrt.</p>
                <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {ergebnis.dienst ? `Bei ${ergebnis.dienst === "brevo" ? "Brevo" : "Amazon SES"} · ` : ""}Grund: {ergebnis.reason ?? "unbekannt"}
                  {ergebnis.lastUpdateTime ? ` · seit ${formatDateTime(ergebnis.lastUpdateTime)}` : ""}
                </p>
                {canEdit ? (
                  <Button variant="primary" type="button" onClick={() => void aufheben()} disabled={busy !== null} className="mt-3 inline-flex items-center gap-1.5">
                    <Icon name="lock_open" className="text-[16px]" />
                    {busy === "aufheben" ? "Hebt auf…" : "Sperre aufheben"}
                  </Button>
                ) : (
                  <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Nur Inhaber dürfen eine Sperre aufheben.</p>
                )}
              </>
            ) : (
              <p className="font-body-md text-body-md text-on-background dark:text-inverse-on-surface">
                Diese Adresse ist nicht gesperrt{ergebnis.dienst ? ` (${ergebnis.dienst === "brevo" ? "Brevo" : "Amazon SES"})` : ""}.
              </p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

const BRAND_DEFAULT_PRIMARY = "#c2410c";
const BRAND_DEFAULT_BG = "#f6f1e7";
const BRAND_DEFAULT_FONT = "Inter,Segoe UI,Arial,sans-serif";
interface MarkeVorschlag {
  status: "found" | "not_found" | "error" | "skipped";
  primaryColor: string | null;
  quelleUrl: string | null;
  errorMessage?: string;
}
function MarkeForm() {
  const { data, loading, reload } = useApi<{ brandPrimaryColor: string | null; brandBgColor: string | null; brandFont: string | null }>("/api/settings/marke");
  const [primaryColor, setPrimaryColor] = useState<string>(BRAND_DEFAULT_PRIMARY);
  const [bgColor, setBgColor] = useState<string>(BRAND_DEFAULT_BG);
  const [font, setFont] = useState<string>(BRAND_DEFAULT_FONT);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [vorschlagBusy, setVorschlagBusy] = useState(false);
  const [vorschlag, setVorschlag] = useState<MarkeVorschlag | null>(null);
  useEffect(() => {
    if (!data) return;
    setPrimaryColor(data.brandPrimaryColor || BRAND_DEFAULT_PRIMARY);
    setBgColor(data.brandBgColor || BRAND_DEFAULT_BG);
    setFont(data.brandFont || BRAND_DEFAULT_FONT);
  }, [data]);
  async function speichern() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/marke", "PUT", {
        brandPrimaryColor: primaryColor,
        brandBgColor: bgColor,
        brandFont: font,
      });
      setSaved(true);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function vorschlagHolen() {
    if (!websiteUrl.trim()) return;
    setVorschlagBusy(true);
    setVorschlag(null);
    setError(null);
    try {
      const v = await apiMutate<MarkeVorschlag>(`/api/settings/marke/vorschlag?website=${encodeURIComponent(websiteUrl.trim())}`, "GET");
      setVorschlag(v);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setVorschlagBusy(false);
    }
  }
  if (loading && !data) return <LoadingRows rows={3} />;
  return (
    <div className="space-y-6">
      {error && <ErrorNote message={error} />}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Akzentfarbe (Buttons, Mail-Chip)</span>
          <div className="flex items-center gap-2">
            <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="h-10 w-14 shrink-0 cursor-pointer rounded-lg border border-outline-variant/40 dark:border-outline/30" />
            <Input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} placeholder={BRAND_DEFAULT_PRIMARY} />
          </div>
        </label>
        <label className="block">
          <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Hintergrundfarbe (Details-Box in Mails)</span>
          <div className="flex items-center gap-2">
            <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="h-10 w-14 shrink-0 cursor-pointer rounded-lg border border-outline-variant/40 dark:border-outline/30" />
            <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} placeholder={BRAND_DEFAULT_BG} />
          </div>
        </label>
      </div>
      <label className="block">
        <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Schriftart (CSS font-family)</span>
        <Input value={font} onChange={(e) => setFont(e.target.value)} placeholder={BRAND_DEFAULT_FONT} />
      </label>
      <div>
        <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">Vorschau</span>
        <div className="rounded-xl border border-outline-variant/40 bg-white p-6 dark:border-outline/30" style={{ fontFamily: font }}>
          <div style={{ background: bgColor, border: "1px solid #efe8da", borderRadius: 14, padding: "15px 20px", fontSize: 13.5, color: "#6f675a" }}>
            <span style={{ color: "#9a9080" }}>Datum:</span> <strong style={{ color: "#262119" }}>Sa., 12. September 2026</strong>
          </div>
          <p style={{ textAlign: "center", margin: "20px 0 0" }}>
            <span style={{ display: "inline-block", background: primaryColor, color: "#fff", padding: "12px 22px", borderRadius: 10, fontWeight: 600, fontSize: 14 }}>Angebot ansehen &amp; wählen →</span>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
        <Button type="button" onClick={() => void speichern()} disabled={busy} className="!py-2">
          {busy ? "Speichert …" : "Speichern"}
        </Button>
        {saved && <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-tertiary dark:text-tertiary-fixed-dim"><Icon name="check_circle" className="text-[18px]" /> Gespeichert</span>}
      </div>
      <div className="border-t border-outline-variant/30 pt-6 dark:border-outline/30">
        <span className="mb-2 block font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">Farbvorschlag aus der eigenen Website</span>
        <p className="mb-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Website-Adresse eingeben — MAESTRO liest dort das theme-color-Meta-Tag aus und schlägt die Akzentfarbe vor. Nichts wird automatisch übernommen.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="ristorantestoria.de"
            className="max-w-xs"
          />
          <Button variant="secondary" type="button" onClick={() => void vorschlagHolen()} disabled={vorschlagBusy || !websiteUrl.trim()} className="!py-2">
            {vorschlagBusy ? "Sucht …" : "Vorschlag holen"}
          </Button>
        </div>
        {vorschlag && (
          <div className="mt-3">
            {vorschlag.status === "found" && vorschlag.primaryColor ? (
              <div className="flex items-center gap-3 rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
                <span className="h-8 w-8 shrink-0 rounded-md border border-outline-variant/40" style={{ background: vorschlag.primaryColor }} />
                <span className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">Vorschlag: {vorschlag.primaryColor}</span>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    if (vorschlag.primaryColor) setPrimaryColor(vorschlag.primaryColor);
                    setVorschlag(null);
                  }}
                  className="!py-1 !px-3 text-[12.5px]"
                >
                  Als Akzentfarbe übernehmen
                </Button>
              </div>
            ) : (
              <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                {vorschlag.errorMessage ?? "Keine Markenfarbe gefunden."}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
/**
 * Globales E-Mail-Design (aufgaben.md, Antoine 22.08.2026, Schritt 3/4) — rohes HTML-Geruest mit
 * einem {{content}}-Platzhalter, das der Mailer (`wrapWithTenantTemplate`, apps/api/src/lib/mailer.ts)
 * um JEDE ausgehende Mail legt. Direktes Vorbild `MarkeForm()` oben: `useApi` zum Laden, lokaler
 * State + `useEffect` zum Befuellen, `apiMutate(..., "PUT", ...)` zum Speichern, `saved`-Anzeige.
 *
 * Eingehaengt im Tab "branding" ("Angebots-Vorlagen & Branding"), NICHT als eigener Tab: der Tab
 * fuehrt schon mehrere lose verwandte Mail-/Vorlagen-Bausteine nebeneinander (Marke = Farben/Schrift,
 * Logo, oeffentliche Angebotsseite) und heisst ausdruecklich "…-Vorlagen & Branding" — ein globales
 * HTML-Geruest fuer Mails ist inhaltlich naeher an "Vorlagen" als an irgendeinem anderen Tab
 * (Zahlungen, Module, Team, Orte, Datenschutz, Widgets). Ein eigener Tab haette dafuer extra Platz
 * in der Tab-Leiste beansprucht, ohne einen zweiten Verbraucher zu haben.
 */
function EmailVorlageForm() {
  const { data, loading, reload } = useApi<{ emailHtmlTemplate: string | null }>("/api/settings/email-vorlage");
  const [template, setTemplate] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<{ kind: "ok" | "error"; message: string } | null>(null);
  useEffect(() => {
    if (!data) return;
    setTemplate(data.emailHtmlTemplate ?? "");
  }, [data]);
  async function speichern() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiMutate("/api/settings/email-vorlage", "PUT", {
        emailHtmlTemplate: template.trim() ? template : null,
      });
      setSaved(true);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  async function testMailSenden() {
    setTestBusy(true);
    setTestResult(null);
    try {
      const res = await apiMutate<{ sentTo: string }>("/api/settings/email-vorlage/test-mail", "POST", {
        emailHtmlTemplate: template.trim() ? template : null,
      });
      setTestResult({ kind: "ok", message: `Test-Mail wurde an ${res.sentTo} gesendet.` });
    } catch (err) {
      setTestResult({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    } finally {
      setTestBusy(false);
    }
  }
  if (loading && !data) return <LoadingRows rows={3} />;
  return (
    <div className="space-y-4">
      {error && <ErrorNote message={error} />}
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Eigenes HTML-Geruest (Rahmen, Footer), das um den Inhalt JEDER ausgehenden Mail gelegt wird — auch um Freitext-Mails. Das Logo musst du hier nicht einbauen: MAESTRO setzt es automatisch aus dem Tab Branding in jede Mail.
        Dein HTML MUSS den Platzhalter <code>{"{{content}}"}</code> enthalten, an dessen Stelle der eigentliche Mail-Inhalt eingesetzt wird.
        Fehlt der Platzhalter, wird deine Vorlage fuer den jeweiligen Versand ignoriert und stattdessen die schlichte Standard-Mail verschickt.
        Leer lassen und speichern entfernt die eigene Vorlage — es gilt dann wieder das Standard-Design.
      </p>
      <label className="block">
        <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">HTML-Vorlage</span>
        <Textarea
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          rows={18}
          placeholder={'<div style="max-width:600px;margin:0 auto;font-family:sans-serif">\n  {{content}}\n  <p style="color:#888;font-size:12px">Mein Betrieb · Musterstraße 1 · 12345 Musterstadt</p>\n</div>'}
          className="resize-y font-mono text-[13px]"
          spellCheck={false}
        />
      </label>
      <div className="flex flex-wrap items-center gap-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30">
        <Button type="button" onClick={() => void speichern()} disabled={busy} className="!py-2">
          {busy ? "Speichert …" : "Speichern"}
        </Button>
        {saved && <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-tertiary dark:text-tertiary-fixed-dim"><Icon name="check_circle" className="text-[18px]" /> Gespeichert</span>}
        <Button variant="secondary" type="button" onClick={() => void testMailSenden()} disabled={testBusy} className="!py-2">
          {testBusy ? "Sendet …" : "Test-Mail senden"}
        </Button>
      </div>
      <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Die Test-Mail geht an deine eigene, im Konto hinterlegte E-Mail-Adresse und nutzt den Text im Feld oben — auch wenn du noch nicht gespeichert hast.
      </p>
      {testResult && (
        testResult.kind === "ok" ? (
          <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-tertiary dark:text-tertiary-fixed-dim"><Icon name="check_circle" className="text-[18px]" /> {testResult.message}</span>
        ) : (
          <ErrorNote message={testResult.message} />
        )
      )}
    </div>
  );
}
/**
 * Laedt den Serverstand und zeichnet das Formular erst, wenn er da ist.
 *
 * WARTESCHLANGENPUNKT 40 (Antoine, 16.09.2026: „Er sagt mir gespeichert, beim erneuten Aufruf ist
 * nichts gespeichert", und danach: „Hard Reload. Dann werden die Speicherungen nicht ausgegeben im
 * Frontend"). Die Trennung in zwei Komponenten IST die Korrektur, und sie ist nicht kosmetisch:
 *
 * Vorher standen die fuenf Auswahlfelder als `useState(data?.… ?? KEIN_WERT)` in dieser
 * Komponente. Hooks laufen aber VOR dem `if (!ready) return <LoadingRows />` — also zu einem
 * Zeitpunkt, an dem der Endpunkt noch laeuft und `data` leer ist. Alle fuenf standen damit auf
 * „Nicht festgelegt". Nachgezogen wurden sie von einem Effekt an `[formKey]`, und `formKey` zaehlt
 * `usePaymentTermsData` NUR bei einem Refetch hoch, nie beim Erstladen: der Effekt lief genau
 * einmal, mit leeren Daten, und danach nie wieder.
 *
 * Sichtbar war das als „Nicht festgelegt" in allen fuenf Feldern. Der Schaden kam beim naechsten
 * Speichern: `save()` schickt fuer KEIN_WERT ein `null` — wer irgendetwas anderes auf dieser Seite
 * aenderte, loeschte damit stillschweigend alle fuenf hinterlegten Werte, darunter
 * `paymentPathway`, an dem haengt, ob nach einer Zusage ueberhaupt ein Zahlungslink entsteht.
 *
 * Die Textfelder waren nie betroffen: sie mounten erst NACH `ready` und lesen ihren `defaultValue`
 * dann aus vollstaendigen Daten. Genau deshalb fiel nur diese eine Feldgruppe auf.
 *
 * Den Effekt zusaetzlich an `ready` zu haengen, war der naheliegende Ein-Zeilen-Fix — er reicht
 * aber nicht: Radix meldet bei einem Wertwechsel NACH dem Mount ein `onValueChange("")` zurueck,
 * und der Select stand danach auf leer statt auf dem gespeicherten Wert. Nachgewiesen an der
 * Zustandsfolge `__kein_wert__` → `stripe_immediate` → `""`. Der Wert darf sich also gar nicht
 * erst nach dem Mount aendern.
 *
 * Deshalb: `data` kommt als Eigenschaft in ein Kind, das ueber `key={formKey}` erst dann entsteht,
 * wenn der Stand vollstaendig ist. Dort ist `useState(data.… ?? KEIN_WERT)` beim Mount richtig,
 * der Nachzieh-Effekt entfaellt ersatzlos, und der Refetch-Weg (neuer `formKey` → Neuaufbau mit
 * frischen Daten) bleibt unveraendert — inklusive seines `dirty`-Vorbehalts, der die gerade
 * getroffene Auswahl des Betreibers schuetzt.
 */
export function PaymentTermsForm() {
  const { data, ready, formKey, markDirty, markSaved } = usePaymentTermsData();
  if (!ready) return <LoadingRows />;
  return <PaymentTermsFelder key={formKey} data={data ?? {}} markDirty={markDirty} markSaved={markSaved} />;
}
function PaymentTermsFelder({
  data,
  markDirty,
  markSaved,
}: {
  data: PaymentTermsData;
  markDirty: () => void;
  markSaved: () => void;
}) {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Die fuenf Selects unten sind Radix-Selects (s. Kopfkommentar KEIN_WERT) statt roher
  // Select-Elemente — anders als die uebrigen Felder dieses Formulars nicht per FormData beim
  // Absenden ausgelesen, sondern als eigener State direkt in `save()` verwendet. Weil diese
  // Komponente erst mit vollstaendigen Daten entsteht (s. Kopfkommentar oben), ist der
  // Anfangswert hier richtig und bleibt es — es gibt keinen Nachzieh-Effekt mehr.
  /*
   * WARTESCHLANGE Punkt 45 — dieselbe Maske wie am Angebot, nur ohne den Wert „wie
   * Mandanten-Standard": hier IST der Standard. Der Schalter entscheidet das OB, die Auswahl das
   * WIE — vorher stand „Keine" als vierter WEG in der Auswahl „Anzahlungsart", und genau daraus
   * entstand der Ausfall vom 17.09.2026: „Anzahlung 100 %" neben „Anzahlungsart: Keine" war
   * speicherbar, und zwar als Standard, den jedes neue Angebot geerbt hätte.
   */
  const [anzahlungAn, setAnzahlungAn] = useState<boolean>(() => standardAnzahlungAn(data));
  const [anzahlungsWeg, setAnzahlungsWeg] = useState<string>(() => standardAnzahlungsWeg(data));
  // Migration 718: worauf sich die Anzahlungs-Frist bezieht.
  const [anzahlungFristModus, setAnzahlungFristModus] = useState<AnzahlungFristModus>(data.depositDueMode ?? "nach_bestaetigung");
  const restStart = standardRestzahlung(data);
  const [restWeg, setRestWeg] = useState<string>(restStart.weg);
  const [restFristArt, setRestFristArt] = useState<RestFristArt>(restStart.fristArt);
  const [acceptanceFlow, setAcceptanceFlow] = useState<string>(data.acceptanceFlow ?? KEIN_WERT);
  const [stornoVorlagen, setStornoVorlagen] = useState<StornoVorlage[]>(() => stornoVorlagenListe(data));
  // Was dieser Betrieb überhaupt anbieten kann — ein Weg, den es nicht gibt, wird mit dem Grund
  // dabei angezeigt statt kommentarlos zur Wahl gestellt (Ausfall vom 17.09.2026).
  const { data: zahlungswege } = useApi<Zahlungswege>("/api/settings/zahlungswege");
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const str = (k: string) => ((f.get(k) as string) || "").trim() || null;
    const depositRaw = ((f.get("depositPercent") as string | null) ?? "").trim();
    let depositPct: number | null = null;
    if (depositRaw !== "") {
      const pct = parseGermanDecimal(depositRaw);
      if (pct == null) {
        setError("Ungültiger Anzahlungs-Prozentsatz – bitte als Zahl eingeben, z. B. 30 oder 50,5.");
        return;
      }
      depositPct = Math.round(pct * 100);
    }
    const depositAmountRaw = ((f.get("depositAmount") as string | null) ?? "").trim();
    let depositAmountCents: number | null = null;
    if (depositAmountRaw !== "") {
      const amount = parseGermanDecimal(depositAmountRaw);
      if (amount == null) {
        setError("Ungültiger Anzahlungs-Fixbetrag – bitte als Zahl eingeben, z. B. 500 oder 499,50.");
        return;
      }
      depositAmountCents = Math.round(amount * 100);
    }
    if (depositPct != null && depositAmountCents != null) {
      setError("Anzahlung entweder als Prozentsatz ODER als Fixbetrag angeben – bitte eines der beiden Felder leeren.");
      return;
    }
    const paymentTermText = str("paymentTermText");
    function parseDaysField(name: string, fehlermeldung: string): number | null | undefined {
      const raw = ((f.get(name) as string | null) ?? "").trim();
      if (raw === "") return null;
      const n = Number(raw.replace(",", "."));
      if (!Number.isInteger(n) || n < 0) {
        setError(fehlermeldung);
        return undefined;
      }
      return n;
    }
    const depositDueDays = parseDaysField("depositDueDays", "Ungültige Anzahlungs-Frist – bitte volle Tage als Zahl eingeben, z. B. 7.");
    if (depositDueDays === undefined) return;
    const restTage = parseDaysField("restTage", "Ungültige Restzahlungs-Frist – bitte volle Tage als Zahl eingeben, z. B. 14.");
    if (restTage === undefined) return;
    const paymentTermDays = parseDaysField("paymentTermDays", "Ungültiges Zahlungsziel – bitte volle Tage als Zahl eingeben, z. B. 14.");
    if (paymentTermDays === undefined) return;
    /*
      Antoine, 18.09.2026, Variante (a) auf Rückfrage: null Tage VOR der Veranstaltung sind
      rechnerisch der Veranstaltungstag — und dafür gibt es die dritte Auswahl bereits. Statt zwei
      Wege zur selben Aussage stehenzulassen, wird die Auswahl selbst richtiggestellt. NACH der
      Veranstaltung bleibt die 0 stehen: dort heißt sie „sofort danach fällig" und ist eine eigene
      Aussage. Dieselbe Regel wie am Vorgang (`NachschlagenTabs.tsx`).
    */
    const restFristGeltend: RestFristArt = restFristArt === "vor_event" && restTage === 0 ? "am_event_tag" : restFristArt;
    if (restFristGeltend !== restFristArt) setRestFristArt(restFristGeltend);
    if (anzahlungAn && depositPct == null && depositAmountCents == null) {
      // Derselbe Satz wie beim Speichern eines Angebots (`pruefeVereinbarung`): eine Anzahlung
      // ohne Höhe ist eine Forderung ohne Zahl.
      setError("Bitte angeben, wie hoch die Anzahlung ist – als Prozentsatz oder als fester Betrag.");
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      /*
       * Punkt 45: die Umrechnung in die Altfelder kommt aus DENSELBEN Funktionen wie am Angebot
       * (`anzahlungSchalten`, `anzahlungsWegFelder`, `restzahlungFelder`). Zwei Umrechnungen für
       * dieselben Felder wären zwei Wahrheiten.
       */
      const anzahlungsFelder = anzahlungAn ? anzahlungsWegFelder(anzahlungsWeg) : anzahlungSchalten(false, null, []);
      await apiMutate("/api/settings/payment-terms", "PUT", {
        depositPercentBp: anzahlungAn ? depositPct : null,
        depositAmountCents: anzahlungAn ? depositAmountCents : null,
        // Punkt 116: Zahlungsziel-Tage und Zusatztext sind unabhängig — beide werden gespeichert.
        paymentTermDays,
        paymentTermText,
        // Migration 712: die Liste; der Server spiegelt den Standard nach `cancellationTerms`.
        stornoVorlagen: stornoVorlagen.length ? stornoVorlagen : null,
        // Punkt 50 b2 (Migration 453): die AGB — wie der Stornotext ein Freitext, nur laenger.
        // Punkt 50: leere Liste = keine Staffel. `null` statt `[]`, damit „nicht gepflegt" und
        // „ausdruecklich leer" dasselbe bedeuten — eine Staffel ohne Stufen sagt nichts aus.
        // Das Altfeld `finalPaymentMode` wird von `finalPaymentMethod` abgelöst und nicht mehr
        // angeboten: es sagte dasselbe noch einmal, nur gröber.
        depositDueDays: anzahlungAn ? depositDueDays : null,
        depositDueMode: anzahlungAn ? anzahlungFristModus : null,
        acceptanceFlow: acceptanceFlow === KEIN_WERT ? null : acceptanceFlow,
        ...anzahlungsFelder,
        // Punkt 116: „Am Veranstaltungstag" wird als 0 gespeichert — leer heißt seitdem wirksam
        // „14 Tage vorher" (die Vorgabe), also nicht mehr dasselbe.
        ...restzahlungFelder(restWeg, restFristGeltend, restFristGeltend === "am_event_tag" ? null : restTage),
        bankHolder: str("bankHolder"),
        bankName: str("bankName"),
        bankIban: str("bankIban"),
        bankBic: str("bankBic"),
      });
      setSaved(true);
      markSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  const d = data;
  return (
    <form className="max-w-3xl space-y-8" onInput={markDirty} onSubmit={(e) => void save(e)}>
      <p className="rounded-lg border border-outline-variant/40 bg-surface-container-low p-4 font-body-sm text-body-sm text-secondary dark:border-outline/30 dark:bg-surface-variant/10 dark:text-secondary-fixed-dim">
        Diese Angaben erscheinen <strong>identisch</strong> in Angebot, Rechnung und Kostenübernahme. Je Angebot lässt sich im Builder davon abweichen.
      </p>
      {error && <ErrorNote message={error} />}
      <section className="space-y-4">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Zahlung</h3>
        {/* WARTESCHLANGE Punkt 45 — der Schalter, der den Ausfall vom 17.09.2026 unmöglich macht,
            jetzt auch am STANDARD. Vorher war „Keine" ein Wert in der Auswahl „Anzahlungsart", also
            ein WEG. Damit ließ sich „Anzahlung 100 %" neben „Anzahlungsart: Keine" speichern — und
            zwar als Vorgabe, die jedes neue Angebot geerbt hätte. */}
        <div className="flex items-center gap-3 rounded-lg bg-surface-variant/40 px-3 py-2.5 dark:bg-surface-dim/40">
          <Toggle
            checked={anzahlungAn}
            onChange={(an) => {
              setAnzahlungAn(an);
              markDirty();
            }}
            label="Anzahlung vereinbaren"
          />
          <span className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
            {anzahlungAn ? "Anzahlung vereinbart" : "Keine Anzahlung – der Gast zahlt alles auf einmal"}
          </span>
        </div>
        <div className={`grid gap-4 sm:grid-cols-2 ${anzahlungAn ? "" : "pointer-events-none opacity-50"}`}>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Anzahlung (%)</span>
            <Input name="depositPercent" inputMode="decimal" defaultValue={d.depositPercentBp != null ? (d.depositPercentBp / 100).toString() : ""} placeholder="50" />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Anzahlung Fixbetrag (€)</span>
            <Input name="depositAmount" inputMode="decimal" defaultValue={d.depositAmountCents != null ? (d.depositAmountCents / 100).toString().replace(".", ",") : ""} placeholder="500" />
            <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
              Entweder Prozent oder fester Betrag – nicht beides.
            </span>
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Wie wird die Anzahlung bezahlt?</span>
            <Select
              ariaLabel="Wie wird die Anzahlung bezahlt?"
              disabled={!anzahlungAn}
              value={anzahlungsWeg}
              onValueChange={(v) => {
                setAnzahlungsWeg(v);
                markDirty();
              }}
              options={anzahlungsWege(zahlungswege ?? null).map((w) => ({
                value: w.value,
                label: w.verfuegbar ? w.label : `${w.label} — nicht eingerichtet`,
              }))}
            />
          </label>
          <div className="block">
            <label htmlFor="depositDueDays" className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Anzahlung fällig</label>
            <div className="flex items-center gap-2">
              <Input id="depositDueDays" name="depositDueDays" className="w-20" inputMode="numeric" defaultValue={d.depositDueDays ?? ""} placeholder="7" />
              <span className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">Tage</span>
            </div>
            <div className="mt-2">
              <Segmente
                label="Bezugspunkt der Anzahlungs-Frist"
                werte={ANZAHLUNG_FRIST_MODI}
                wert={anzahlungFristModus}
                onWahl={(k) => {
                  setAnzahlungFristModus(k);
                  markDirty();
                }}
              />
            </div>
            <span className="mt-1 block font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
              {anzahlungFristModus === "vor_veranstaltung"
                ? "Gezählt rückwärts vom Veranstaltungstag – frühestens am Tag der Auftragsbestätigung."
                : "Gezählt ab dem Tag der Auftragsbestätigung. Leer lassen heißt: sofort (nur bei Stripe möglich)."}
            </span>
          </div>
        </div>
        {/* Punkt 45: hier stand „Restzahlungsart" mit vier Werten, die WEG UND FRIST vermischten —
            „Rechnung vor Event" sagte beides zugleich. Wer die Frist ändern wollte, musste den Weg
            anfassen. Jetzt zwei Felder, dieselbe Aufteilung wie am Angebot. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Wie wird die Restzahlung bezahlt?</span>
            <Select
              ariaLabel="Wie wird die Restzahlung bezahlt?"
              value={restWeg}
              onValueChange={(v) => {
                setRestWeg(v);
                // „Nach der Veranstaltung" kennt der Altbestand nur mit Überweisung.
                if (v !== "ueberweisung" && restFristArt === "nach_event") setRestFristArt("am_event_tag");
                markDirty();
              }}
              options={restzahlungsWege(zahlungswege ?? null).map((w) => ({
                value: w.value,
                label: w.verfuegbar ? w.label : `${w.label} — nicht eingerichtet`,
              }))}
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Wann ist die Restzahlung fällig?</span>
            {/* Antoine, 18.09.2026: die Anzahl Tage steht klein VOR der Auswahl, ohne eigene
                Überschrift — die Angabe liest sich als ein Satz: „5 Tage vor der Veranstaltung".
                Dieselbe Form wie am Vorgang; Antoines Regel „es soll überall gleich sein". */}
            <div className="flex items-center gap-2">
              {restFristArt !== "am_event_tag" && (
                <>
                  <Input
                    name="restTage"
                    aria-label={restFristArt === "nach_event" ? "Tage nach der Veranstaltung" : "Tage vor der Veranstaltung"}
                    inputMode="numeric"
                    className="w-16 shrink-0 text-center"
                    defaultValue={restStart.tage ?? ""}
                    placeholder="5"
                  />
                  <span className="shrink-0 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">Tage</span>
                </>
              )}
              <div className="min-w-0 flex-1">
            <Select
              ariaLabel="Wann ist die Restzahlung fällig?"
              value={restFristArt}
              onValueChange={(v) => {
                setRestFristArt(v as RestFristArt);
                markDirty();
              }}
              options={[
                { value: "vor_event", label: "vor der Veranstaltung" },
                { value: "am_event_tag", label: "Am Veranstaltungstag" },
                // Antoine, 18.09.2026: „Doch, nach der Veranstaltung ist okay." Der Altbestand
                // kennt diese Frist nur mit Überweisung — ehrlich eingeschränkt statt frei erfunden.
                ...(restWeg === "ueberweisung" ? [{ value: "nach_event", label: "nach der Veranstaltung" }] : []),
              ]}
            />
              </div>
            </div>
            {restFristArt === "nach_event" && (
              <p className="mt-1 font-label-md text-label-md text-on-surface-variant dark:text-secondary-fixed-dim">
                0 = sofort nach der Veranstaltung fällig
              </p>
            )}
          </label>
        </div>
        {/* Punkt 116 (Antoine, 24.09.2026): das Zahlungsziel ist wieder ein eigenes Feld — und zwar
            NUR „N Tage nach Rechnungserhalt" (LexOffice-Zahlungsziel). Die Frist „nach der
            Veranstaltung" steht oben bei der Restzahlung in einem eigenen Feld. Der Text daneben
            ersetzt die Tage nicht mehr, er erscheint zusätzlich. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
              Zahlungsziel (Tage nach Rechnungserhalt)
            </span>
            <Input name="paymentTermDays" inputMode="numeric" defaultValue={d.paymentTermDays ?? ""} placeholder="14" />
          </label>
          <TextField
            name="paymentTermText"
            label="Zusatztext Zahlungsziel (erscheint auf Rechnung und Angebot)"
            defaultValue={d.paymentTermText}
            placeholder="Zahlbar ohne Abzug"
          />
        </div>
        <StornoVorlagenEditor
          vorlagen={stornoVorlagen}
          onChange={(neu) => {
            setStornoVorlagen(neu);
            markDirty();
          }}
        />
        {/*
          22.09.2026, Antoine: „Das braucht es unter Zahlungen in den Einstellungen alles nicht.
          Komplett entfernen. AGBs haben wir als Link unter Betrieb."

          WEG sind hier zwei Eingaben:
           * die STRUKTURIERTE Stornostaffel — die Bedingungen stehen im Feld darueber, und zwei
             Orte fuer dieselbe Aussage laufen unweigerlich auseinander;
           * der AGB-VOLLTEXT — er waere eine zweite Fassung neben der auf der Webseite. Gepflegt
             wird jetzt der Link (Einstellungen → Betrieb), und die Angebotsseite verweist darauf.
        */}
      </section>
      {/* § 8 (Migration 345, 31.08.2026, docs/KONZEPT-ZAHLUNGSKONDITIONEN-PRO-ANGEBOT.md § 8):
          Annahme-/Zahlungsfluss-Umschalter — Mandant-Default, je Angebot im Builder ueberschreibbar. */}
      <section className="space-y-4">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Annahme- &amp; Zahlungsfluss</h3>
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Legt fest, wie der Gast das Angebot auf der öffentlichen Seite annimmt und was danach erscheint.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Annahme-Weg des Gasts</span>
            <Select
              ariaLabel="Annahme-Weg des Gasts"
              value={acceptanceFlow}
              onValueChange={(v) => {
                setAcceptanceFlow(v);
                markDirty();
              }}
              options={[
                { value: KEIN_WERT, label: "explicit (Standard) — Angebot annehmen" },
                { value: "explicit", label: "Angebot annehmen (wie bisher)" },
                { value: "direct_payment", label: "Direkt zum Zahlungsschritt" },
              ]}
            />
          </label>
          {/* WARTESCHLANGE Punkt 45 / K2: „Zahlungsweg nach der Annahme" stand hier als eigenes
              Feld neben der Anzahlungsart — zwei Auswahlen für dieselbe Frage, und sie konnten
              einander widersprechen. Antoine, 18.09.2026: „Kostenübernahme ist ein Vertrag und
              ersetzt die Anzahlung. Die Anzahlung ist eine Sicherheitsleistung, wie ein Pfand."
              Sie steht deshalb jetzt oben bei den Wegen der Anzahlung. */}
        </div>
      </section>
      <section className="space-y-4">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Kontoverbindung</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField name="bankHolder" label="Kontoinhaber" defaultValue={d.bankHolder} />
          <TextField name="bankName" label="Bank" defaultValue={d.bankName} />
          <TextField name="bankIban" label="IBAN" defaultValue={d.bankIban} />
          <TextField name="bankBic" label="BIC" defaultValue={d.bankBic} />
        </div>
      </section>
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={busy}>{busy ? "Speichert …" : "Speichern"}</Button>
        {saved && <span className="inline-flex items-center gap-1 font-body-sm text-body-sm text-tertiary dark:text-tertiary-fixed-dim"><Icon name="check_circle" className="text-[18px]" /> Gespeichert</span>}
      </div>
    </form>
  );
}

/* ============================================================================================ */
/* Reiter „Postfächer" — Credential-Variante B (Migration 356)                                  */
/* ============================================================================================ */

/**
 * Hier trägt der Betrieb ein, welche Postfächer MAESTRO abholt — inklusive der Zugangsdaten.
 * Antoine, 03.09.2026, wörtlich: „Bau das gleich in die Einstellungen. Welche Postfächer abgeholt
 * werden und dort gebe ich alle Daten ein, Mail, Host, Passwort etc."
 *
 * ── Optik und Aufbau ──────────────────────────────────────────────────────────────────────────
 * Übernommen vom Reiter „Absenderdomain" (Karte, Kopfzeile mit Trennlinie, Hinweistext,
 * Status-Chips) plus die Tabellendarstellung MIT Spaltenköpfen aus dem Reiter „Orte" — darauf legt
 * Antoine ausdrücklich Wert.
 *
 * Die Eingabefelder eines Postfachs (Bezeichnung, Server, Port, Benutzername, Ordner) gehören
 * zusammen und stehen deshalb in einem FENSTER, nicht als einzelne Inline-Felder neben der
 * Tabelle — dieselbe Vorliebe, die Antoine am 03.09.2026 für die Eckdaten geäußert hat.
 *
 * ── Was diese Oberfläche NIE zeigt ────────────────────────────────────────────────────────────
 * Das Passwort. Auch nicht gekürzt, auch nicht als „••••1234" — ein Präfix ist ebenfalls ein Teil
 * des Geheimnisses. Sie zeigt nur, OB eines hinterlegt ist und wann es zuletzt geändert wurde.
 * Der Server liefert es gar nicht erst mit (`lib/mailbox-sicht.ts`), und die Datenbank gibt die
 * Spalte für niemanden frei.
 *
 * ── Kein Fachjargon ───────────────────────────────────────────────────────────────────────────
 * „IMAP", „UIDVALIDITY", „Backoff" kommen in keiner sichtbaren Zeile vor. Es heißt „Posteingangs-
 * Server", „zuletzt abgeholt", „Verbindung prüfen".
 */
interface PostfachOrdnerZustand {
  mailboxId: string;
  folder: string;
  lastSuccessAt: string | null;
  lastRunAt: string | null;
  lastErrorCode: string | null;
  consecutiveFailures: number;
  mailsIngestedTotal: number;
}

interface Postfach {
  id: string;
  label: string;
  imapHost: string;
  imapPort: number;
  imapUsername: string;
  providerHint: string | null;
  folderInbox: string | null;
  folderSent: string | null;
  active: boolean;
  /** Posteingang S1 (P7, Migration 538): Import-Startdatum (YYYY-MM-DD) oder null. */
  backfillSince: string | null;
  /** Ersatz für das Passwort: OB eines hinterlegt ist — nie der Wert. */
  passwortHinterlegt: boolean;
  passwortGeaendertAm: string | null;
  passwortGeaendertVon: string | null;
  pruefungAngefordertAm: string | null;
  pruefungStatus: string | null;
  pruefungAm: string | null;
  /** Deutscher Satz zum Prüfergebnis — kommt vom Server, damit es nur EINE Übersetzung gibt. */
  pruefungText: string | null;
  ordner: PostfachOrdnerZustand[];
}

interface PostfaecherDaten {
  postfaecher: Postfach[];
  /**
   * Ob diese Umgebung Zugangsdaten überhaupt verschlüsselt ablegen kann (Worker-Secret
   * MAILBOX_KEY). Kommt vom Server, weil nur dort bekannt ist, was gesetzt ist — und es steht
   * VOR der Eingabe da: ein Formular, das ein Passwort annimmt und es dann nicht speichern kann,
   * hätte es bereits über die Leitung geschickt.
   */
  zugangsdatenMoeglich: boolean;
}

/** Der Eingabezustand des Postfach-Fensters. Leere Zeichenketten statt `null` — HTML-Inputs. */
interface PostfachEingabe {
  id: string | null;
  label: string;
  imapHost: string;
  imapPort: 993 | 143;
  imapUsername: string;
  folderInbox: string;
  folderSent: string;
}

const LEERE_EINGABE: PostfachEingabe = {
  id: null,
  label: "",
  imapHost: "",
  imapPort: 993,
  imapUsername: "",
  folderInbox: "",
  folderSent: "",
};

/** Ein Chip in der Optik der Absenderdomain-Badges. */
function PostfachChip({ ton, icon, children }: { ton: "gut" | "warten" | "fehler" | "neutral" | "pausiert"; icon: string; children: ReactNode }) {
  const klasse = {
    // Design E9: Pausiert ist ueberall gelb (wie Widgets/Editor).
    pausiert: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
    gut: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
    warten: "bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]",
    fehler: "bg-error/10 text-error",
    neutral: "bg-surface-variant text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]",
  }[ton];
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 font-body-sm text-[12px] font-medium ${klasse}`}>
      <Icon name={icon} className="text-[14px]" />
      {children}
    </span>
  );
}

/**
 * Der Zustand der Abholung als EIN Satz.
 *
 * Bewusst nicht drei Chips nebeneinander: die Frage des Betreibers lautet „läuft es?", und darauf
 * gibt es genau eine Antwort. Ein Fehlercode gewinnt immer gegen „zuletzt abgeholt" — sonst stünde
 * neben einem seit Tagen scheiternden Postfach ein freundliches Datum.
 */
function abholZustand(p: Postfach): { ton: "gut" | "warten" | "fehler" | "neutral" | "pausiert"; icon: string; text: string } {
  const fehler = p.ordner.find((o) => o.lastErrorCode);
  if (fehler) {
    const text =
      fehler.lastErrorCode === "credential_unlesbar"
        ? "Zugangsdaten nicht lesbar — bitte Passwort erneut eingeben"
        : fehler.lastErrorCode === "credential_fehlt"
          ? "Kein Passwort hinterlegt"
          : fehler.lastErrorCode === "auth_failed"
            ? "Anmeldung fehlgeschlagen — Passwort erneuern"
            : "Letzter Abholversuch fehlgeschlagen";
    return { ton: "fehler", icon: "error", text };
  }
  if (!p.active) return { ton: "pausiert", icon: "pause_circle", text: "Pausiert" };
  const erfolg = p.ordner.map((o) => o.lastSuccessAt).filter(Boolean).sort().at(-1) ?? null;
  if (!erfolg) return { ton: "warten", icon: "schedule", text: "Wartet auf den ersten Abholvorgang" };
  return { ton: "gut", icon: "check_circle", text: `Zuletzt abgeholt: ${formatDateTime(erfolg)}` };
}

export function PostfaecherTab({ canEdit }: { canEdit: boolean }) {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const { data, loading, error: ladeFehler, reload } = useApi<PostfaecherDaten | null>("/api/settings/postfaecher");
  const [fenster, setFenster] = useState<PostfachEingabe | null>(null);
  const [passwortFuer, setPasswortFuer] = useState<Postfach | null>(null);
  const [passwortEingabe, setPasswortEingabe] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [aktionsFehler, setAktionsFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  const postfaecher = data?.postfaecher ?? [];
  const kryptoBereit = data?.zugangsdatenMoeglich !== false;

  /** Eine Aktion + Neuladen, mit EINER Fehlerbehandlung — statt sie achtmal zu wiederholen. */
  async function fuehreAus(marke: string, arbeit: () => Promise<void>, erfolgshinweis?: string): Promise<void> {
    setBusy(marke);
    setAktionsFehler(null);
    setHinweis(null);
    try {
      await arbeit();
      await reload();
      if (erfolgshinweis) setHinweis(erfolgshinweis);
    } catch (err) {
      setAktionsFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function speicherePostfach(e: FormEvent) {
    e.preventDefault();
    if (!fenster) return;
    const rumpf = {
      label: fenster.label.trim(),
      imapHost: fenster.imapHost.trim(),
      imapPort: fenster.imapPort,
      imapUsername: fenster.imapUsername.trim(),
      folderInbox: fenster.folderInbox.trim() || null,
      folderSent: fenster.folderSent.trim() || null,
    };
    const id = fenster.id;
    await fuehreAus("speichern", async () => {
      if (id) await apiMutate(`/api/settings/postfaecher/${id}`, "PUT", rumpf);
      else await apiMutate("/api/settings/postfaecher", "POST", rumpf);
      setFenster(null);
    });
  }

  async function speicherePasswort(e: FormEvent) {
    e.preventDefault();
    if (!passwortFuer) return;
    const id = passwortFuer.id;
    const wert = passwortEingabe;
    await fuehreAus(
      "passwort",
      async () => {
        await apiMutate(`/api/settings/postfaecher/${id}/passwort`, "PUT", { passwort: wert });
        // Das eingegebene Passwort wird sofort aus dem Zustand geworfen — es hat im Speicher der
        // Oberfläche nichts mehr verloren, sobald es abgeschickt ist.
        setPasswortEingabe("");
        setPasswortFuer(null);
      },
      "Zugangsdaten gespeichert.",
    );
  }

  return (
    <Card className="p-6">
      {bestaetigungsDialog}
      <div className="mb-6 flex items-center gap-2 border-b border-outline-variant/30 pb-4 dark:border-outline/30">
        <h3 className="font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Postfächer</h3>
      </div>

      {ladeFehler && <ErrorNote message={ladeFehler} />}
      {aktionsFehler && <ErrorNote message={aktionsFehler} />}
      {hinweis && (
        <p className="mb-4 inline-flex items-center gap-1.5 font-body-sm text-body-sm text-primary dark:text-primary-fixed-dim">
          <Icon name="check_circle" className="text-[18px]" /> {hinweis}
        </p>
      )}

      <p className="mb-4 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        MAESTRO kann die E-Mails Ihrer Betriebs-Postfächer regelmäßig abholen und den passenden
        Vorgängen zuordnen. Tragen Sie hier ein, welche Postfächer das sind, und hinterlegen Sie die
        Zugangsdaten. Gelesen wird nur — MAESTRO verschiebt, löscht und markiert nichts in Ihrem
        Postfach.
      </p>

      {!kryptoBereit && (
        <div className="mb-6 rounded-lg border border-outline-variant/40 bg-surface-container-low p-4 dark:border-outline/30 dark:bg-surface-variant/10">
          <p className="flex items-start gap-1.5 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
            <Icon name="info" className="mt-0.5 shrink-0 text-[15px] text-secondary" />
            <span>
              In dieser Umgebung können noch keine Zugangsdaten gespeichert werden — dafür fehlt ein
              Einrichtungsschritt beim Betreiber. Postfächer lassen sich schon eintragen, das
              Passwort kann erst danach hinterlegt werden.
            </span>
          </p>
        </div>
      )}

      {canEdit ? (
        <div className="mb-6 border-b border-outline-variant/30 pb-6 dark:border-outline/30">
          <Button variant="primary" type="button" onClick={() => setFenster({ ...LEERE_EINGABE })} disabled={busy !== null} className="inline-flex items-center gap-1.5">
            <Icon name="add" className="text-[16px]" />
            Postfach hinzufügen
          </Button>
        </div>
      ) : (
        <p className="mb-6 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Nur Inhaber dürfen Postfächer und ihre Zugangsdaten ändern.
        </p>
      )}

      {loading ? (
        <LoadingRows rows={3} />
      ) : postfaecher.length === 0 ? (
        <EmptyState icon="inbox" title="Noch kein Postfach eingetragen" />
      ) : (
        <div className="overflow-x-auto">
          <table className="handy-karten w-full min-w-[860px] border-collapse text-left">
            <thead>
              <tr className="bg-surface-container-low dark:bg-surface-variant/20">
                <th className="table-header">Postfach</th>
                <th className="table-header">Posteingangs-Server</th>
                <th className="table-header">Zugangsdaten</th>
                <th className="table-header">Verbindung</th>
                <th className="table-header">Abholung</th>
                <th className="table-header" />
              </tr>
            </thead>
            <tbody>
              {postfaecher.map((p) => {
                const zustand = abholZustand(p);
                const zeileBusy = busy === p.id;
                return (
                  <tr key={p.id} className="transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20">
                    <td className="border-t border-outline-variant/20 px-4 py-4 font-body-md text-body-md text-on-background dark:border-outline/20 dark:text-inverse-on-surface">
                      <span className="block font-semibold">{p.label}</span>
                      <span className="block font-mono text-[12px] text-secondary dark:text-secondary-fixed-dim">{p.imapUsername}</span>
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 font-body-md text-body-md text-on-surface-variant dark:border-outline/20 dark:text-secondary-fixed-dim">
                      <span className="font-mono">{p.imapHost}</span>
                      <span className="block text-[12px]">Port {p.imapPort}</span>
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 dark:border-outline/20">
                      {p.passwortHinterlegt ? (
                        <>
                          <PostfachChip ton="gut" icon="key">
                            Hinterlegt
                          </PostfachChip>
                          <span className="mt-1 block font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                            geändert {formatDateTime(p.passwortGeaendertAm)}
                          </span>
                        </>
                      ) : (
                        <PostfachChip ton="fehler" icon="key_off">
                          Fehlt
                        </PostfachChip>
                      )}
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 dark:border-outline/20">
                      {p.pruefungStatus === "ok" ? (
                        <PostfachChip ton="gut" icon="check_circle">
                          Geprüft
                        </PostfachChip>
                      ) : p.pruefungStatus === "ausstehend" || p.pruefungStatus === "laeuft" ? (
                        <PostfachChip ton="warten" icon="schedule">
                          Prüfung läuft
                        </PostfachChip>
                      ) : p.pruefungStatus ? (
                        <PostfachChip ton="fehler" icon="error">
                          Fehlgeschlagen
                        </PostfachChip>
                      ) : (
                        <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Noch nicht geprüft</span>
                      )}
                      {p.pruefungText && (
                        <span className="mt-1 block font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">{p.pruefungText}</span>
                      )}
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 dark:border-outline/20">
                      <PostfachChip ton={zustand.ton} icon={zustand.icon}>
                        {p.active ? "Aktiv" : "Pausiert"}
                      </PostfachChip>
                      <span className="mt-1 block font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">{zustand.text}</span>
                      {/* P7: Import erst ab diesem Datum; ältere Mails bleiben im Postfach und werden in MAESTRO ausgeblendet. */}
                      <label className="mt-2 flex items-center gap-1.5 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                        Import ab
                        <DateInput
                          aria-label={`Import-Startdatum für ${p.label}`}
                          value={p.backfillSince ? String(p.backfillSince).slice(0, 10) : ""}
                          disabled={!canEdit || busy !== null}
                          onValueChange={(v) =>
                            void fuehreAus(
                              p.id,
                              async () => {
                                await apiMutate(`/api/settings/postfaecher/${p.id}/startdatum`, "PUT", { datum: v || null });
                              },
                              "Startdatum gespeichert — ältere Mails werden nicht mehr importiert und im Posteingang ausgeblendet.",
                            )
                          }
                          className="!w-[150px]"
                          inputClassName="!py-1"
                        />
                      </label>
                    </td>
                    <td className="border-t border-outline-variant/20 px-4 py-4 text-right dark:border-outline/20">
                      {canEdit && (
                        <div className="flex flex-wrap items-center justify-end gap-3">
                          <button
                            type="button"
                            className="font-label-md text-label-md text-primary dark:text-primary-fixed-dim disabled:opacity-50"
                            disabled={busy !== null}
                            onClick={() =>
                              setFenster({
                                id: p.id,
                                label: p.label,
                                imapHost: p.imapHost,
                                imapPort: p.imapPort === 143 ? 143 : 993,
                                imapUsername: p.imapUsername,
                                folderInbox: p.folderInbox ?? "",
                                folderSent: p.folderSent ?? "",
                              })
                            }
                          >
                            Bearbeiten
                          </button>
                          <button
                            type="button"
                            className="font-label-md text-label-md text-primary dark:text-primary-fixed-dim disabled:opacity-50"
                            disabled={busy !== null || !kryptoBereit}
                            onClick={() => {
                              setPasswortEingabe("");
                              setPasswortFuer(p);
                            }}
                          >
                            {p.passwortHinterlegt ? "Passwort ändern" : "Passwort hinterlegen"}
                          </button>
                          <button
                            type="button"
                            className="font-label-md text-label-md text-primary dark:text-primary-fixed-dim disabled:opacity-50"
                            disabled={busy !== null || !p.passwortHinterlegt}
                            onClick={() =>
                              void fuehreAus(
                                p.id,
                                async () => {
                                  await apiMutate(`/api/settings/postfaecher/${p.id}/pruefen`, "POST");
                                },
                                "Prüfung angefordert — das Ergebnis erscheint nach dem nächsten stündlichen Abholvorgang.",
                              )
                            }
                          >
                            Verbindung prüfen
                          </button>
                          {/* Pausieren behält die Zugangsdaten, Entfernen löscht sie — die beiden
                              Handlungen stehen deshalb getrennt und heißen unterschiedlich. */}
                          <button
                            type="button"
                            className="font-label-md text-label-md text-primary dark:text-primary-fixed-dim disabled:opacity-50"
                            disabled={busy !== null || (!p.active && !p.passwortHinterlegt)}
                            onClick={() =>
                              void fuehreAus(p.id, async () => {
                                await apiMutate(`/api/settings/postfaecher/${p.id}/active`, "PUT", { active: !p.active });
                              })
                            }
                          >
                            {zeileBusy ? "…" : p.active ? "Pausieren" : "Abholung starten"}
                          </button>
                          <button
                            type="button"
                            className="font-label-md text-label-md text-error disabled:opacity-50"
                            disabled={busy !== null}
                            onClick={async () => {
                              if (
                                !(await bestaetigen({
                                  titel: `Postfach „${p.label}“ entfernen?`,
                                  text: "Die bereits abgeholten E-Mails bleiben erhalten, die Zugangsdaten werden gelöscht.",
                                  bestaetigen: "Entfernen",
                                  gefahr: true,
                                }))
                              )
                                return;
                              void fuehreAus(p.id, async () => {
                                await apiMutate(`/api/settings/postfaecher/${p.id}`, "DELETE");
                              });
                            }}
                          >
                            Entfernen
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Posteingang S2 (P6): Messung + Schalter der KI-Zuordnung bei mehreren Vorgängen. */}
      {postfaecher.length > 0 && <KiZuordnungKarte />}

      {/* Das Postfach-Fenster: die Felder gehören zusammen und stehen deshalb im Fenster, nicht
          einzeln neben der Tabelle. */}
      <Modal
        open={fenster !== null}
        onClose={() => setFenster(null)}
        width="560px"
        title={fenster?.id ? "Postfach bearbeiten" : "Postfach hinzufügen"}
        onSubmit={(e) => void speicherePostfach(e)}
        footer={
          <>
            <Button variant="secondary" type="button" onClick={() => setFenster(null)}>
              Abbrechen
            </Button>
            <Button variant="primary" type="submit" disabled={busy !== null}>
              {busy === "speichern" ? "Speichert …" : "Speichern"}
            </Button>
          </>
        }
      >
        {fenster && (
          <div className="flex flex-col gap-4">
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Bezeichnung</span>
              <Input
                type="text"
                value={fenster.label}
                onChange={(e) => setFenster({ ...fenster, label: e.target.value })}
                placeholder="Zentrale Anfragen"
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">E-Mail-Adresse / Benutzername</span>
              <Input
                type="text"
                value={fenster.imapUsername}
                onChange={(e) => setFenster({ ...fenster, imapUsername: e.target.value })}
                placeholder="info@ihre-domain.de"
                required
              />
            </label>
            <div className="grid grid-cols-[1fr_140px] gap-3">
              <label className="block">
                <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Posteingangs-Server</span>
                <Input
                  type="text"
                  value={fenster.imapHost}
                  onChange={(e) => setFenster({ ...fenster, imapHost: e.target.value })}
                  placeholder="imap.ihr-anbieter.de"
                  required
                />
              </label>
              <label className="block">
                <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Verbindung</span>
                <Select
                  ariaLabel="Verbindungsart"
                  value={String(fenster.imapPort)}
                  onValueChange={(v) => setFenster({ ...fenster, imapPort: v === "143" ? 143 : 993 })}
                  options={[
                    { value: "993", label: "Verschlüsselt (993)" },
                    { value: "143", label: "STARTTLS (143)" },
                  ]}
                />
              </label>
            </div>
            <details className="rounded-lg border border-outline-variant/40 p-3 dark:border-outline/30">
              <summary className="cursor-pointer font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                Ordnernamen abweichend? (nur nötig, wenn Ihr Anbieter andere Namen benutzt)
              </summary>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Posteingang</span>
                  <Input
                    type="text"
                    value={fenster.folderInbox}
                    onChange={(e) => setFenster({ ...fenster, folderInbox: e.target.value })}
                    placeholder="INBOX"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Gesendet</span>
                  <Input
                    type="text"
                    value={fenster.folderSent}
                    onChange={(e) => setFenster({ ...fenster, folderSent: e.target.value })}
                    placeholder="Sent"
                  />
                </label>
              </div>
            </details>
            {fenster.id && (
              <p className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                Wenn Sie Server oder E-Mail-Adresse ändern, wird das hinterlegte Passwort gelöscht
                und die Abholung pausiert — es gilt immer für genau eine Kombination aus beidem.
              </p>
            )}
            {aktionsFehler && <ErrorNote message={aktionsFehler} />}
          </div>
        )}
      </Modal>

      {/* Das Passwort-Fenster. Getrennt vom Postfach-Fenster, weil ein leeres Feld dort
          „unverändert" heißen müsste — und ein Formular, das beim Speichern still ein Zugangsdatum
          entfernt, weil jemand ein Feld nicht ausgefüllt hat, ist die teuerste Überraschung. */}
      <Modal
        open={passwortFuer !== null}
        onClose={() => {
          setPasswortEingabe("");
          setPasswortFuer(null);
        }}
        width="480px"
        title={<>Zugangsdaten für {passwortFuer?.label}</>}
        onSubmit={(e) => void speicherePasswort(e)}
        footer={
          passwortFuer && (
            <>
              {passwortFuer.passwortHinterlegt && (
                <button
                  type="button"
                  className="mr-auto font-label-md text-label-md text-error disabled:opacity-50"
                  disabled={busy !== null}
                  onClick={async () => {
                    const ziel = passwortFuer;
                    if (
                      !(await bestaetigen({
                        titel: "Hinterlegtes Passwort löschen?",
                        text: "Die Abholung wird dabei pausiert.",
                        bestaetigen: "Passwort löschen",
                        gefahr: true,
                      }))
                    )
                      return;
                    setPasswortFuer(null);
                    void fuehreAus(ziel.id, async () => {
                      await apiMutate(`/api/settings/postfaecher/${ziel.id}/passwort`, "DELETE");
                    });
                  }}
                >
                  Passwort löschen
                </button>
              )}
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  setPasswortEingabe("");
                  setPasswortFuer(null);
                }}
              >
                Abbrechen
              </Button>
              <Button variant="primary" type="submit" disabled={busy !== null || passwortEingabe.length === 0}>
                {busy === "passwort" ? "Speichert …" : "Speichern"}
              </Button>
            </>
          )
        }
      >
        {passwortFuer && (
          <div className="flex flex-col gap-4">
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              Das Passwort wird verschlüsselt gespeichert und danach nirgends mehr angezeigt — auch
              Ihnen nicht. Sie können es jederzeit überschreiben.
            </p>
            <label className="block">
              <span className="mb-1 block font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Passwort des Postfachs</span>
              <Input
                type="password"
                value={passwortEingabe}
                onChange={(e) => setPasswortEingabe(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            {aktionsFehler && <ErrorNote message={aktionsFehler} />}
          </div>
        )}
      </Modal>
    </Card>
  );
}
